import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { canExportPayrollCsv, canViewAppTab, getVisibleStores } from "./accessControl";
import {
  attendanceCapabilityForStore,
  attendanceStoresForTab,
  canOpenAttendanceTab,
  canOpenMonthlyAttendanceTab,
} from "./attendanceAccess";
import { getPasswordValidationError, MIN_PASSWORD_LENGTH } from "./passwordPolicy";
import { filterHandoversByStore } from "./handoverStore";
import { buildTasksListPath } from "./tasksStore";
import { getReceiptStatus } from "./readReceipt";
import { getActiveUsers, getSimilarNameWarning, getUserOperationalLabel, isUserDisabled } from "./userStatus";
import PermissionManagementPanel from "./PermissionManagementPanel";
import ServiceUsersTab from "./ServiceUsersTab";
import JippiLinkedTab from "./JippiTab";
import JippiDailyEntry from "./JippiDailyEntry";
import {
  applyClockOutToRecords,
  canClockOutAttendance,
  calculatePayrollMinutes,
  displayClockOut,
  findCurrentAttendance,
  hasBlockingAttendance,
  isEarlyDeparture,
  isFinalAutoClockout,
  isProvisionalAutoClockout,
} from "./attendanceRecords";
import {
  CUSTOM_SHIFT_CODE,
  OFF_SHIFT_CODE,
  SHIFT_PRESETS_BY_STORE,
  buildStructuredShiftPayload,
  getShiftEditorInitialState,
  getShiftDisplayValue,
  getShiftPreset,
  getShiftWorktableSymbol,
  usesStructuredShiftEditor,
  validateCustomShift,
} from "./shiftSchedule";
import {
  SHIFT_TABLE_ZOOM_DEFAULT,
  SHIFT_TABLE_ZOOM_MAX,
  SHIFT_TABLE_ZOOM_MIN,
  SHIFT_TABLE_ZOOM_STORAGE_KEY,
  adjustShiftTableZoom,
  calculatePinchContentPosition,
  calculatePinchZoom,
  calculateZoomedScrollOffset,
  getTouchGeometry,
  normalizeShiftTableZoom,
} from "./shiftTableZoom";

// ═══════════════════════════════════════════════════════════
// ★ サーバーURL設定（制作会社にアップ後ここだけ書き換え）
// ═══════════════════════════════════════════════════════════
const API_BASE = "/api"; // 例: "https://your-domain.sakura.ne.jp/api"
const AUTH_REQUIRED_EVENT = "five:auth-required";

function isQrAttendanceSearch(search) {
  const attendanceMode = new URLSearchParams(search).get("attendance");
  return ["clock-in", "clock-out"].includes(attendanceMode);
}

function authRecoveryMessage(message, destination) {
  const prefix = message?.includes("セッションの有効期限")
    ? "セッションの有効期限が切れました。再度ログインしてください。"
    : "ログインが必要です。";
  if (destination === "qr") {
    return `${prefix} ログイン後、QR打刻画面に戻ります。QRコードの再読取は不要です。`;
  }
  if (destination === "attendance") {
    return `${prefix} ログイン後、出退勤画面に戻ります。`;
  }
  return `${prefix} ログイン後、元の画面に戻ります。`;
}

// API呼び出し共通関数
async function api(path, method = "GET", body = null) {
  const opts = {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${API_BASE}${path}`, opts);
  const data = await res.json();
  if (!res.ok) {
    const error = new Error(data.error || "エラーが発生しました");
    error.status = res.status;
    error.payload = data;
    if (res.status === 401 && path !== "/login.php" && typeof window !== "undefined") {
      window.dispatchEvent(new window.CustomEvent(AUTH_REQUIRED_EVENT, {
        detail: { message:error.message },
      }));
    }
    throw error;
  }
  return data;
}

function newMutationRequestId(prefix) {
  if (typeof crypto === "undefined" || typeof crypto.randomUUID !== "function") {
    throw new Error("安全なrequest IDを生成できないため処理を開始できません");
  }
  return `${prefix}:${crypto.randomUUID()}`;
}

async function uploadHandoverAttachments(handoverId, files) {
  const body = new FormData();
  body.append("handover_id", String(handoverId));
  files.forEach(file => body.append("files[]", file));
  const res = await fetch(`${API_BASE}/handover_attachments.php`, {
    method: "POST",
    credentials: "include",
    body
  });
  const data = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(data.error || "添付ファイルのアップロードに失敗しました");
  return data;
}

async function uploadNoticeAttachments(noticeId, files) {
  const body = new FormData();
  body.append("notice_id", String(noticeId));
  files.forEach(file => body.append("files[]", file));
  const res = await fetch(`${API_BASE}/notice_attachments.php`, {
    method: "POST",
    credentials: "include",
    body
  });
  const data = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(data.error || "添付ファイルのアップロードに失敗しました");
  return data;
}

async function postChatMessage(partnerId, text, files) {
  const body = new FormData();
  body.append("partnerId", String(partnerId));
  body.append("text", text);
  files.forEach(file => body.append("files[]", file));
  const res = await fetch(`${API_BASE}/chat.php`, {
    method: "POST",
    credentials: "include",
    body
  });
  const data = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(data.error || "チャットの投稿に失敗しました。");
  return data;
}

// ═══════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════
const STORES = [
  { id:"kasama",  name:"ファイブ笠間", color:"#2D6A4F", accent:"#52B788" },
  { id:"re-five", name:"Reファイブ",   color:"#1D3557", accent:"#457B9D" },
  { id:"ami",     name:"ファイブ阿見", color:"#6D2C2C", accent:"#C77B7B" },
];
const ROLE_LABELS = { president:"社長", manager:"幹部", staff:"社員", part:"パート" };
const ROLE_ICONS  = { president:"👑", manager:"💼", staff:"🙋", part:"👤" };
const ROLE_COLORS = { president:"#6B21A8", manager:"#1E40AF", staff:"#0F766E", part:"#71717A" };
const ALL_TABS = [
  { id:"shift",      label:"シフト表",   icon:"📅" },
  { id:"attendance", label:"出退勤", icon:"⏱️" },
  { id:"monthlyAttendance", label:"月次勤務集計", icon:"📊" },
  { id:"payrollCsv", label:"給与CSV", icon:"📥" },
  { id:"handover",   label:"申し送り",   icon:"🔄" },
  { id:"notice",     label:"連絡",       icon:"📣" },
  { id:"chat",       label:"チャット",   icon:"💬" },
  { id:"tasks",      label:"タスク",     icon:"✅" },
  { id:"minutes",    label:"会議議事録", icon:"📋" },
  { id:"expense",    label:"経費申請",   icon:"💴" },
  { id:"kokuho",     label:"国保連請求", icon:"🏥" },
  { id:"jippiDaily", label:"実費を登録", icon:"✍️" },
  { id:"jippi",      label:"実費請求",   icon:"🧾" },
  { id:"serviceUsers", label:"利用者管理", icon:"👥" },
];
const HIDDEN_MAIN_TAB_IDS = new Set(["jippiDaily"]);

export function resolveSafeAppTab(visibleTabs, activeTab) {
  if (visibleTabs.some(tab => tab.id === activeTab)) return activeTab;
  if (activeTab === "jippiDaily" && visibleTabs.some(tab => tab.id === "jippi")) return "jippi";
  return visibleTabs[0]?.id;
}
const DEFAULT_PERMS = {
  president: {
    shift: true,
    attendance: true,
    monthlyAttendance: true,
    payrollCsv: true,
    handover: true,
    notice: true,
    chat: true,
    tasks: true,
    minutes: true,
    expense: true,
    kokuho: true,
    jippi: true,
    canEditShift: true,
    canSeeAllAttendance: true,
    canPost: true,
    isAdmin: true
  },
  manager: {
    shift: true,
    attendance: true,
    monthlyAttendance: true,
    payrollCsv: true,
    handover: true,
    notice: true,
    chat: true,
    tasks: true,
    minutes: true,
    expense: true,
    kokuho: true,
    jippi: true,
    canEditShift: true,
    canSeeAllAttendance: true,
    canPost: true,
    isAdmin: true
  },
  staff: {
    shift: true,
    attendance: true,
    monthlyAttendance: false,
    payrollCsv: false,
    handover: true,
    notice: true,
    chat: true,
    tasks: true,
    minutes: false,
    expense: true,
    kokuho: false,
    jippi: false,
    canEditShift: true,
    canSeeAllAttendance: false,
    canPost: true,
    isAdmin: false
  },
  part: {
    shift: true,
    attendance: true,
    monthlyAttendance: false,
    payrollCsv: false,
    handover: true,
    notice: true,
    chat: true,
    tasks: false,
    minutes: false,
    expense: false,
    kokuho: false,
    jippi: false,
    canEditShift: false,
    canSeeAllAttendance: false,
    canPost: true,
    isAdmin: false
  }
};
const DAYS  = ["月","火","水","木","金","土","日"];
const EXPENSE_CATS = ["交通費","消耗品","接待費","通信費","備品購入","その他"];
const KOKUHO_SVC   = { kasama:["共同生活援助","共同生活援助（介護サービス包括型）","短期入所"],"re-five":["自立訓練（生活訓練）","自立訓練（機能訓練）","就労移行支援"],ami:["共同生活援助","共同生活援助（介護サービス包括型）","短期入所"] };
const KOKUHO_LABEL = { kasama:"障害者グループホーム","re-five":"自立訓練",ami:"障害者グループホーム" };
const NOTICE_TGTS  = { all:{label:"全員",icon:"📢"},managers:{label:"幹部以上",icon:"💼"},staff_up:{label:"社員以上",icon:"🙋"},store:{label:"店舗全員",icon:"🏪"} };
const CLAIM_ST = { draft:{label:"作成中",color:"#888",bg:"#f5f5f5"},submitted:{label:"請求済み",color:"#457B9D",bg:"#e8f4fb"},returned:{label:"返戻",color:"#e74c3c",bg:"#fdf0ef"},paid:{label:"入金済み",color:"#27ae60",bg:"#eafaf1"} };
const EXP_ST   = { pending:{label:"審査中",color:"#e67e22",bg:"#fef3e2"},approved:{label:"承認済み",color:"#27ae60",bg:"#eafaf1"},rejected:{label:"差し戻し",color:"#e74c3c",bg:"#fdf0ef"} };

// ─── helpers ───────────────────────────────────────────────
const getToday = () => new Date().toISOString().split("T")[0];
const nowYM    = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; };
const fmtTime  = d => d.toTimeString().slice(0,5);
const fmtDT    = d => { const n=new Date(d); return `${n.getMonth()+1}/${n.getDate()} ${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`; };
export const formatMeetingDate = value => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${Number(match[1])}年${Number(match[2])}月${Number(match[3])}日` : String(value || "");
};

const getCurrentLocation = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error("この端末は位置情報の取得に対応していません"));
    return;
  }

  navigator.geolocation.getCurrentPosition(
    pos => resolve({
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy
    }),
    err => {
      const messages = {
        1: "位置情報の利用が許可されていません。ブラウザの設定で位置情報を許可してください。",
        2: "現在地を取得できませんでした。電波状況を確認してください。",
        3: "位置情報の取得がタイムアウトしました。もう一度お試しください。"
      };
      reject(new Error(messages[err.code] || "位置情報を取得できませんでした"));
    },
    {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0
    }
  );
});

// ─── styles ────────────────────────────────────────────────
const thS  = c => ({ background:c,color:"#fff",padding:"8px 12px",fontSize:13,fontWeight:600,textAlign:"center",whiteSpace:"nowrap" });
const tdS  = { padding:"8px 12px",textAlign:"center",borderBottom:"1px solid #f0f0f0",fontSize:13 };
const btnS = (c,dis) => ({ background:dis?"#e0e0e0":c,color:dis?"#aaa":"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontWeight:700,fontSize:14,cursor:dis?"not-allowed":"pointer",fontFamily:"inherit" });
export const getShiftSaveButtonStyle = (storeId, disabled, hovered = false) => {
  const storeColor = STORES.find(store => store.id === storeId)?.color;
  return {
    ...btnS(storeColor, disabled),
    color: disabled ? "#aaa" : "#ffffff",
    filter: !disabled && hovered ? "brightness(0.9)" : "none",
    transition: "filter 160ms ease, background-color 160ms ease",
    padding: "7px 14px",
  };
};
export const getShiftLockedFieldStyle = hasValue => {
  const color = hasValue ? "#222" : "#555";
  return {
    opacity: 1,
    color,
    WebkitTextFillColor: color,
    background: "#f7f7f7",
    border: "1px solid #c7c7c7",
    cursor: "not-allowed",
  };
};
const inpS = { border:"1px solid #ddd",borderRadius:8,padding:"8px 12px",fontSize:14,outline:"none",width:"100%",boxSizing:"border-box",fontFamily:"inherit" };
const selS = { border:"1px solid #ddd",borderRadius:8,padding:"8px 10px",fontSize:14,outline:"none",background:"#fff",fontFamily:"inherit" };
const lblS = { fontSize:12,color:"#888",marginBottom:4,display:"block" };

// ─── ローディング表示 ───────────────────────────────────────
function Loading({ text="読み込み中…" }) {
  return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:40,color:"#aaa",fontSize:14 }}>
      <div style={{ width:20,height:20,border:"3px solid #ddd",borderTopColor:"#52B788",borderRadius:"50%",animation:"spin 0.8s linear infinite" }}/>
      {text}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── エラー表示 ─────────────────────────────────────────────
function ErrBox({ msg, onRetry }) {
  return (
    <div style={{ background:"#fdf0ef",border:"1px solid #e74c3c",borderRadius:10,padding:"14px 16px",margin:"12px 0",fontSize:13 }}>
      <div style={{ color:"#e74c3c",fontWeight:700,marginBottom:6 }}>⚠️ エラー</div>
      <div style={{ color:"#333",marginBottom:onRetry?10:0 }}>{msg}</div>
      {onRetry && <button onClick={onRetry} style={{ ...btnS("#e74c3c",false),fontSize:12,padding:"5px 12px" }}>再試行</button>}
    </div>
  );
}

// ─── Avatar ─────────────────────────────────────────────────
function Avatar({ u, size=32 }) {
  if (!u) return null;
  return <div style={{ width:size,height:size,borderRadius:"50%",background:ROLE_COLORS[u.role]||"#888",display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.42,fontWeight:800,color:"#fff",flexShrink:0 }} title={u.name}>{u.avatar||u.name?.[0]||"?"}</div>;
}

// ─── 共有ボタン ─────────────────────────────────────────────
function ShareButtons({ title, body, compact=false }) {
  const [copied, setCopied] = useState(false);
  const fullText = title ? `${title}\n\n${body}` : body;
  const sendMail = () => window.open(`mailto:?subject=${encodeURIComponent(title||"ファイブからのお知らせ")}&body=${encodeURIComponent(body)}`, "_blank");
  const sendLine = () => window.open(`https://line.me/R/share?text=${encodeURIComponent(fullText)}`, "_blank");
  const copyText = () => {
    navigator.clipboard.writeText(fullText).catch(()=>{const el=document.createElement("textarea");el.value=fullText;document.body.appendChild(el);el.select();document.execCommand("copy");document.body.removeChild(el);});
    setCopied(true); setTimeout(()=>setCopied(false),2000);
  };
  const b = { display:"flex",alignItems:"center",gap:compact?4:6,border:"none",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontWeight:700,padding:compact?"6px 10px":"9px 16px",fontSize:compact?12:13 };
  return (
    <div style={{ display:"flex",gap:6,flexWrap:"wrap",alignItems:"center" }}>
      <button onClick={sendMail} style={{ ...b,background:"#EA4335",color:"#fff" }}><span style={{ fontSize:compact?14:16 }}>✉️</span>{!compact&&<span>メール</span>}</button>
      <button onClick={sendLine} style={{ ...b,background:"#06C755",color:"#fff" }}><span style={{ fontSize:compact?14:16 }}>💬</span>{!compact&&<span>LINE</span>}</button>
      <button onClick={copyText} style={{ ...b,background:copied?"#27ae60":"#555",color:"#fff" }}><span style={{ fontSize:compact?14:16 }}>{copied?"✅":"📋"}</span>{!compact&&<span>{copied?"コピー済み":"コピー"}</span>}</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// LOGIN SCREEN
// ═══════════════════════════════════════════════════════════
function LoginScreen({ onLogin, notice = "" }) {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!loginId.trim() || !password.trim()) { setError("IDとパスワードを入力してください"); return; }
    setLoading(true); setError("");
    try {
      const data = await api("/login.php", "POST", { loginId: loginId.trim(), password });
      onLogin(data.user);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:"100vh",background:"linear-gradient(135deg,#0f172a 0%,#1e293b 55%,#0f3460 100%)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Hiragino Sans','Noto Sans JP',sans-serif",padding:20 }}>
      <div style={{ width:"100%",maxWidth:400 }}>
        <div style={{ textAlign:"center",marginBottom:36 }}>
          <div style={{ width:72,height:72,borderRadius:22,background:"rgba(255,255,255,0.08)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:38,margin:"0 auto 14px",border:"1px solid rgba(255,255,255,0.12)" }}>🏢</div>
          <div style={{ fontSize:28,fontWeight:900,color:"#fff",letterSpacing:5 }}>FIVE</div>
          <div style={{ fontSize:11,color:"rgba(255,255,255,0.35)",letterSpacing:3,marginTop:5 }}>MANAGEMENT SYSTEM</div>
        </div>
        <div style={{ background:"rgba(255,255,255,0.06)",backdropFilter:"blur(20px)",borderRadius:24,padding:32,border:"1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize:15,color:"rgba(255,255,255,0.8)",fontWeight:700,marginBottom:24,textAlign:"center" }}>スタッフログイン</div>
          {notice && <div style={{ background:"rgba(245,158,11,0.15)",border:"1px solid rgba(245,158,11,0.45)",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:13,color:"#fcd34d",lineHeight:1.6,textAlign:"center" }}>⚠️ {notice}</div>}
          <div style={{ marginBottom:14 }}>
            <span style={{ ...lblS,color:"rgba(255,255,255,0.5)" }}>ログインID</span>
            <input value={loginId} onChange={e=>{ setLoginId(e.target.value); setError(""); }} onKeyDown={e=>e.key==="Enter"&&handleLogin()} placeholder="例: tanaka" style={{ ...inpS,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.18)",color:"#fff",fontSize:16 }}/>
          </div>
          <div style={{ marginBottom:20 }}>
            <span style={{ ...lblS,color:"rgba(255,255,255,0.5)" }}>パスワード</span>
            <div style={{ position:"relative" }}>
              <input type={showPw?"text":"password"} value={password} onChange={e=>{ setPassword(e.target.value); setError(""); }} onKeyDown={e=>e.key==="Enter"&&handleLogin()} placeholder="パスワードを入力" style={{ ...inpS,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.18)",color:"#fff",fontSize:16,paddingRight:44 }}/>
              <button onClick={()=>setShowPw(!showPw)} style={{ position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:18 }}>{showPw?"🙈":"👁️"}</button>
            </div>
          </div>
          {error && <div style={{ background:"rgba(231,76,60,0.15)",border:"1px solid rgba(231,76,60,0.4)",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:13,color:"#ff8a80",textAlign:"center" }}>⚠️ {error}</div>}
          <button onClick={handleLogin} disabled={loading} style={{ ...btnS("#52B788",loading),width:"100%",padding:14,fontSize:16,borderRadius:12 }}>{loading?"ログイン中…":"ログイン"}</button>
          <div style={{ marginTop:16,fontSize:11,color:"rgba(255,255,255,0.3)",textAlign:"center" }}>初期パスワードは管理者にお問い合わせください</div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ADMIN TAB（権限管理）
// ═══════════════════════════════════════════════════════════
function AdminTab({ currentUser, users, setUsers, store }) {
  const [section, setSection] = useState("list");
  const [editingId, setEditingId] = useState(null);
  const [editPerms, setEditPerms] = useState(null);
  const [editInfo, setEditInfo] = useState(null);
  const [newUser, setNewUser] = useState({ name:"",loginId:"",password:"",role:"staff",store:"kasama" });
  const [msg, setMsg] = useState({ text:"",type:"" });
  const [saving, setSaving] = useState(false);

  const showMsg = (text, type="ok") => { setMsg({text,type}); setTimeout(()=>setMsg({text:"",type:""}),3000); };

  const startEdit = u => { setEditingId(u.id); setEditPerms({...u.perms}); setEditInfo({ name:u.name,loginId:u.loginId,password:"",role:u.role,store:u.store }); };
  const editingUser = users.find(candidate => candidate.id === editingId);

  const savePerms = async () => {
    const passwordError = getPasswordValidationError(editInfo.password, { required:false });
    if (passwordError) { showMsg(passwordError,"err"); return; }
    setSaving(true);
    try {
      await api("/users.php", "PUT", {
        id:editingId,
        name:editInfo.name,
        loginId:editInfo.loginId,
        password:editInfo.password,
        perms:editPerms,
      });
      const safeEditInfo = { name:editInfo.name,loginId:editInfo.loginId };
      setUsers(prev => prev.map(u => u.id===editingId ? { ...u, ...safeEditInfo, perms:editPerms } : u));
      setEditInfo(prev => ({ ...prev, password:"" }));
      showMsg("保存しました ✓");
    } catch(e) { showMsg(e.message,"err"); }
    setSaving(false);
  };

  const addUser = async () => {
    if (!newUser.name.trim()||!newUser.loginId.trim()) { showMsg("氏名とログインIDを入力してください","err"); return; }
    const passwordError = getPasswordValidationError(newUser.password, { required:true });
    if (passwordError) { showMsg(passwordError,"err"); return; }
    setSaving(true);
    try {
      const payload = { ...newUser, storeId: newUser.store };
      const localMatches = getSimilarNameWarning(users,newUser.name);
      let confirmSimilarName = false;
      if (localMatches.length) {
        const labels = localMatches.map(getUserOperationalLabel).join("、");
        confirmSimilarName = window.confirm(`同一または類似氏名のユーザーが存在します: ${labels}\n\n他施設で勤務するために別ユーザーを作成する必要はありません。\n同姓同名など、別人であることを確認して作成しますか？`);
        if (!confirmSimilarName) { setSaving(false); return; }
      }
      const data = await api("/users.php", "POST", {
        ...payload,
        confirmSimilarName,
        requestId:newMutationRequestId("user-create"),
      });
      const formalUser = data.user;
      if (!formalUser || Number(formalUser.permission_revision) !== 1) {
        throw new Error("作成後の正式な権限状態を確認できません");
      }
      const created = {
        ...formalUser,
        loginId:formalUser.login_id,
        store:formalUser.store_id,
      };
      setUsers(prev => [...prev, created]);
      setNewUser({ name:"",loginId:"",password:"",role:"staff",store:"kasama" });
      showMsg("追加しました ✓");
      setSection("list");
    } catch(e) { showMsg(e.message,"err"); }
    setSaving(false);
  };

  const disableUser = async target => {
    const candidates = getActiveUsers(users).filter(candidate => candidate.id !== target.id);
    const choices = candidates.map(candidate => `${candidate.id} : ${candidate.name}（${STORES.find(store=>store.id===candidate.store)?.name || candidate.store}）`).join("\n");
    const canonicalUserId = window.prompt(`正本ユーザーのuser_idを入力してください。\n\n${choices}`);
    if (!canonicalUserId) return;
    if (!candidates.some(candidate => candidate.id === canonicalUserId.trim())) {
      showMsg("有効な正本ユーザーを指定してください","err");
      return;
    }
    const disabledReason = window.prompt("無効化理由を入力してください", "重複アカウントを正本へ統合");
    if (!disabledReason?.trim()) return;
    if (!window.confirm(`${target.name}を無効化します。過去履歴は変更しません。よろしいですか？`)) return;
    try {
      const result = await api("/users.php", "PUT", {
        action:"disable",id:target.id,canonicalUserId:canonicalUserId.trim(),disabledReason:disabledReason.trim()
      });
      setUsers(prev => prev.map(user => user.id===target.id ? { ...user,...result } : user));
      setEditingId(null);
      showMsg("無効化しました。過去履歴は保持されています");
    } catch(e) { showMsg(e.message,"err"); }
  };

  const reactivateUser = async target => {
    if (!window.confirm(`${target.name}を再有効化しますか？`)) return;
    try {
      await api("/users.php","PUT",{ action:"reactivate",id:target.id });
      setUsers(prev=>prev.map(user=>user.id===target.id ? {
        ...user,disabled_at:null,disabled_by:null,disabled_reason:null,canonical_user_id:null,canonical_user_name:null
      }:user));
      setEditingId(null);
      showMsg("再有効化しました");
    } catch(e) { showMsg(e.message,"err"); }
  };

  return (
    <div>
      <div style={{ display:"flex",gap:8,marginBottom:20,background:"#f5f5f5",borderRadius:10,padding:4 }}>
        {[["list","👥 アカウント・旧表示権限"],["permissions","🔐 機能・施設権限"],["new","➕ 新規スタッフ追加"]].map(([k,l])=>(
          <button key={k} onClick={()=>{ setSection(k); setEditingId(null); }} style={{ flex:1,padding:"9px 0",background:section===k?"#fff":"transparent",border:"none",borderRadius:8,fontWeight:section===k?700:400,fontSize:13,cursor:"pointer",fontFamily:"inherit",color:section===k?"#333":"#888",boxShadow:section===k?"0 1px 4px #0000001a":"none" }}>{l}</button>
        ))}
      </div>

      {msg.text && <div style={{ background:msg.type==="err"?"#fdf0ef":"#eafaf1",border:`1px solid ${msg.type==="err"?"#e74c3c":"#52B788"}`,borderRadius:8,padding:"8px 14px",marginBottom:14,fontSize:13,color:msg.type==="err"?"#c0392b":"#2D6A4F",fontWeight:700 }}>{msg.text}</div>}

      {section==="permissions" && <PermissionManagementPanel />}

      {/* 新規追加 */}
      {section==="new" && (
        <div style={{ background:"#f8f8f8",borderRadius:14,padding:20,border:"1px solid #eee" }}>
          <div style={{ fontWeight:700,fontSize:15,color:"#333",marginBottom:16 }}>新規スタッフ登録</div>
          <div style={{ fontSize:12,color:"#8a5b00",background:"#fff8df",border:"1px solid #f2d27a",borderRadius:8,padding:"9px 12px",marginBottom:14,lineHeight:1.7 }}>他施設で勤務するために別ユーザーを作成する必要はありません。主所属はユーザー情報、実勤務施設はシフト・勤怠で設定できます。</div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
            <div><span style={lblS}>氏名</span><input value={newUser.name} onChange={e=>setNewUser({...newUser,name:e.target.value})} placeholder="田中 花子" style={inpS}/></div>
            <div><span style={lblS}>ログインID</span><input value={newUser.loginId} onChange={e=>setNewUser({...newUser,loginId:e.target.value})} placeholder="tanaka_h" style={inpS}/></div>
            <div><span style={lblS}>初期パスワード（{MIN_PASSWORD_LENGTH}文字以上）</span><input type="password" autoComplete="new-password" minLength={MIN_PASSWORD_LENGTH} value={newUser.password} onChange={e=>setNewUser({...newUser,password:e.target.value})} style={inpS}/></div>
            <div><span style={lblS}>役職</span><select value={newUser.role} onChange={e=>setNewUser({...newUser,role:e.target.value})} style={{...selS,width:"100%"}}>{Object.entries(ROLE_LABELS).map(([k,v])=><option key={k} value={k}>{ROLE_ICONS[k]} {v}</option>)}</select></div>
            <div><span style={lblS}>担当店舗</span><select value={newUser.store} onChange={e=>setNewUser({...newUser,store:e.target.value})} style={{...selS,width:"100%"}}>{STORES.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          </div>
          <button onClick={addUser} disabled={saving} style={{ ...btnS("#2D6A4F",saving),width:"100%",padding:13,marginTop:16,fontSize:15 }}>追加する</button>
        </div>
      )}

      {/* スタッフ一覧 */}
      {section==="list" && !editingId && (
        <div>
          {[store].map(s=>{
            const su=users.filter(u=>u.store===s.id);
            if(!su.length) return null;
            return(
              <div key={s.id} style={{ marginBottom:20 }}>
                <div style={{ fontSize:13,fontWeight:700,color:s.color,marginBottom:8,padding:"4px 10px",background:s.color+"10",borderRadius:6,display:"inline-block" }}>{s.name}</div>
                <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                  {su.map(u=>(
                    <div key={u.id} style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:"#fff",border:"1px solid #eee",borderRadius:12,cursor:"pointer" }} onClick={()=>startEdit(u)}>
                      <Avatar u={u} size={42}/>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700,fontSize:14,color:isUserDisabled(u)?"#777":"#222" }}>{getUserOperationalLabel(u)}</div>
                        <div style={{ fontSize:12,color:"#aaa",marginTop:2 }}>{ROLE_ICONS[u.role]} {ROLE_LABELS[u.role]}　ID: {u.loginId}</div>
                        {isUserDisabled(u)&&<div style={{ fontSize:11,color:"#b42318",marginTop:4 }}>無効化: {u.disabled_at}　理由: {u.disabled_reason}</div>}
                        <div style={{ display:"flex",flexWrap:"wrap",gap:4,marginTop:6 }}>
                          {ALL_TABS.filter(t=>u.perms[t.id]).map(t=><span key={t.id} style={{ fontSize:10,background:"#f0f0f0",borderRadius:4,padding:"1px 6px",color:"#666" }}>{t.icon}{t.label}</span>)}
                        </div>
                      </div>
                      <div style={{ color:"#ccc",fontSize:20 }}>›</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 個別編集 */}
      {section==="list" && editingId && editPerms && editInfo && (
        <div>
          <button onClick={()=>setEditingId(null)} style={{ background:"none",border:"none",color:"#52B788",cursor:"pointer",fontSize:14,fontWeight:700,marginBottom:16 }}>← 一覧へ戻る</button>
          {/* アカウント情報 */}
          <div style={{ background:"#fff",borderRadius:14,padding:20,border:"1px solid #eee",marginBottom:16 }}>
            <div style={{ fontWeight:700,fontSize:14,color:"#333",marginBottom:14 }}>👤 アカウント情報</div>
            {isUserDisabled(editingUser)&&<div style={{ background:"#fff1f0",border:"1px solid #f3b6b2",borderRadius:8,padding:"9px 12px",fontSize:12,color:"#8f1d18",marginBottom:12 }}>このアカウントは{editingUser.canonical_user_name || "正本ユーザー"}へ統合済みです。過去履歴の表示用として保持されています。</div>}
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
              <div><span style={lblS}>氏名</span><input value={editInfo.name} onChange={e=>setEditInfo({...editInfo,name:e.target.value})} style={inpS}/></div>
              <div><span style={lblS}>ログインID</span><input value={editInfo.loginId} onChange={e=>setEditInfo({...editInfo,loginId:e.target.value})} style={inpS}/></div>
              <div><span style={lblS}>新パスワード（変更する場合のみ・{MIN_PASSWORD_LENGTH}文字以上）</span><input type="password" autoComplete="new-password" minLength={MIN_PASSWORD_LENGTH} value={editInfo.password} onChange={e=>setEditInfo({...editInfo,password:e.target.value})} placeholder="変更しない場合は空欄" style={inpS}/></div>
              <div><span style={lblS}>役職（変更は専用権限画面が必要）</span><select value={editInfo.role} disabled style={{...selS,width:"100%"}}>{Object.entries(ROLE_LABELS).map(([k,v])=><option key={k} value={k}>{ROLE_ICONS[k]} {v}</option>)}</select></div>
              <div><span style={lblS}>担当店舗（変更は専用権限画面が必要）</span><select value={editInfo.store} disabled style={{...selS,width:"100%"}}>{STORES.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            </div>
          </div>
          {/* タブ権限 */}
          <div style={{ background:"#fff",borderRadius:14,padding:20,border:"1px solid #eee",marginBottom:16 }}>
            <div style={{ fontWeight:700,fontSize:14,color:"#333",marginBottom:6 }}>📋 タブ表示権限</div>
            <div style={{ fontSize:12,color:"#aaa",marginBottom:14 }}>ONにしたタブのみ表示されます</div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
              {ALL_TABS.map(t=>(
                <label key={t.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:editPerms[t.id]?"#eafaf1":"#f9f9f9",border:`1px solid ${editPerms[t.id]?"#52B788":"#eee"}`,borderRadius:10,cursor:"pointer" }}>
                  <div style={{ position:"relative",width:40,height:22,background:editPerms[t.id]?"#52B788":"#ccc",borderRadius:11,flexShrink:0 }} onClick={()=>setEditPerms({...editPerms,[t.id]:!editPerms[t.id]})}>
                    <div style={{ position:"absolute",top:2,left:editPerms[t.id]?20:2,width:18,height:18,background:"#fff",borderRadius:"50%",transition:"left 0.2s",boxShadow:"0 1px 3px #0003" }}/>
                  </div>
                  <span style={{ fontSize:13,fontWeight:editPerms[t.id]?700:400,color:editPerms[t.id]?"#2D6A4F":"#999" }}>{t.icon} {t.label}</span>
                </label>
              ))}
            </div>
          </div>
          {/* 詳細権限 */}
          <div style={{ background:"#fff",borderRadius:14,padding:20,border:"1px solid #eee",marginBottom:16 }}>
            <div style={{ fontWeight:700,fontSize:14,color:"#333",marginBottom:14 }}>⚙️ 詳細権限</div>
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
              {[["canEditShift","シフト表を編集できる"],["canSeeAllAttendance","全スタッフの出退勤を閲覧できる"],["canPost","連絡を投稿できる"],["isAdmin","管理者（権限管理タブを使える）"]].map(([key,label])=>(
                <label key={key} style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:editPerms[key]?"#e8f4fb":"#f9f9f9",border:`1px solid ${editPerms[key]?"#457B9D":"#eee"}`,borderRadius:10,cursor:"pointer" }}>
                  <div style={{ position:"relative",width:40,height:22,background:editPerms[key]?"#457B9D":"#ccc",borderRadius:11,flexShrink:0 }} onClick={()=>setEditPerms({...editPerms,[key]:!editPerms[key]})}>
                    <div style={{ position:"absolute",top:2,left:editPerms[key]?20:2,width:18,height:18,background:"#fff",borderRadius:"50%",transition:"left 0.2s",boxShadow:"0 1px 3px #0003" }}/>
                  </div>
                  <span style={{ fontSize:13,fontWeight:editPerms[key]?700:400,color:editPerms[key]?"#1D3557":"#999" }}>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div style={{ display:"flex",gap:10 }}>
            <button onClick={savePerms} disabled={saving || isUserDisabled(editingUser)} style={{ ...btnS("#2D6A4F",saving || isUserDisabled(editingUser)),flex:1,padding:13,fontSize:15 }}>💾 保存する</button>
            {(currentUser.perms.isAdmin || currentUser.role==="manager") && editingId!==currentUser.id && !isUserDisabled(editingUser) && <button onClick={()=>disableUser(editingUser)} style={{ ...btnS("#e67e22",false),padding:13,fontSize:13 }}>無効化</button>}
            {(currentUser.perms.isAdmin || currentUser.role==="manager") && isUserDisabled(editingUser) && <button onClick={()=>reactivateUser(editingUser)} style={{ ...btnS("#457B9D",false),padding:13,fontSize:13 }}>再有効化</button>}
          </div>
          {/* ログイン情報共有 */}
          <div style={{ marginTop:16,background:"#f0f7ff",borderRadius:12,padding:"14px 16px",border:"1px solid #bfdbfe" }}>
            <div style={{ fontWeight:700,fontSize:13,color:"#1E40AF",marginBottom:10 }}>📨 ログイン情報をスタッフに送る</div>
            <div style={{ fontSize:12,color:"#555",marginBottom:10,background:"#fff",borderRadius:8,padding:"10px 12px",border:"1px solid #e0e7ff",lineHeight:1.8,whiteSpace:"pre-wrap" }}>{`【ファイブ業務管理アプリ ログイン情報】\n\n${editInfo.name} さん\n\nアプリURL: ${window.location.origin}/\nログインID: ${editInfo.loginId}\n\nパスワードは安全な別経路でお伝えください。\n\nスマホはSafariで開いて「ホーム画面に追加」するとアプリのように使えます。`}</div>
            <ShareButtons title={`【ファイブ業務管理アプリ】${editInfo.name}さんのログイン情報`} body={`アプリURL: ${window.location.origin}/\nログインID: ${editInfo.loginId}\n\nパスワードは安全な別経路でお伝えください。\n\nスマホはSafariで開いて「ホーム画面に追加」するとアプリのように使えます。`}/>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SHIFT TAB（サーバー保存）
// ═══════════════════════════════════════════════════════════
export function getShiftDeleteConfirmation({ row, staffName, shiftDate, storeName, publicationStatus }) {
  const date = new Date(`${shiftDate}T00:00:00`);
  const dateLabel = `${date.getMonth() + 1}月${date.getDate()}日`;
  let timeLabel = row?.shift_value || "登録済みシフト";
  if (row?.preset_code === OFF_SHIFT_CODE) {
    timeLabel = "休み";
  } else if (row?.start_time && row?.end_time) {
    const start = String(row.start_time).slice(0, 5);
    const end = String(row.end_time).slice(0, 5);
    timeLabel = `${start}～${Number(row.end_day_offset) === 1 ? "翌" : ""}${end}`;
  }
  const base = `${staffName}さんの${storeName}・${dateLabel} ${timeLabel} のシフトを削除します。よろしいですか？`;
  return publicationStatus === "published"
    ? `${base}\n\nこのシフトを削除すると、この月のシフトは下書きに戻ります。変更後は再度「スタッフにシフトを公開する」を実行してください。`
    : base;
}

export function getShiftPublicationState(data = {}) {
  const publication = data.publication || {};
  const status = publication.status || data.publication_status;
  if (!['draft', 'published'].includes(status)) {
    return { status: "error", publishedAt: null, publishedByName: null };
  }
  return {
    status,
    publishedAt: status === "published" ? publication.published_at || null : null,
    publishedByName: status === "published" ? publication.published_by_name || null : null,
  };
}

export function formatShiftMonthLabel(ym) {
  const match = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  return match ? `${Number(match[1])}年${Number(match[2])}月` : String(ym || "");
}

export function formatShiftPublishedAt(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return match
    ? `${Number(match[1])}年${Number(match[2])}月${Number(match[3])}日 ${match[4]}:${match[5]}`
    : String(value || "");
}

export function getJapanYearMonth(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(date);
  const year = parts.find(part => part.type === "year")?.value;
  const month = parts.find(part => part.type === "month")?.value;
  return `${year}-${month}`;
}

export function formatLocalDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function getShiftDateAfterDays(dateStr, diff) {
  const match = String(dateStr || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(dateStr || "");
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  date.setDate(date.getDate() + diff);
  return formatLocalDateKey(date);
}

export function getTodayShiftDate() {
  return formatLocalDateKey(new Date());
}

export function KasamaShiftEditor({ row, staff, shiftDate, storeId, storeName, publicationStatus, disabled, onSave, onDelete }) {
  const [draft, setDraft] = useState(() => getShiftEditorInitialState(row, storeId));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState("");
  const [saveHovered, setSaveHovered] = useState(false);

  useEffect(() => {
    setDraft(getShiftEditorInitialState(row, storeId));
    setMessage("");
  }, [row, storeId, shiftDate]);

  const selectPreset = code => {
    const preset = getShiftPreset(storeId, code);
    setDraft(current => ({
      ...current,
      presetCode: code,
      startTime: preset?.startTime || (code === CUSTOM_SHIFT_CODE ? current.startTime : ""),
      endTime: preset?.endTime || (code === CUSTOM_SHIFT_CODE ? current.endTime : ""),
      endDayOffset: preset?.endDayOffset ?? (code === CUSTOM_SHIFT_CODE ? current.endDayOffset : 0),
    }));
    setMessage("");
  };

  const save = async () => {
    if (!draft.presetCode) {
      setMessage("勤務時間帯を選択してください");
      return;
    }
    if (draft.presetCode === CUSTOM_SHIFT_CODE) {
      const validationError = validateCustomShift(draft);
      if (validationError) {
        setMessage(validationError);
        return;
      }
    }

    setSaving(true);
    setMessage("");
    try {
      await onSave(buildStructuredShiftPayload({
        storeId,
        staff,
        shiftDate,
        presetCode: draft.presetCode,
        startTime: draft.startTime,
        endTime: draft.endTime,
        endDayOffset: draft.endDayOffset,
      }));
      setMessage("保存しました");
    } catch (e) {
      setMessage(e.message || "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const customValidationError = draft.presetCode === CUSTOM_SHIFT_CODE
    ? validateCustomShift(draft)
    : "";
  const saveDisabled = disabled || saving || !draft.presetCode || Boolean(customValidationError);

  const remove = async () => {
    if (!row?.id || !onDelete) return;
    const confirmed = window.confirm(getShiftDeleteConfirmation({
      row,
      staffName: staff.name,
      shiftDate,
      storeName,
      publicationStatus,
    }));
    if (!confirmed) return;

    setDeleting(true);
    setMessage("");
    try {
      await onDelete(row);
      setMessage("削除しました");
    } catch (e) {
      setMessage(e.message || "削除に失敗しました");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {draft.legacyValue && (
        <div style={{ fontSize: 13, color: "#6B5B35", background: "#FFF8E1", padding: "7px 9px", borderRadius: 6 }}>
          旧形式の登録値: {draft.legacyValue}（保存するまで変更されません）
        </div>
      )}
      <select
        aria-label={`${staff.name}の勤務時間帯`}
        value={draft.presetCode}
        onChange={event => selectPreset(event.target.value)}
        disabled={disabled || saving}
        style={{
          ...selS,
          ...(disabled ? getShiftLockedFieldStyle(Boolean(draft.presetCode || draft.legacyValue)) : {}),
        }}
      >
        <option value="">勤務時間帯を選択</option>
        {(SHIFT_PRESETS_BY_STORE[storeId] || []).map(preset => (
          <option key={preset.code} value={preset.code}>{preset.label}</option>
        ))}
        <option value={CUSTOM_SHIFT_CODE}>任意入力</option>
        <option value={OFF_SHIFT_CODE}>休み</option>
      </select>

      {draft.presetCode === CUSTOM_SHIFT_CODE && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13 }}>
            開始 <input type="time" value={draft.startTime} disabled={disabled || saving}
              onChange={event => setDraft(current => ({ ...current, startTime: event.target.value }))}
              style={disabled ? getShiftLockedFieldStyle(Boolean(draft.startTime)) : undefined} />
          </label>
          <label style={{ fontSize: 13 }}>
            終了 <input type="time" value={draft.endTime} disabled={disabled || saving}
              onChange={event => setDraft(current => ({ ...current, endTime: event.target.value }))}
              style={disabled ? getShiftLockedFieldStyle(Boolean(draft.endTime)) : undefined} />
          </label>
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" checked={draft.endDayOffset === 1} disabled={disabled || saving}
              onChange={event => setDraft(current => ({ ...current, endDayOffset: event.target.checked ? 1 : 0 }))} /> 翌日終了
          </label>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={save}
          disabled={saveDisabled}
          onMouseEnter={() => !saveDisabled && setSaveHovered(true)}
          onMouseLeave={() => setSaveHovered(false)}
          style={getShiftSaveButtonStyle(storeId, saveDisabled, saveHovered)}
        >
          {saving ? "保存中..." : "シフトを保存"}
        </button>
        {row?.id && (
          <button
            type="button"
            onClick={remove}
            disabled={disabled || saving || deleting}
            style={{ ...btnS("#B42318", disabled || saving || deleting), background: disabled || saving || deleting ? "#eee" : "#fff", color: disabled || saving || deleting ? "#aaa" : "#B42318", border: "1px solid currentColor" }}
          >
            {deleting ? "削除中..." : "シフトを削除"}
          </button>
        )}
        {message && <span role="status" style={{ fontSize: 12, color: ["保存しました", "削除しました"].includes(message) ? "#287A4D" : "#B42318" }}>{message}</span>}
      </div>
    </div>
  );
}

export function groupShiftRows(rows) {
  const grouped = {};
  (rows || []).forEach(row => {
    const day = row.shift_date || row.day_week;
    if (!grouped[day]) grouped[day] = {};
    const key = row.user_id ? String(row.user_id) : `legacy:${row.staff_name}`;
    grouped[day][key] = row;
  });
  return grouped;
}

export function getShiftMonthDates(ym) {
  const match = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return [];
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return [];
  const lastDay = new Date(year, month, 0).getDate();
  return Array.from(
    { length: lastDay },
    (_, index) => `${match[1]}-${match[2]}-${String(index + 1).padStart(2, "0")}`
  );
}

function getStaffStoreId(staff) {
  return String(staff?.store_id || staff?.store || staff?.storeId || "");
}

export function getShiftWorktableStaff({ users = [], shiftStoreId, shifts = {} }) {
  const result = [];
  const seenKeys = new Set();
  const usersById = new Map(
    users.filter(staff => staff?.id !== null && staff?.id !== undefined)
      .map(staff => [String(staff.id), staff])
  );

  const addStaff = staff => {
    if (seenKeys.has(staff.key)) return;
    seenKeys.add(staff.key);
    result.push(staff);
  };

  users.forEach(staff => {
    if (getStaffStoreId(staff) !== String(shiftStoreId) || staff?.id === null || staff?.id === undefined) return;
    const userId = String(staff.id);
    addStaff({ key: `user:${userId}`, rowKey: userId, name: staff.name || `職員ID ${userId}`, isLegacy: false });
  });

  Object.values(shifts).forEach(dayRows => {
    Object.values(dayRows || {}).forEach(row => {
      if (row?.user_id !== null && row?.user_id !== undefined && String(row.user_id) !== "") {
        const userId = String(row.user_id);
        const currentUser = usersById.get(userId);
        addStaff({
          key: `user:${userId}`,
          rowKey: userId,
          name: currentUser?.name || row.staff_name || `職員ID ${userId}`,
          isLegacy: false,
        });
        return;
      }

      const staffName = String(row?.staff_name || "").trim() || "氏名不明";
      const rowKey = `legacy:${row?.staff_name}`;
      addStaff({ key: rowKey, rowKey, name: staffName, isLegacy: true });
    });
  });

  return result;
}

function getShiftWorktableDetail(row) {
  if (!row) return "未登録（休みではありません）";
  const symbol = getShiftWorktableSymbol(row);
  const displayValue = getShiftDisplayValue(row);
  if (symbol === "○") return `出勤${displayValue ? `、${displayValue}` : ""}`;
  if (symbol === "/") return "休み";
  if (symbol === "■") return `夜勤${displayValue ? `、${displayValue}` : ""}`;
  if (row.preset_code === CUSTOM_SHIFT_CODE) {
    return `未判定（custom${displayValue ? `、勤務時刻 ${displayValue}` : ""}）`;
  }
  if (row.preset_code) {
    return `未判定（preset_code: ${row.preset_code}${displayValue ? `、登録内容 ${displayValue}` : ""}）`;
  }
  return `未判定（旧形式${row.shift_value ? `、登録値 ${row.shift_value}` : "、登録値なし"}）`;
}

function ShiftWorktable({ ym, shifts, users, shiftStoreId, color }) {
  const dates = getShiftMonthDates(ym);
  const staffRows = getShiftWorktableStaff({ users, shiftStoreId, shifts });
  const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
  const scrollRef = useRef(null);
  const animationFrameRef = useRef(null);
  const zoomRef = useRef(SHIFT_TABLE_ZOOM_DEFAULT);
  const [zoom, setZoom] = useState(() => {
    try {
      return normalizeShiftTableZoom(window.localStorage.getItem(SHIFT_TABLE_ZOOM_STORAGE_KEY));
    } catch (_error) {
      return SHIFT_TABLE_ZOOM_DEFAULT;
    }
  });
  const updateZoom = useCallback(nextZoom => {
    const normalized = normalizeShiftTableZoom(nextZoom);
    zoomRef.current = normalized;
    setZoom(normalized);
  }, []);

  useEffect(() => {
    zoomRef.current = zoom;
    try {
      window.localStorage.setItem(SHIFT_TABLE_ZOOM_STORAGE_KEY, String(zoom));
    } catch (_error) {
      // 表示倍率だけの補助設定なので、storage利用不可時はページ内stateで継続する。
    }
  }, [zoom]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return undefined;
    let gesture = null;

    const scheduleScroll = ({ contentX, contentY, viewportX, viewportY, nextZoom }) => {
      if (animationFrameRef.current !== null && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
      const applyScroll = () => {
        animationFrameRef.current = null;
        scrollElement.scrollLeft = calculateZoomedScrollOffset({ contentPosition: contentX, viewportPosition: viewportX, zoom: nextZoom });
        scrollElement.scrollTop = calculateZoomedScrollOffset({ contentPosition: contentY, viewportPosition: viewportY, zoom: nextZoom });
      };
      if (window.requestAnimationFrame) {
        animationFrameRef.current = window.requestAnimationFrame(applyScroll);
      } else {
        applyScroll();
      }
    };

    const handleTouchStart = event => {
      if (event.touches.length !== 2) return;
      const geometry = getTouchGeometry(event.touches);
      if (!geometry) return;
      event.preventDefault();
      const rect = scrollElement.getBoundingClientRect();
      const viewportX = geometry.midpointX - rect.left;
      const viewportY = geometry.midpointY - rect.top;
      const startZoom = zoomRef.current;
      gesture = {
        startDistance: geometry.distance,
        startZoom,
        viewportX,
        viewportY,
        contentX: calculatePinchContentPosition({ scrollOffset: scrollElement.scrollLeft, viewportPosition: viewportX, zoom: startZoom }),
        contentY: calculatePinchContentPosition({ scrollOffset: scrollElement.scrollTop, viewportPosition: viewportY, zoom: startZoom }),
      };
    };

    const handleTouchMove = event => {
      if (!gesture || event.touches.length !== 2) return;
      const geometry = getTouchGeometry(event.touches);
      if (!geometry) return;
      event.preventDefault();
      const nextZoom = calculatePinchZoom({
        startZoom: gesture.startZoom,
        startDistance: gesture.startDistance,
        currentDistance: geometry.distance,
      });
      updateZoom(nextZoom);
      const rect = scrollElement.getBoundingClientRect();
      scheduleScroll({
        ...gesture,
        viewportX: geometry.midpointX - rect.left,
        viewportY: geometry.midpointY - rect.top,
        nextZoom,
      });
    };

    const endGesture = event => {
      if (!event.touches || event.touches.length < 2) gesture = null;
    };

    scrollElement.addEventListener("touchstart", handleTouchStart, { passive: false });
    scrollElement.addEventListener("touchmove", handleTouchMove, { passive: false });
    scrollElement.addEventListener("touchend", endGesture);
    scrollElement.addEventListener("touchcancel", endGesture);
    return () => {
      scrollElement.removeEventListener("touchstart", handleTouchStart);
      scrollElement.removeEventListener("touchmove", handleTouchMove);
      scrollElement.removeEventListener("touchend", endGesture);
      scrollElement.removeEventListener("touchcancel", endGesture);
      if (animationFrameRef.current !== null && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [updateZoom]);

  const headerStyle = {
    position: "sticky",
    top: 0,
    zIndex: 3,
    borderRight: "1px solid #ddd",
    borderBottom: "1px solid #ccc",
    background: "#F4F6F8",
    color: "#333",
    textAlign: "center",
    whiteSpace: "nowrap",
  };
  const staffHeaderStyle = {
    position: "sticky",
    left: 0,
    zIndex: 2,
    borderRight: "1px solid #ccc",
    borderBottom: "1px solid #ddd",
    background: "#fff",
    color: "#222",
    textAlign: "left",
    overflowWrap: "anywhere",
  };

  return (
    <section aria-labelledby="shift-worktable-title">
      <style>{`
        .shift-worktable-day-header { min-width: 56px; width: 56px; padding: 8px 5px; }
        .shift-worktable-staff-cell { min-width: 150px; width: 150px; max-width: 150px; padding: 9px 10px; font-size: 14px; }
        .shift-worktable-day-cell { min-width: 56px; width: 56px; height: 48px; padding: 7px 5px; font-size: 18px; }
        @media (max-width: 600px) {
          .shift-worktable-day-header { min-width: 48px; width: 48px; padding: 7px 3px; }
          .shift-worktable-staff-cell { min-width: 124px; width: 124px; max-width: 124px; padding: 8px 7px; font-size: 14px; }
          .shift-worktable-day-cell { min-width: 48px; width: 48px; height: 46px; padding: 6px 3px; font-size: 18px; }
        }
      `}</style>
      <h3 id="shift-worktable-title" style={{ margin: "0 0 10px", color }}>
        {formatShiftMonthLabel(ym)} 勤務表
      </h3>
      <div
        role="group"
        aria-label="勤務表の拡大縮小"
        style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}
      >
        <button
          type="button"
          aria-label="勤務表を縮小"
          disabled={zoom <= SHIFT_TABLE_ZOOM_MIN}
          onClick={() => updateZoom(adjustShiftTableZoom(zoomRef.current, "out"))}
          style={{ ...btnS("#fff", zoom <= SHIFT_TABLE_ZOOM_MIN), minWidth: 48, minHeight: 44, padding: "8px 12px", color: zoom <= SHIFT_TABLE_ZOOM_MIN ? "#777" : "#333", border: "1px solid #C7CDD1" }}
        >
          縮小
        </button>
        <output aria-label="勤務表の表示倍率" aria-live="polite" style={{ minWidth: 54, textAlign: "center", fontWeight: 800, color: "#333" }}>
          {Math.round(zoom * 100)}%
        </output>
        <button
          type="button"
          aria-label="勤務表を拡大"
          disabled={zoom >= SHIFT_TABLE_ZOOM_MAX}
          onClick={() => updateZoom(adjustShiftTableZoom(zoomRef.current, "in"))}
          style={{ ...btnS(color, zoom >= SHIFT_TABLE_ZOOM_MAX), minWidth: 48, minHeight: 44, padding: "8px 12px" }}
        >
          拡大
        </button>
        <button
          type="button"
          aria-label="勤務表の表示倍率を標準に戻す"
          disabled={zoom === SHIFT_TABLE_ZOOM_DEFAULT}
          onClick={() => updateZoom(SHIFT_TABLE_ZOOM_DEFAULT)}
          style={{ ...btnS("#fff", zoom === SHIFT_TABLE_ZOOM_DEFAULT), minHeight: 44, padding: "8px 12px", color: zoom === SHIFT_TABLE_ZOOM_DEFAULT ? "#777" : "#333", border: "1px solid #C7CDD1" }}
        >
          標準に戻す
        </button>
      </div>
      <p id="shift-worktable-zoom-help" style={{ margin: "0 0 8px", fontSize: 12, lineHeight: 1.5, color: "#555" }}>
        表の上で2本指を広げると拡大、縮めると縮小できます。1本指では縦横に移動できます。
      </p>
      <div
        ref={scrollRef}
        data-testid="shift-worktable-scroll"
        aria-describedby="shift-worktable-zoom-help"
        style={{
          width: "100%",
          maxWidth: "100%",
          maxHeight: "min(65vh, 620px)",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x pan-y",
          overscrollBehavior: "contain",
          border: "1px solid #ccc",
          borderRadius: 10,
          background: "#fff",
        }}
      >
        <table
          aria-label="勤務表"
          className="shift-worktable-table"
          data-worktable-zoom={zoom}
          style={{
            width: "max-content",
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "fixed",
            zoom,
          }}
        >
          <thead>
            <tr>
              <th
                scope="col"
                className="shift-worktable-staff-cell"
                style={{ ...staffHeaderStyle, top: 0, zIndex: 4, background: "#EEF3F6" }}
              >
                職員
              </th>
              {dates.map(date => {
                const [year, month, day] = date.split("-").map(Number);
                const weekday = weekdays[new Date(year, month - 1, day, 12).getDay()];
                return (
                  <th key={date} scope="col" data-worktable-date={date} className="shift-worktable-day-header" style={headerStyle}>
                    <span style={{ display: "block", fontSize: 14 }}>{day}</span>
                    <span style={{ display: "block", fontSize: 11, color: "#666" }}>{weekday}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {staffRows.length === 0 ? (
              <tr>
                <th scope="row" className="shift-worktable-staff-cell" style={staffHeaderStyle}>職員なし</th>
                <td colSpan={dates.length} style={{ padding: 16, color: "#666" }}>表示対象の職員はいません</td>
              </tr>
            ) : staffRows.map(staff => (
              <tr key={staff.key} data-worktable-staff={staff.key}>
                <th scope="row" className="shift-worktable-staff-cell" style={staffHeaderStyle}>
                  {staff.name}
                  {staff.isLegacy && <span style={{ display: "block", marginTop: 2, fontSize: 10, color: "#7A5B00" }}>旧形式データ</span>}
                </th>
                {dates.map(date => {
                  const row = shifts[date]?.[staff.rowKey] || null;
                  const symbol = getShiftWorktableSymbol(row);
                  const detail = getShiftWorktableDetail(row);
                  return (
                    <td
                      key={date}
                      data-shift-date={date}
                      data-staff-key={staff.key}
                      className="shift-worktable-day-cell"
                      title={detail}
                      aria-label={`${staff.name}、${date}：${detail}`}
                      style={{
                        borderRight: "1px solid #eee",
                        borderBottom: "1px solid #eee",
                        color: symbol === "？" ? "#8A5A00" : symbol === "—" ? "#777" : "#222",
                        background: symbol === "？" ? "#FFF8E1" : "#fff",
                        fontWeight: symbol === "—" ? 500 : 800,
                        textAlign: "center",
                      }}
                    >
                      {symbol}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div aria-label="勤務表の凡例" style={{ marginTop: 12, display: "grid", gap: 7, fontSize: 13, color: "#444" }}>
        <div><strong>正式な勤務記号：</strong> ○ 出勤　/ 休み　■ 夜勤</div>
        <div><strong>システム上の補助表示：</strong> ？ 未判定　— 未登録（休みではありません）</div>
      </div>
    </section>
  );
}

function dayRowsContainStaff(dayRows, staff) {
  return Object.values(dayRows || {}).some(row =>
    (row?.user_id && String(row.user_id) === String(staff.id))
    || (!row?.user_id && row?.staff_name === staff.name)
  );
}

export function getShiftStaffForDate({ users, shiftStoreId, dayRows, addedStaffIds, canManageShift, shiftUserIds }) {
  if (!canManageShift) {
    return users.filter(staff => shiftUserIds.has(String(staff.id)));
  }

  const addedIds = new Set((addedStaffIds || []).map(String));
  return users.filter(staff =>
    dayRowsContainStaff(dayRows, staff)
    || (!isUserDisabled(staff) && (
      getStaffStoreId(staff) === String(shiftStoreId)
      || addedIds.has(String(staff.id))
    ))
  );
}

export function getOtherStoreStaffCandidates({ users, shiftStoreId, dayRows, addedStaffIds }) {
  const addedIds = new Set((addedStaffIds || []).map(String));
  return users.filter(staff =>
    !isUserDisabled(staff)
    && getStaffStoreId(staff) !== String(shiftStoreId)
    && !addedIds.has(String(staff.id))
    && !dayRowsContainStaff(dayRows, staff)
  );
}

export function ShiftTab({ store, user, users }) {
  const [ym, setYm] = useState(() => getJapanYearMonth());
  const [viewMode, setViewMode] = useState("calendar");
  const [selectedDate, setSelectedDate] = useState(null);
  const [shifts, setShifts] = useState({});
  const [loading, setLoading] = useState(false);
  const initialStoreId = store.id || store.store_id || store.storeId;
  const [shiftStoreId, setShiftStoreId] = useState(initialStoreId);
  const [accessibleStoreIds, setAccessibleStoreIds] = useState([initialStoreId]);
  const [publicationStatus, setPublicationStatus] = useState("loading");
  const [publicationDetails, setPublicationDetails] = useState({ publishedAt: null, publishedByName: null });
  const [publicationMessage, setPublicationMessage] = useState("");
  const [publicationError, setPublicationError] = useState(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showDraftConfirm, setShowDraftConfirm] = useState(false);
  const [publicationSaving, setPublicationSaving] = useState(false);
  const [addedStaffIdsByDate, setAddedStaffIdsByDate] = useState({});
  const [showOtherStaffPicker, setShowOtherStaffPicker] = useState(false);
  const pendingSelectedDateRef = useRef(null);
  const canManageShift = ["president", "manager"].includes(user?.role);
  const isUnpublishedViewer = !canManageShift && publicationStatus === "draft";
  const canEditShiftMonth = canManageShift && publicationStatus === "draft";
  const canOpenShiftDays = ["draft", "published"].includes(publicationStatus)
    && (canManageShift || publicationStatus === "published");
  const shiftStore = STORES.find(item => item.id === shiftStoreId) || store;

  useEffect(() => {
    setShiftStoreId(initialStoreId);
  }, [initialStoreId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api(`/shifts.php?access=1&ym=${ym}`);
        if (cancelled) return;
        const nextIds = data.store_ids || [initialStoreId];
        setAccessibleStoreIds(nextIds);
        if (!nextIds.includes(shiftStoreId)) setShiftStoreId(initialStoreId);
      } catch (error) {
        if (!cancelled) setAccessibleStoreIds([initialStoreId]);
      }
    })();
    return () => { cancelled = true; };
  }, [ym, user?.id, initialStoreId, shiftStoreId]);

  const shiftUserIds = new Set(
    Object.values(shifts).flatMap(dayRows =>
      Object.values(dayRows || {}).map(row => String(row.user_id || ""))
    )
  );
  const selectedDayRows = selectedDate ? (shifts[selectedDate] || {}) : {};
  const selectedDateAddedStaffIds = selectedDate ? (addedStaffIdsByDate[selectedDate] || []) : [];
  const storeStaff = getShiftStaffForDate({
    users,
    shiftStoreId,
    dayRows: selectedDayRows,
    addedStaffIds: selectedDateAddedStaffIds,
    canManageShift,
    shiftUserIds,
  });
  const otherStoreStaffCandidates = getOtherStoreStaffCandidates({
    users,
    shiftStoreId,
    dayRows: selectedDayRows,
    addedStaffIds: selectedDateAddedStaffIds,
  });
  const sortedStoreStaff = [
    ...storeStaff.filter(s => s.name === user.name),
    ...storeStaff.filter(s => s.name !== user.name),
  ];

  useEffect(() => {
    let cancelled = false;
    const pendingSelectedDate = pendingSelectedDateRef.current;
    pendingSelectedDateRef.current = null;
    setShifts({});
    setSelectedDate(pendingSelectedDate?.startsWith(`${ym}-`) ? pendingSelectedDate : null);
    setPublicationStatus("loading");
    setPublicationDetails({ publishedAt: null, publishedByName: null });
    setPublicationMessage("");
    setPublicationError(null);
    setShowPublishConfirm(false);
    setShowDraftConfirm(false);
    setAddedStaffIdsByDate({});
    setShowOtherStaffPicker(false);
    if (!shiftStoreId) return () => { cancelled = true; };
    setLoading(true);
    (async () => {
      try {
        const data = await api(`/shifts.php?store=${shiftStoreId}&ym=${ym}`);
        if (cancelled) return;
        const publication = getShiftPublicationState(data);
        setShifts(groupShiftRows(data.shifts || []));
        setPublicationStatus(publication.status);
        setPublicationDetails({
          publishedAt: publication.publishedAt,
          publishedByName: publication.publishedByName,
        });
      } catch (error) {
        if (!cancelled) {
          setPublicationStatus("error");
          setPublicationDetails({ publishedAt: null, publishedByName: null });
          setPublicationMessage(error.message || "公開状態を取得できません");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ym, shiftStoreId]);

  const changeMonth = (diff) => {
    const d = new Date(`${ym}-01`);
    d.setMonth(d.getMonth() + diff);
    setYm(d.toISOString().slice(0, 7));
  };

  const getMonthDays = () => {
    const [y, m] = ym.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    const days = [];

    for (let i = 0; i < first.getDay(); i++) {
      days.push(null);
    }

    for (let d = 1; d <= last.getDate(); d++) {
      const date = `${ym}-${String(d).padStart(2, "0")}`;
      days.push(date);
    }

    return days;
  };

  const handleChange = async (staff, val) => {
    if (!selectedDate || !canEditShiftMonth) return;

    const next = {
      ...shifts,
      [selectedDate]: {
        ...(shifts[selectedDate] || {}),
        [`legacy:${staff.name}`]: {
          ...((shifts[selectedDate] || {})[`legacy:${staff.name}`] || {}),
          staff_name: staff.name,
          day_week: selectedDate,
          shift_value: val,
        }
      }
    };

    setShifts(next);

    try {
      await api("/shifts.php", "POST", {
        store_id: shiftStoreId,
        staff_name: staff.name,
        year_month: ym,
        day_of_week: selectedDate,
        shift_value: val
      });
      setPublicationStatus("draft");
      setPublicationDetails({ publishedAt: null, publishedByName: null });
    } catch (e) {
      console.error(e);
      alert(e.message || "保存に失敗しました");
    }
  };

  const handleStructuredSave = async payload => {
    if (!canEditShiftMonth) {
      throw new Error("公開中のため編集できません。先に下書きへ戻してください");
    }
    const data = await api("/shifts.php", "POST", payload);
    const saved = data.saved;
    setShifts(current => ({
      ...current,
      [selectedDate]: {
        ...(current[selectedDate] || {}),
        [String(payload.user_id)]: saved,
      },
    }));
    setPublicationStatus(getShiftPublicationState(data).status);
    setPublicationDetails({ publishedAt: null, publishedByName: null });
  };

  const handleStructuredDelete = async shift => {
    if (!canEditShiftMonth) {
      throw new Error("公開中のため編集できません。先に下書きへ戻してください");
    }
    const shiftId = Number(shift?.id);
    if (!Number.isInteger(shiftId) || shiftId <= 0 || !selectedDate) {
      throw new Error("削除対象のシフトを確認できません");
    }
    const data = await api(`/shifts.php?shift_id=${encodeURIComponent(shiftId)}&store_id=${encodeURIComponent(shiftStoreId)}`, "DELETE");
    setShifts(current => {
      const currentDay = { ...(current[selectedDate] || {}) };
      Object.keys(currentDay).forEach(key => {
        if (Number(currentDay[key]?.id) === shiftId) delete currentDay[key];
      });
      return { ...current, [selectedDate]: currentDay };
    });
    setPublicationStatus(getShiftPublicationState(data).status);
    setPublicationDetails({ publishedAt: null, publishedByName: null });
    setPublicationMessage(publicationStatus === "published"
      ? "シフトを削除しました。内容を確認後、再度スタッフに公開してください。"
      : "シフトを削除しました。");
  };

  const addOtherStoreStaff = staffId => {
    if (!selectedDate) return;
    setAddedStaffIdsByDate(current => ({
      ...current,
      [selectedDate]: [...new Set([...(current[selectedDate] || []), String(staffId)])],
    }));
  };

  const updatePublicationStatus = async status => {
    setPublicationMessage("");
    setPublicationError(null);
    setPublicationSaving(true);
    try {
      const data = await api("/shifts.php", "PUT", {
        store_id: shiftStoreId,
        year_month: ym,
        status,
      });
      const publication = getShiftPublicationState(data);
      setPublicationStatus(publication.status);
      setPublicationDetails({
        publishedAt: publication.publishedAt,
        publishedByName: publication.publishedByName,
      });
      setPublicationMessage(status === "published" ? "公開しました" : "下書きに戻しました");
    } catch (error) {
      setPublicationError({
        message: error.message || "公開状態の変更に失敗しました",
        causeCode: error.payload?.cause_code || "PUBLICATION_FAILED",
        targets: Array.isArray(error.payload?.targets) ? error.payload.targets : [],
      });
    } finally {
      setPublicationSaving(false);
    }
  };

  const showShiftDate = targetDate => {
    if (!targetDate) return;
    setShowOtherStaffPicker(false);
    const targetMonth = targetDate.slice(0, 7);
    if (targetMonth !== ym) {
      pendingSelectedDateRef.current = targetDate;
      setSelectedDate(targetDate);
      setYm(targetMonth);
      return;
    }
    setSelectedDate(targetDate);
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    const d = new Date(year, month - 1, day, 12);
    const w = ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${w}）`;
  };


const days = getMonthDays();

const todayStr = getTodayShiftDate();
const selectedShiftCount = Object.values(selectedDayRows).filter(shift => shift?.shift_value).length;

  return (
    <div>
      {accessibleStoreIds.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          <span style={lblS}>シフト施設</span>
          <select
            aria-label="シフト施設"
            value={shiftStoreId}
            onChange={event => setShiftStoreId(event.target.value)}
            style={selS}
          >
            {STORES.filter(item => accessibleStoreIds.includes(item.id)).map(item => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <button onClick={() => changeMonth(-1)} style={{ ...btnS(), background: store.color, color: "#fff" }}>← 前月</button>
        <input
          type="month"
          value={ym}
          onChange={e => setYm(e.target.value)}
          style={{ ...selS, width: "auto" }}
        />
        <button onClick={() => changeMonth(1)} style={{ ...btnS(), background: store.color, color: "#fff" }}>翌月 →</button>
      </div>

      {publicationStatus === "loading" && (
        <div role="status" style={{ marginBottom: 12, padding: "11px 14px", borderRadius: 9, background: "#F4F6F8", color: "#445", fontWeight: 700 }}>
          公開状態を確認しています…
        </div>
      )}

      {publicationStatus === "error" && (
        <div role="alert" style={{ marginBottom: 12, padding: "11px 14px", borderRadius: 9, background: "#FFF1F0", border: "1px solid #FFCCC7", color: "#A61D24", fontWeight: 700 }}>
          <div>公開状態を取得できません</div>
          <div style={{ marginTop: 4, fontSize: 13 }}>安全のため編集操作は利用できません。ページを再読み込みしてください。</div>
          {publicationMessage && <div style={{ marginTop: 4, fontSize: 12 }}>{publicationMessage}</div>}
        </div>
      )}

      {isUnpublishedViewer && (
        <div
          role="status"
          style={{
            background: "#FFF8E1",
            border: "1px solid #FFE082",
            borderRadius: 9,
            padding: "11px 14px",
            marginBottom: 12,
            fontSize: 14,
            fontWeight: 700,
            color: "#754F00",
          }}
        >
          この月のシフトはまだ公開されていません
        </div>
      )}

      {["draft", "published"].includes(publicationStatus) && !isUnpublishedViewer && <section
        aria-label="現在の公開状態"
        data-publication-status={publicationStatus}
        style={{
          background: publicationStatus === "published" ? "#EAF7EE" : "#FFF7E0",
          border: `2px solid ${publicationStatus === "published" ? "#52A66B" : "#E0A928"}`,
          borderRadius: 10,
          padding: publicationStatus === "published" ? "10px 12px" : "12px 14px",
          marginBottom: 10,
          color: publicationStatus === "published" ? "#1E6B37" : "#754F00",
        }}
      >
        <div aria-label="公開状態概要" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 700 }}>現在の公開状態</span>
          <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>
            {publicationStatus === "published" ? "✓" : "⚠"}
          </span>
          <div style={{ fontSize: publicationStatus === "published" ? 17 : 18, fontWeight: 900, lineHeight: 1.25 }}>
            {publicationStatus === "published" ? "公開済み" : "未公開（下書き）"}
          </div>
        </div>
        {publicationStatus === "draft" && (
          <div style={{ marginTop: 6, fontSize: 14, fontWeight: 700 }}>
            スタッフにはまだ表示されていません
          </div>
        )}
        {publicationStatus === "published" && (publicationDetails.publishedAt || publicationDetails.publishedByName) && (
          <div style={{ marginTop: 5, fontSize: 11, color: "#355E40", lineHeight: 1.45 }}>
            {publicationDetails.publishedAt && (
              <div>公開日時：{formatShiftPublishedAt(publicationDetails.publishedAt)}</div>
            )}
            {publicationDetails.publishedByName && <div>公開者：{publicationDetails.publishedByName}</div>}
          </div>
        )}
        {canManageShift && publicationStatus === "published" && (
          <div style={{ marginTop: 7, fontSize: 13, fontWeight: 700 }}>
            現在は公開中のため編集できません。編集する場合は、先に下書きへ戻してください。
          </div>
        )}
      </section>}

      {canManageShift && ["draft", "published"].includes(publicationStatus) && (
        <div aria-label="公開操作" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          {publicationStatus === "draft" ? (
            <button
              type="button"
              onClick={() => setShowPublishConfirm(true)}
              disabled={publicationSaving || loading}
              style={{ ...btnS(shiftStore.accent, publicationSaving || loading), padding: "10px 16px", fontSize: 14 }}
            >
              スタッフにシフトを公開する
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowDraftConfirm(true)}
              disabled={publicationSaving || loading}
              style={{ ...btnS("#777", publicationSaving || loading), padding: "9px 14px" }}
            >
              編集のため下書きに戻す
            </button>
          )}
          {publicationMessage && <span role="status" style={{ fontSize: 13, fontWeight: 700 }}>{publicationMessage}</span>}
        </div>
      )}

      {!canManageShift && publicationMessage && (
        <div role="status" style={{ fontSize: 13, marginBottom: 12 }}>{publicationMessage}</div>
      )}

      {showPublishConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 1000,
          }}
          onClick={() => setShowPublishConfirm(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="shift-publish-confirm-title"
            onClick={event => event.stopPropagation()}
            style={{
              width: "min(460px, 100%)",
              background: "#fff",
              borderRadius: 14,
              padding: 22,
              boxShadow: "0 16px 44px rgba(0,0,0,0.25)",
            }}
          >
            <div id="shift-publish-confirm-title" style={{ fontSize: 19, fontWeight: 900, color: "#222", marginBottom: 10 }}>
              {formatShiftMonthLabel(ym)}のシフトを公開しますか？
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.7, color: "#444", marginBottom: 20 }}>
              公開するとスタッフのシフト画面に表示されます。
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <button type="button" onClick={() => setShowPublishConfirm(false)} style={{ ...btnS("#777"), background: "#fff", color: "#555", border: "1px solid #aaa" }}>
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPublishConfirm(false);
                  updatePublicationStatus("published");
                }}
                style={{ ...btnS(shiftStore.accent), padding: "10px 18px" }}
              >
                公開する
              </button>
            </div>
          </div>
        </div>
      )}

      {showDraftConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 1000,
          }}
          onClick={() => setShowDraftConfirm(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="shift-draft-confirm-title"
            onClick={event => event.stopPropagation()}
            style={{
              width: "min(500px, 100%)",
              background: "#fff",
              borderRadius: 14,
              padding: 22,
              boxShadow: "0 16px 44px rgba(0,0,0,0.25)",
            }}
          >
            <div id="shift-draft-confirm-title" style={{ fontSize: 19, fontWeight: 900, color: "#222", marginBottom: 10 }}>
              {formatShiftMonthLabel(ym)}のシフトを下書きに戻しますか？
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.7, color: "#444", marginBottom: 20 }}>
              公開中のシフトを下書きに戻します。スタッフからはこの月のシフトが見えなくなります。編集後は再度公開してください。
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <button type="button" onClick={() => setShowDraftConfirm(false)} style={{ ...btnS("#777"), background: "#fff", color: "#555", border: "1px solid #aaa" }}>
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDraftConfirm(false);
                  updatePublicationStatus("draft");
                }}
                style={{ ...btnS("#A15C00"), padding: "10px 18px" }}
              >
                下書きに戻す
              </button>
            </div>
          </div>
        </div>
      )}

      {publicationError && (
        <div role="alert" style={{
          background: "#FDECEC",
          border: "2px solid #C62828",
          borderRadius: 10,
          color: "#8E0000",
          padding: "12px 14px",
          marginBottom: 14,
          lineHeight: 1.6,
          position: "sticky",
          top: 8,
          zIndex: 20,
        }}>
          <div style={{ fontSize: 16, fontWeight: 900 }}>公開に失敗しました</div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{publicationError.message}</div>
          {publicationError.targets.length > 0 && (
            <ul style={{ margin: "8px 0 0", paddingLeft: 22, fontSize: 13 }}>
              {publicationError.targets.map((target, index) => (
                <li key={`${target.shift_id || "unknown"}-${index}`}>
                  ID: {target.shift_id ?? "不明"}
                  {target.shift_date ? ` ／ ${target.shift_date}` : ""}
                  {target.staff_name ? ` ／ ${target.staff_name}` : ""}
                  {target.reason ? ` ／ ${target.reason}` : ""}
                </li>
              ))}
            </ul>
          )}
          <div style={{ marginTop: 8, fontSize: 13, fontWeight: 900 }}>
            現在も「下書き」です。内容を修正してから再度公開してください。
          </div>
        </div>
      )}

      {!canManageShift && publicationStatus === "published" && (
        <div style={{
          background: "#FFF8E1",
          border: "1px solid #FFE082",
          borderRadius: 8,
          padding: "8px 14px",
          marginBottom: 12,
          fontSize: 13,
          color: "#F57F17"
        }}>
          📋 公開済みシフトのみ閲覧できます
        </div>
      )}

      <div
        role="group"
        aria-label="シフト表示切替"
        style={{ display: "inline-flex", gap: 4, padding: 4, marginBottom: 14, borderRadius: 10, background: "#EEF1F3" }}
      >
        {[{ id: "calendar", label: "カレンダー" }, { id: "worktable", label: "勤務表" }].map(option => {
          const active = viewMode === option.id;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={active}
              onClick={() => {
                setViewMode(option.id);
                if (option.id === "worktable") {
                  setSelectedDate(null);
                  setShowOtherStaffPicker(false);
                }
              }}
              style={{
                ...btnS(active ? shiftStore.color : "#fff"),
                padding: "8px 14px",
                background: active ? shiftStore.color : "#fff",
                color: active ? "#fff" : "#444",
                border: active ? `1px solid ${shiftStore.color}` : "1px solid #D5DADD",
                boxShadow: active ? "0 1px 3px rgba(0,0,0,0.14)" : "none",
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {viewMode === "worktable" && ["draft", "published"].includes(publicationStatus) && !isUnpublishedViewer && (
        <ShiftWorktable
          ym={ym}
          shifts={shifts}
          users={users}
          shiftStoreId={shiftStoreId}
          color={shiftStore.color}
        />
      )}

      {viewMode === "worktable" && (!["draft", "published"].includes(publicationStatus) || isUnpublishedViewer) && (
        <div role="status" style={{ padding: "12px 14px", borderRadius: 9, background: "#F4F6F8", color: "#555" }}>
          {isUnpublishedViewer
            ? "勤務表はシフト公開後に表示されます"
            : "公開状態を確認できるまで勤務表は表示しません"}
        </div>
      )}

      {viewMode === "calendar" && !selectedDate && (
        <div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 6,
            marginBottom: 8,
            fontWeight: 700,
            color: store.color,
            textAlign: "center"
          }}>
            {["日", "月", "火", "水", "木", "金", "土"].map(d => <div key={d}>{d}</div>)}
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 6
          }}>
            {days.map((date, i) => {
              const count = date
                ? Object.values(shifts[date] || {}).filter(shift => shift?.shift_value).length
                : 0;

              return (
                <div
                  key={i}
                  onClick={() => {
                    if (!date || !canOpenShiftDays) return;
                    setSelectedDate(date);
                    setShowOtherStaffPicker(false);
                  }}
                  style={{
                    minHeight: 76,
                    border: "1px solid #ddd",
                    borderRadius: 10,
                    padding: 8,
background:
  !date
    ? "#fff"
    : date === selectedDate
      ? store.color
      : date === todayStr
        ? "#eaf8ea"
        : "#fff",
                    cursor: date && canOpenShiftDays ? "pointer" : "default"
                  }}
                >
                  {date && (
                    <>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>
                        {Number(date.slice(-2))}
                      </div>
                      {!isUnpublishedViewer && <div style={{ fontSize: 12, color: count ? store.color : "#999", fontWeight: count ? 700 : 400 }}>
                        {count
                          ? publicationStatus === "published" ? `${count}名・公開済` : `入力 ${count}名・未公開`
                          : publicationStatus === "published" ? "未入力・公開済" : "未入力・未公開"}
                      </div>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewMode === "calendar" && selectedDate && (
        <div>
          <button onClick={() => { setSelectedDate(null); setShowOtherStaffPicker(false); }} style={{ ...btnS(), background: store.color, color: "#fff", marginBottom: 12 }}>
  ← カレンダーへ戻る
</button>

          <div style={{ marginBottom: 16, padding: 12, border: `1px solid ${shiftStore.accent}`, borderRadius: 10, background: "#fafafa" }}>
            <div aria-label="日付移動" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <button type="button" onClick={() => showShiftDate(getShiftDateAfterDays(selectedDate, -1))} style={{ ...btnS(), padding: "9px 14px", background: "#fff", color: shiftStore.color, border: `1px solid ${shiftStore.accent}` }}>← 前日</button>
              <button type="button" onClick={() => showShiftDate(getTodayShiftDate())} style={{ ...btnS(), padding: "9px 14px", background: shiftStore.color, color: "#fff" }}>今日</button>
              <button type="button" onClick={() => showShiftDate(getShiftDateAfterDays(selectedDate, 1))} style={{ ...btnS(), padding: "9px 14px", background: "#fff", color: shiftStore.color, border: `1px solid ${shiftStore.accent}` }}>翌日 →</button>
            </div>
            <h3 style={{ margin: 0, color: shiftStore.color }}>
              {formatDate(selectedDate)} のシフト
            </h3>
          </div>

          {selectedShiftCount === 0 && (
            <div role="status" style={{ marginBottom: 14, padding: "11px 12px", borderRadius: 9, background: "#FFF8E1", color: "#6B5B35", fontWeight: 700 }}>
              この日のシフト登録はありません
            </div>
          )}

          {canEditShiftMonth && (
            <div style={{ marginBottom: 14 }}>
              <button
                type="button"
                aria-expanded={showOtherStaffPicker}
                onClick={() => setShowOtherStaffPicker(current => !current)}
                style={{ ...btnS(), background: "#fff", color: shiftStore.color, border: `1px solid ${shiftStore.accent}` }}
              >
                他施設スタッフを追加
              </button>
              {showOtherStaffPicker && (
                <div aria-label="他施設スタッフ候補" style={{ marginTop: 8, padding: 10, border: "1px solid #ddd", borderRadius: 10, background: "#fafafa", display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {otherStoreStaffCandidates.length === 0 ? (
                    <span style={{ fontSize: 13, color: "#666" }}>追加できる他施設スタッフはいません</span>
                  ) : otherStoreStaffCandidates.map(staff => {
                    const homeStore = STORES.find(item => item.id === getStaffStoreId(staff));
                    return (
                      <button
                        key={staff.id}
                        type="button"
                        aria-label={`${staff.name}を追加`}
                        onClick={() => addOtherStoreStaff(staff.id)}
                        style={{ ...btnS(), background: "#fff", color: "#333", border: "1px solid #ccc" }}
                      >
                        {staff.name}（所属: {homeStore?.name || getStaffStoreId(staff) || "未設定"}）
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {(canManageShift || selectedShiftCount > 0) && <div style={{ display: "grid", gap: 10 }}>
            {sortedStoreStaff.map(staff => {
              const dayRows = shifts[selectedDate] || {};
              const shift = dayRows[String(staff.id)] || dayRows[`legacy:${staff.name}`];
              const shiftDisplayValue = getShiftDisplayValue(shift);
              const homeStoreId = getStaffStoreId(staff);
              const homeStore = STORES.find(item => item.id === homeStoreId);
              const isOtherStoreStaff = homeStoreId !== String(shiftStoreId);
              return (
              <div
                key={staff.id || staff.name}
               style={{
  display: "grid",
  gridTemplateColumns: publicationStatus === "published"
    ? "minmax(100px, 130px) minmax(0, 1fr)"
    : "130px 1fr",
  gap: 10,
  alignItems: "center",
  background: staff.name === user.name ? "#f3fbf3" : "#fff",
  border: "1px solid #eee",
  borderRadius: 10,
  padding: 10
}}
              >
                <div>
                  <div style={{ fontWeight: 700, color: "#222" }}>{staff.name}</div>
                  {isOtherStoreStaff && (
                    <div style={{ marginTop: 3, fontSize: 11, color: "#666" }}>
                      所属: {homeStore?.name || homeStoreId || "未設定"}
                    </div>
                  )}
                </div>

                {publicationStatus === "published" ? (
                  <div
                    aria-label={`${staff.name}の確定シフト`}
                    style={{
                      minWidth: 0,
                      color: shiftDisplayValue ? "#222" : "#777",
                      fontSize: 15,
                      fontWeight: shiftDisplayValue ? 800 : 400,
                      lineHeight: 1.5,
                    }}
                  >
                    {shiftDisplayValue || "シフトなし"}
                  </div>
                ) : canManageShift ? (
                  usesStructuredShiftEditor(shiftStoreId) ? (
                    <KasamaShiftEditor
                      row={shift}
                      staff={staff}
                      shiftDate={selectedDate}
                      storeId={shiftStoreId}
                      storeName={shiftStore.name}
                      publicationStatus={publicationStatus}
                      disabled={!canEditShiftMonth}
                      onSave={handleStructuredSave}
                      onDelete={handleStructuredDelete}
                    />
                  ) : (
                  <input
                    value={shift?.shift_value || ""}
                    onChange={e => handleChange(staff, e.target.value)}
                    disabled={!canEditShiftMonth}
                    placeholder="例：9-17、早番、遅番、休"
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: 6,
                      padding: "8px 10px",
                      fontSize: 14,
                      ...(!canEditShiftMonth ? getShiftLockedFieldStyle(Boolean(shift?.shift_value)) : {}),
                    }}
                  />
                  )
                ) : (
                  <div style={{ color: "#555" }}>
                    {getShiftDisplayValue(shift) || "--"}
                  </div>
                )}
              </div>
            );})}
          </div>}
        </div>
      )}

      {loading && <p style={{ color: "#888", fontSize: 12 }}>読み込み中...</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ATTENDANCE TAB（サーバー保存）
// ═══════════════════════════════════════════════════════════
function correctionRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function correctionDateTimeInput(value) {
  return value ? String(value).replace(" ", "T").slice(0, 16) : "";
}

function AttendanceCorrectionModal({ store, availableStores = [store], users, defaultUserId = "", record = null, canResyncCurrentShift = false, onClose, onSaved }) {
  const isUpdate = Boolean(record);
  const [form, setForm] = useState({
    user_id: record?.user_id || defaultUserId || users[0]?.id || "",
    store_id: record?.store_id || store.id,
    clock_in_at: correctionDateTimeInput(record?.clock_in_at),
    clock_out_at: correctionDateTimeInput(record?.clock_out_at),
    shift_id: record ? (record.shift_id == null ? "none" : String(record.shift_id)) : "",
    reason_code: "missed_punch",
    reason: "",
    note: "",
    early_departure_confirmed: Boolean(record?.early_departure_confirmed_at),
    resync_from_current_shift: false,
  });
  const [requestId, setRequestId] = useState(correctionRequestId);
  const [shifts, setShifts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewWarnings, setPreviewWarnings] = useState([]);
  const [scheduleSource, setScheduleSource] = useState(null);
  const [currentShift, setCurrentShift] = useState(null);
  const [openedShiftVersion, setOpenedShiftVersion] = useState(null);
  const [resyncAvailable, setResyncAvailable] = useState(false);
  const [previewShiftVersion, setPreviewShiftVersion] = useState(null);
  const [editedDateTimes, setEditedDateTimes] = useState({ clock_in_at:false,clock_out_at:false });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(()=>{
    if (!isUpdate || !canResyncCurrentShift || !record?.id) return;
    let active=true;
    api(`/attendance_corrections.php?action=context&attendance_id=${encodeURIComponent(record.id)}`)
      .then(data=>{
        if(!active)return;
        setCurrentShift(data.current_shift || null);
        setOpenedShiftVersion(data.current_shift?.version || null);
        setResyncAvailable(Boolean(data.resync_available));
        if(Number(data.revision)!==Number(record.revision)){
          setMessage("画面表示後に勤怠が変更されました。画面を再読み込みしてください。");
          setResyncAvailable(false);
        }else if(!data.resync_available){
          setMessage("関連shiftが削除・非公開・別職員・別施設・別勤務日になっているため再同期できません。");
        }
      })
      .catch(error=>{if(active){setResyncAvailable(false);setMessage(error.message);}});
    return()=>{active=false;};
  },[isUpdate,canResyncCurrentShift,record?.id,record?.revision]);

  const change = (key, value) => {
    const invalidatesShift = ["user_id","store_id","clock_in_at","clock_out_at"].includes(key);
    if (["clock_in_at","clock_out_at"].includes(key)) {
      setEditedDateTimes(current => ({ ...current,[key]:true }));
    }
    setForm(current => ({ ...current, [key]:value,
      ...(invalidatesShift ? {shift_id:""} : {}),
    }));
    if (invalidatesShift) setShifts([]);
    setPreview(null);
    setPreviewWarnings([]);
    setScheduleSource(null);
    setPreviewShiftVersion(null);
    setRequestId(correctionRequestId());
    setMessage("");
  };
  const dateTimePayload = key => {
    if (isUpdate && !editedDateTimes[key]) return record?.[key] || "";
    return form[key].replace("T", " ");
  };
  const payload = action => ({
    action,
    store_id: form.store_id,
    user_id: form.user_id,
    attendance_id: record?.id,
    revision: record?.revision,
    clock_in_at: dateTimePayload("clock_in_at"),
    clock_out_at: dateTimePayload("clock_out_at"),
    shift_id: form.shift_id === "none" ? null : Number(form.shift_id),
    reason_code: form.reason_code,
    reason: form.reason,
    note: form.note,
    early_departure_confirmed: form.early_departure_confirmed,
    resync_from_current_shift: Boolean(
      isUpdate && canResyncCurrentShift && form.resync_from_current_shift
    ),
    ...(form.resync_from_current_shift
      ? { expected_shift_version:previewShiftVersion || openedShiftVersion }
      : {}),
    request_id: requestId,
  });
  const changeResync = checked => {
    setForm(current => ({ ...current,resync_from_current_shift:checked }));
    setPreview(null);
    setPreviewWarnings([]);
    setScheduleSource(null);
    setPreviewShiftVersion(null);
    setRequestId(correctionRequestId());
    setMessage("");
  };
  const loadShifts = async () => {
    if (!form.user_id || !form.clock_in_at || !form.clock_out_at) {
      setMessage("スタッフと実出勤・実退勤を入力してください。");
      return;
    }
    setBusy(true);
    try {
      const query = new URLSearchParams({
        action:"shift_candidates", store_id:form.store_id, user_id:form.user_id,
        clock_in_at:form.clock_in_at.replace("T", " "),
        clock_out_at:form.clock_out_at.replace("T", " "),
      });
      const data = await api(`/attendance_corrections.php?${query}`);
      setShifts(data.records || []);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  };
  const runPreview = async () => {
    if (form.shift_id === "") {
      setMessage("対象shiftまたはshiftなしを明示選択してください。");
      return;
    }
    if (!form.clock_out_at) {
      setMessage("確認済みの実退勤を入力してください。");
      return;
    }
    if (!form.reason.trim()) {
      setMessage("補正理由を入力してください。");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const data = await api("/attendance_corrections.php", "POST", payload("preview"));
      setPreview(data.preview);
      setPreviewWarnings(data.warnings || []);
      setScheduleSource(data.schedule_source || null);
      setCurrentShift(data.current_shift || null);
      setPreviewShiftVersion(data.current_shift?.version || null);
    } catch (error) {
      const detail = error.payload?.details;
      setMessage(detail?.attendance_id
        ? `${error.message}（${detail.store_id || "施設不明"} ${detail.work_date || ""} ${detail.clock_in_at || ""}～${detail.clock_out_at || "未退勤"} / attendance ID: ${detail.attendance_id}）`
        : error.message);
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!preview) {
      setMessage("先にプレビューを実行してください。");
      return;
    }
    if (form.resync_from_current_shift && !previewShiftVersion) {
      setMessage("現在shiftを再取得して、もう一度プレビューしてください。");
      return;
    }
    const targetName=users.find(candidate=>String(candidate.id)===String(form.user_id))?.name||form.user_id;
    const targetStore=availableStores.find(candidate=>String(candidate.id)===String(form.store_id))?.name||form.store_id;
    const requestPayload=payload(isUpdate ? "update" : "create");
    if (!window.confirm(`${targetName} / ${targetStore}\n実績 ${requestPayload.clock_in_at}～${requestPayload.clock_out_at}\n${form.shift_id==="none"?"shiftなし":`shift #${form.shift_id}`}\n${form.resync_from_current_shift?"現在shiftから予定・休憩・給与時間を再計算します。\n":""}理由: ${form.reason}\n\nこの内容で監査付き保存します。`)) return;
    setBusy(true);
    try {
      await api("/attendance_corrections.php", isUpdate ? "PUT" : "POST", requestPayload);
      await onSaved();
      onClose();
    } catch (error) {
      const detail = error.payload?.details;
      const stale = ["revision","shift_version"].includes(detail?.conflict_type);
      if (stale) {
        setPreview(null);
        setPreviewWarnings([]);
        setPreviewShiftVersion(null);
        setResyncAvailable(false);
      }
      setMessage(stale
        ? "画面表示後に勤怠またはshiftが変更されました。古いプレビューを破棄しました。画面を再読み込みしてやり直してください。"
        : error.message);
    } finally {
      setBusy(false);
    }
  };
  const scheduled = preview?.scheduled_start_at && preview?.scheduled_end_at;
  const contextScheduleDiffers = Boolean(record && currentShift && (
    String(record.scheduled_start_at || "") !== String(currentShift.scheduled_start_at || "")
    || String(record.scheduled_end_at || "") !== String(currentShift.scheduled_end_at || "")
  ));
  return <div role="dialog" aria-modal="true" aria-label={isUpdate ? "勤怠を修正" : "過去勤怠を追加"} style={{ position:"fixed",inset:0,zIndex:3000,background:"rgba(0,0,0,.45)",display:"flex",alignItems:"center",justifyContent:"center",padding:12 }}>
    <div style={{ width:"min(720px,100%)",maxHeight:"92vh",overflowY:"auto",background:"#fff",borderRadius:14,padding:20,boxSizing:"border-box" }}>
      <div style={{ display:"flex",justifyContent:"space-between",gap:12,alignItems:"center",marginBottom:16 }}>
        <div><div style={{ fontSize:19,fontWeight:800 }}>{isUpdate ? "勤怠を修正" : "過去勤怠を追加"}</div>{isUpdate&&<div style={{fontSize:11,color:"#667085",marginTop:3}}>現在revision: {record.revision ?? 0}（競合時は再読込が必要です）</div>}</div>
        <button type="button" onClick={onClose} disabled={busy}>閉じる</button>
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12 }}>
        <label style={{fontSize:12,fontWeight:700}}>対象スタッフ
          <select value={form.user_id} disabled={isUpdate} onChange={e=>change("user_id",e.target.value)} style={{...selS,width:"100%",marginTop:5}}>
            {users.map(candidate=><option key={candidate.id} value={candidate.id}>{candidate.name}{isUserDisabled(candidate)?`（無効${candidate.canonical_user_name?`・正本: ${candidate.canonical_user_name}`:""}）`:""}</option>)}
          </select>
        </label>
        <label style={{fontSize:12,fontWeight:700}}>対象施設
          <select value={form.store_id} disabled={isUpdate} onChange={e=>change("store_id",e.target.value)} style={{...selS,width:"100%",marginTop:5}}>
            {availableStores.map(candidate=><option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}
          </select>
        </label>
        <label style={{fontSize:12,fontWeight:700}}>実出勤
          <input type="datetime-local" value={form.clock_in_at} onChange={e=>change("clock_in_at",e.target.value)} style={{...inpS,width:"100%",marginTop:5,boxSizing:"border-box"}}/>
        </label>
        <label style={{fontSize:12,fontWeight:700}}>実退勤
          <input type="datetime-local" value={form.clock_out_at} onChange={e=>change("clock_out_at",e.target.value)} style={{...inpS,width:"100%",marginTop:5,boxSizing:"border-box"}}/>
        </label>
      </div>
      <div style={{ display:"flex",gap:8,alignItems:"end",marginTop:12,flexWrap:"wrap" }}>
        <fieldset style={{fontSize:12,fontWeight:700,flex:"1 1 300px",border:"1px solid #ddd",borderRadius:8,padding:10}}><legend>対象shift（必ず選択）</legend>
          <label style={{display:"block",fontWeight:500,marginBottom:6}}><input type="radio" name="correction-shift" checked={form.shift_id==="none"} onChange={()=>change("shift_id","none")}/> shiftなし（要承認・給与未確定）</label>
          {shifts.map(shift=><label key={shift.id} style={{display:"block",fontWeight:500,marginBottom:6}}><input type="radio" name="correction-shift" checked={String(form.shift_id)===String(shift.id)} onChange={()=>change("shift_id",String(shift.id))}/> #{shift.id} {String(shift.scheduled_start_at).slice(0,16)}～{String(shift.scheduled_end_at).slice(0,16)} / {shift.preset_code}</label>)}
          {record?.shift_id && !shifts.some(shift=>Number(shift.id)===Number(record.shift_id))&&<label style={{display:"block",fontWeight:500}}><input type="radio" name="correction-shift" checked={String(form.shift_id)===String(record.shift_id)} onChange={()=>change("shift_id",String(record.shift_id))}/> 現在のshift #{record.shift_id}</label>}
        </fieldset>
        <button type="button" onClick={loadShifts} disabled={busy}>shift候補を取得</button>
      </div>
      {isUpdate&&canResyncCurrentShift&&record?.shift_id&&<label style={{display:"flex",gap:8,alignItems:"flex-start",marginTop:12,padding:12,border:"2px solid #b45309",borderRadius:8,background:"#fff7ed",fontSize:13,fontWeight:800}}>
        <input type="checkbox" disabled={!resyncAvailable} checked={form.resync_from_current_shift} onChange={event=>changeResync(event.target.checked)}/>
        <span>現在のシフト内容から予定時間・休憩・給与時間を再計算する<br/><small style={{fontWeight:500}}>通常修正では保存済みsnapshotを維持します。管理者が現在shiftへの再同期を明示した場合だけ有効です。</small></span>
      </label>}
      {isUpdate&&currentShift&&<div style={{marginTop:12,padding:12,border:`1px solid ${contextScheduleDiffers?"#d97706":"#bbd7c2"}`,borderRadius:8,background:contextScheduleDiffers?"#fffbeb":"#f0fdf4",fontSize:12,lineHeight:1.65}}>
        <div style={{fontWeight:800,color:contextScheduleDiffers?"#92400e":"#166534"}}>{contextScheduleDiffers?"保存済みsnapshotと現在shiftに差があります":"保存済みsnapshotと現在shiftは一致しています"}</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:8,marginTop:6}}>
          <div><b>保存済みattendance snapshot</b><br/>{record.scheduled_start_at || "なし"} ～ {record.scheduled_end_at || "なし"}<br/>勤務区分：{record.attendance_type || "不明"} ／ 休憩：{record.payroll_break_minutes ?? "未確定"}分</div>
          <div><b>現在の関連shift #{currentShift.id}</b><br/>{currentShift.scheduled_start_at} ～ {currentShift.scheduled_end_at}<br/>勤務区分：{currentShift.preset_code || "不明"} ／ 公開状態：{currentShift.publication_status || "確認中"}</div>
        </div>
      </div>}
      <div style={{ display:"grid",gridTemplateColumns:"minmax(160px,220px) 1fr",gap:12,marginTop:12 }}>
        <label style={{fontSize:12,fontWeight:700}}>理由区分
          <select value={form.reason_code} onChange={e=>change("reason_code",e.target.value)} style={{...selS,width:"100%",marginTop:5}}>
            <option value="missed_punch">打刻忘れ</option><option value="field_confirmed">現場確認</option><option value="incorrect_punch">誤打刻</option><option value="other">その他</option>
          </select>
        </label>
        <label style={{fontSize:12,fontWeight:700}}>補正理由
          <input value={form.reason} maxLength={500} onChange={e=>change("reason",e.target.value)} style={{...inpS,width:"100%",marginTop:5,boxSizing:"border-box"}}/>
        </label>
      </div>
      <label style={{display:"block",fontSize:12,fontWeight:700,marginTop:12}}>備考
        <textarea value={form.note} onChange={e=>change("note",e.target.value)} style={{...inpS,width:"100%",minHeight:64,marginTop:5,boxSizing:"border-box"}}/>
      </label>
      <label style={{display:"flex",gap:8,alignItems:"center",fontSize:12,fontWeight:700,marginTop:12}}><input type="checkbox" checked={form.early_departure_confirmed} onChange={e=>change("early_departure_confirmed",e.target.checked)}/>予定終了前の退勤を現場確認済みとして保存</label>
      <div style={{display:"flex",gap:8,marginTop:16,flexWrap:"wrap"}}>
        <button type="button" onClick={runPreview} disabled={busy} style={btnS(store.accent,busy)}>プレビュー</button>
        <button type="button" onClick={save} disabled={busy||!preview} style={btnS("#267a3e",busy||!preview)}>監査付きで保存</button>
      </div>
      {message&&<div role="alert" style={{marginTop:12,padding:10,background:"#fff4e8",color:"#9a3412",borderRadius:8,fontSize:13,fontWeight:700}}>{message}</div>}
      {preview&&<div style={{marginTop:16,padding:14,background:"#f8fafc",border:"1px solid #dbe3ea",borderRadius:10,fontSize:13,lineHeight:1.8}}>
        <div style={{fontWeight:800,marginBottom:4}}>保存後プレビュー</div>
        {scheduleSource&&<div>予定の計算元：{scheduleSource==="attendance_snapshot"?"勤怠作成時の保存済みsnapshot":scheduleSource==="current_shift"?"現在の公開shift":"shiftなし"}</div>}
        {previewWarnings.map((warning,index)=><div key={`${warning.code || "warning"}-${index}`} role="alert" style={{margin:"8px 0",padding:10,background:"#fff4e8",color:"#9a3412",borderRadius:8,fontWeight:700}}>
          <div>{warning.message}</div>
          {warning.snapshot&&<div>保存済み：{warning.snapshot.scheduled_start_at} ～ {warning.snapshot.scheduled_end_at}</div>}
          {warning.current_shift&&<div>現在shift：{warning.current_shift.scheduled_start_at} ～ {warning.current_shift.scheduled_end_at}</div>}
        </div>)}
        {currentShift&&<div style={{margin:"8px 0",padding:10,background:"#eef6ff",borderRadius:8}}>
          <b>現在の関連shift #{currentShift.id}</b>：{currentShift.scheduled_start_at} ～ {currentShift.scheduled_end_at}<br/>
          勤務区分：{currentShift.preset_code || "不明"} ／ 公開状態：{currentShift.publication_status || "確認中"} ／ 更新：{currentShift.shift_updated_at || "不明"}
        </div>}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:10,marginTop:10}}>
          {record&&<div style={{padding:10,background:"#fff",border:"1px solid #d1d5db",borderRadius:8}}>
            <b>変更前（attendance snapshot）</b><br/>
            実績：{record.clock_in_at || "未記録"} ～ {record.clock_out_at || "未退勤"}<br/>
            予定：{record.scheduled_start_at || "なし"} ～ {record.scheduled_end_at || "なし"}<br/>
            勤務区分：{record.attendance_type || "不明"} ／ 休憩：{record.payroll_break_minutes ?? "未確定"}分<br/>
            給与base：{record.payroll_base_start_at || "未確定"} ～ {record.payroll_base_end_at || "未確定"}<br/>
            給与対象：{calculatePayrollMinutes(record)===null?"未確定":`${calculatePayrollMinutes(record)}分`} ／ revision {record.revision ?? 0}<br/>
            残業候補：{record.overtime_candidate_minutes || 0}分（{record.overtime_status || "none"}）
          </div>}
          <div style={{padding:10,background:form.resync_from_current_shift?"#ecfdf5":"#fff",border:"1px solid #d1d5db",borderRadius:8}}>
            <b>変更後{form.resync_from_current_shift?"（現在shiftから再計算）":""}</b><br/>
            実績：{preview.clock_in_at} ～ {preview.clock_out_at}<br/>
            予定：{scheduled ? `${preview.scheduled_start_at} ～ ${preview.scheduled_end_at}` : "shiftなし"}<br/>
            勤務区分：{preview.attendance_type} ／ 休憩：{preview.payroll_break_minutes ?? "未確定"}分<br/>
            給与base：{preview.payroll_base_start_at || "未確定"} ～ {preview.payroll_base_end_at || "未確定"}<br/>
            給与対象：{calculatePayrollMinutes(preview)===null?"未確定":`${calculatePayrollMinutes(preview)}分`}<br/>
            早退：{preview.early_departure_confirmed_at?"確認済み":"なし"} ／ 残業候補：{preview.overtime_candidate_minutes}分（{preview.overtime_status}）<br/>
            予定外確認：{preview.unscheduled_review_status}
          </div>
        </div>
      </div>}
    </div>
  </div>;
}

export function AttendanceTab({ store, user, users = [], correctionStores = [store], capabilities = {}, locationSearch = window.location.search }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locating, setLocating] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [correctionRecord, setCorrectionRecord] = useState(null);

  const params = new URLSearchParams(locationSearch);
  const qrAction = params.get("attendance");
  const qrStore = params.get("store");
  const isQrMode = ["clock-in", "clock-out"].includes(qrAction);
  const qrStoreMatches = !qrStore || qrStore === store.id;

const today = (() => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
})();
  const targetUserId = user.id;
  // 前日から継続中の夜勤を、当日レコードより優先して認識する。
  const todayRec = findCurrentAttendance(records, targetUserId, today);
  const clockOutEnabled = Boolean(capabilities.can_punch_self) && canClockOutAttendance(todayRec);
  const clockInBlocked = !capabilities.can_punch_self || hasBlockingAttendance(records, targetUserId, today);
  const canReviewAttendance = Boolean(capabilities.can_approve_attendance);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api(`/attendance.php?store=${store.id}`);
      setRecords(d.records || []);
    } catch (e) {
      if (e.status !== 401) setStatusMsg(e.message);
    } finally {
      setLoading(false);
    }
  }, [store.id]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

const runWithLocation = async (action, methodName, actionOptions = {}) => {
  setLocating(true);
  setStatusMsg("現在地を確認しています…");

  try {
    const location = await getCurrentLocation();

    if (action === "in") {
      const d = await api("/attendance.php", "POST", {
        store_id: store.id,
        user_id: user.id,
        staff_name: user.name,
        action: "in",
        method: methodName,
        ...location
      });

      setRecords(p => [{
        id: d.id,
        user_id: user.id,
        staff_name: user.name,
        work_date: d.work_date || today,
        clock_in: d.clock_in || fmtTime(new Date()),
        clock_in_at: d.clock_in_at,
        clock_out: null,
        clock_out_at: null,
        shift_id: d.shift_id,
        scheduled_start_at: d.scheduled_start_at,
        scheduled_end_at: d.scheduled_end_at,
        attendance_type: d.attendance_type,
        payroll_base_start_at: d.payroll_base_start_at,
        payroll_base_end_at: d.payroll_base_end_at,
        payroll_break_minutes: d.payroll_break_minutes,
        unscheduled_review_status: d.unscheduled_review_status,
        clock_in_latitude: location.latitude,
        clock_in_longitude: location.longitude,
        clock_in_accuracy: location.accuracy,
        clock_in_method: methodName
      }, ...p]);

      setStatusMsg(`出勤を記録しました。GPS精度：約${Math.round(location.accuracy)}m`);
    } else {
      const d = await api("/attendance.php", "PUT", {
        store_id: store.id,
        user_id: user.id,
        staff_name: user.name,
        action: "out",
        method: methodName,
        attendance_id: todayRec?.id,
        early_departure_confirmed: !!actionOptions.earlyDepartureConfirmed,
        ...location
      });

      setRecords(current => applyClockOutToRecords(current, d, {
        clock_out: fmtTime(new Date()),
        clock_out_at: new Date().toISOString(),
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        method: methodName,
      }));

      setStatusMsg(`退勤を記録しました。GPS精度：約${Math.round(location.accuracy)}m`);
    }
  } catch (e) {
    if (e.status !== 401) {
      const isStaleOpenAttendance = e.payload?.error_code === "stale_open_attendance_requires_admin";
      const workDate = e.payload?.details?.work_date;
      const message = isStaleOpenAttendance
        ? `過去の未退勤記録${workDate ? `（勤務日：${workDate}）` : ""}が残っているため、この打刻は処理できません。管理者へ勤怠修正を依頼してください。`
        : e.message;
      setStatusMsg(message);
      alert(message);
    }
  } finally {
    setLocating(false);
  }
};

const clockIn = () => runWithLocation("in", isQrMode ? "qr_gps" : "gps");
const clockOut = () => {
  if (!todayRec) return;
  const isEarly = isEarlyDeparture(todayRec);
  if (isEarly && !window.confirm("予定より早い退勤です。早退として記録しますか？")) return;
  return runWithLocation("out", isQrMode ? "qr_gps" : "gps", {
    earlyDepartureConfirmed: !!isEarly,
  });
};
const loadOpenAttendance = async () => {
  try {
    const d = await api(`/attendance.php?store=${store.id}&open=1`);
    alert(
      (d.records || []).map(r =>
        `${r.work_date}　${r.staff_name}　出勤:${r.clock_in}`
      ).join('\n') || '未退勤はありません'
    );
  } catch(e) {
    alert(e.message);
  }
};

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const adminCloseAttendance = async record => {
  if (!window.confirm(`${record.staff_name}さんを現在時刻で退勤扱いにします。管理者処理として記録されます。よろしいですか？`)) return;

  try {
await api("/attendance.php", "PUT", {
  store_id: store.id,
  attendance_id: record.id,
  action: "admin_close"
});

    alert("退勤扱いにしました。");
    await loadRecords();
  } catch (e) {
    alert(e.message);
  }
};

const reviewOvertime = async (record, decision) => {
  let approvedMinutes = 0;
  if (decision === "approved") {
    const entered = window.prompt(
      `承認する残業分数を入力してください（候補 ${record.overtime_candidate_minutes}分）`,
      String(record.overtime_candidate_minutes || 0)
    );
    if (entered === null) return;
    approvedMinutes = Number(entered);
  }
  try {
    await api("/attendance.php", "PUT", {
      action: "review_overtime",
      store_id: store.id,
      attendance_id: record.id,
      decision,
      approved_minutes: approvedMinutes,
    });
    await loadRecords();
  } catch (e) {
    alert(e.message);
  }
};

const confirmUnscheduled = async record => {
  if (!record.clock_in_at || !record.clock_out_at) {
    alert("実出勤・実退勤が揃うまで時間確定できません。");
    return;
  }
  if (!window.confirm("実出勤・実退勤から給与対象時間を正式計算して確定します。よろしいですか？")) return;
  try {
    await api("/attendance.php", "PUT", {
      action: "confirm_unscheduled",
      store_id: store.id,
      attendance_id: record.id,
    });
    await loadRecords();
  } catch (e) {
    alert(e.message);
  }
};

const startDate = new Date();
startDate.setDate(startDate.getDate() - 6);
const startStr = toDateStr(startDate);

const baseRecords = capabilities.can_view_attendance
  ? records
  : records.filter(r =>
      String(r.user_id || "") === String(user.id)
    );

const show = baseRecords
  .filter(r => (r.work_date >= startStr && r.work_date <= today)
    || r.overtime_status === "pending"
    || r.unscheduled_review_status === "pending")
  .sort((a, b) => String(b.work_date).localeCompare(String(a.work_date)));

  if (isQrMode) {
    const actionLabel = qrAction === "clock-in" ? "出勤" : "退勤";
    const disabled =
      !qrStoreMatches ||
      locating ||
      (qrAction === "clock-in" ? clockInBlocked : !clockOutEnabled);

    return (
      <div style={{ maxWidth:520,margin:"0 auto" }}>
        <div style={{ background:store.color+"12",border:`2px solid ${store.accent}`,borderRadius:16,padding:22,textAlign:"center" }}>
          <div style={{ fontSize:44,marginBottom:8 }}>{qrAction === "clock-in" ? "🏢" : "🏠"}</div>
          <div style={{ fontSize:20,fontWeight:900,color:store.color }}>{actionLabel}QR打刻</div>
          <div style={{ fontSize:13,color:"#666",marginTop:6 }}>{store.name}</div>
          <div style={{ marginTop:16,background:"#fff",borderRadius:12,padding:14,border:"1px solid #eee" }}>
            <div style={{ fontSize:12,color:"#888" }}>ログイン中のスタッフ</div>
            <div style={{ fontSize:18,fontWeight:800,marginTop:4 }}>{user.name}</div>
          </div>

          {!qrStoreMatches && (
            <ErrBox msg="このQRコードの施設と、現在表示している施設が一致しません。正しい施設のQRコードを読み取ってください。"/>
          )}

          {todayRec && (
            <div style={{ marginTop:14,fontSize:13,color:"#555" }}>
              対象勤務（{todayRec.work_date}）：出勤 {todayRec.clock_in || "-"} ／ 退勤 {isProvisionalAutoClockout(todayRec) ? `仮自動退勤 ${todayRec.clock_out}` : (isFinalAutoClockout(todayRec) ? `自動退勤 ${todayRec.clock_out}` : (todayRec.clock_out || "未打刻"))}
            </div>
          )}

          <button
            onClick={qrAction === "clock-in" ? clockIn : clockOut}
            disabled={disabled}
            style={{
              ...btnS(qrAction === "clock-in" ? store.accent : "#555", disabled),
              width:"100%",
              padding:16,
              fontSize:18,
              marginTop:18
            }}
          >
            {locating ? "現在地を確認中…" : `${actionLabel}を記録する`}
          </button>

          <div style={{ fontSize:11,color:"#888",lineHeight:1.7,marginTop:12 }}>
            打刻時に位置情報を取得します。ブラウザの確認が表示された場合は「許可」を選択してください。
          </div>

          {statusMsg && (
            <div style={{ marginTop:14,fontSize:13,fontWeight:700,color:statusMsg.includes("記録しました")?"#267a3e":"#b45309" }}>
              {statusMsg}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {correctionRecord&&<AttendanceCorrectionModal store={store} availableStores={correctionStores} users={users.length?users:[user]} record={correctionRecord} canResyncCurrentShift={Boolean(capabilities.can_correct_attendance)} onClose={()=>setCorrectionRecord(null)} onSaved={loadRecords}/>}
      <div style={{ display:"flex",gap:12,alignItems:"center",marginBottom:20,flexWrap:"wrap" }}>
        <div style={{ padding:"8px 14px",background:"#f5f5f5",borderRadius:8,fontSize:14,fontWeight:700,color:"#555" }}>{user.name}</div>
        <button onClick={clockIn} disabled={clockInBlocked || locating} style={btnS(store.accent,clockInBlocked || locating)}>
          {locating ? "位置確認中…" : "出勤"}
        </button>
        <button onClick={clockOut} disabled={!clockOutEnabled || locating} style={btnS("#555",!clockOutEnabled || locating)}>
          {locating ? "位置確認中…" : "退勤"}
        </button>

{capabilities.can_view_attendance && (
  <button
    onClick={loadOpenAttendance}
    style={btnS(store.accent, false)}
  >
    未退勤一覧
  </button>
)}

      </div>
      <div style={{ background:"#f0f7ff",border:"1px solid #bfdbfe",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#1E40AF" }}>
        出勤・退勤ボタンを押すと、端末の位置情報を取得して記録します。
      </div>
      {statusMsg && (
        <div style={{ background:statusMsg.includes("記録しました")?"#eafaf1":"#fff8e1",border:`1px solid ${statusMsg.includes("記録しました")?"#52B788":"#f0c36d"}`,borderRadius:8,padding:"9px 14px",marginBottom:14,fontSize:13 }}>
          {statusMsg}
        </div>
      )}
      {todayRec && <div style={{ background:store.color+"15",border:`2px solid ${store.accent}`,borderRadius:10,padding:16,marginBottom:20 }}><div style={{ fontSize:13,color:"#666" }}>対象勤務 ({todayRec.work_date})</div><div style={{ fontSize:18,fontWeight:700,color:store.color }}>{todayRec.staff_name}　出勤:{todayRec.clock_in}　{isProvisionalAutoClockout(todayRec)?`仮自動退勤:${todayRec.clock_out}（本人退勤受付中）`:(isFinalAutoClockout(todayRec)?`自動退勤:${todayRec.clock_out}`:(todayRec.clock_out?`退勤:${todayRec.clock_out}`:"勤務中…"))}</div></div>}
      {loading ? <Loading/> : (
        <div style={{ width:"100%",maxWidth:"100%",minWidth:0,overflowX:"auto",WebkitOverflowScrolling:"touch" }}>
        <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
          <thead><tr>{["日付","スタッフ","実出勤","実退勤","予定勤務","給与対象","確認状態","管理"].map(h=><th key={h} style={thS(store.color)}>{h}</th>)}</tr></thead>
          <tbody>
            {show.map((r,i)=>{
              const payrollMins=calculatePayrollMinutes(r);
              const payrollText=payrollMins===null?"未確定":`${Math.floor(payrollMins/60)}h${payrollMins%60}m`;
              const scheduleText=r.scheduled_start_at&&r.scheduled_end_at
                ? `${String(r.scheduled_start_at).slice(11,16)}～${String(r.scheduled_end_at).slice(11,16)}`
                : "シフト外";
              const states=[];
              if(isProvisionalAutoClockout(r)) states.push("仮自動退勤・本人退勤受付中");
              if(r.early_departure_confirmed_at) states.push("早退確認済み");
              if(r.overtime_status==="pending") states.push(`残業候補${r.overtime_candidate_minutes}分`);
              if(r.overtime_status==="approved") states.push(`残業承認${r.approved_overtime_minutes}分`);
              if(r.overtime_status==="rejected") states.push("残業却下");
              if(r.unscheduled_review_status==="pending") states.push(r.clock_out_at?"シフト外・要確認":"シフト外・未退勤");
              if(r.unscheduled_review_status==="confirmed") states.push("シフト外確認済み");
              return(<tr key={r.id||i} style={{ background:i%2===0?"#fff":"#f9f9f9" }}>
                <td style={tdS}>{r.work_date}</td><td style={tdS}>{r.staff_name}</td>
                <td style={tdS}>{r.clock_in}</td><td style={tdS}>{isProvisionalAutoClockout(r)?<span style={{color:"#b45309"}}>仮 {r.clock_out}</span>:(isFinalAutoClockout(r)?`自動 ${r.clock_out}`:(r.clock_out||<span style={{color:"#e87040"}}>未退勤</span>))}</td>
                <td style={tdS}>{scheduleText}</td><td style={tdS}>{payrollText}<div style={{fontSize:10,color:"#777"}}>休憩{r.payroll_break_minutes??"-"}分</div></td>
                <td style={tdS}>{states.join(" / ")||"通常"}</td>
                <td style={tdS}><div style={{display:"flex",gap:4,justifyContent:"center",flexWrap:"wrap"}}>
                  {capabilities.can_correct_attendance&&!r.clock_out&&!r.clock_out_at&&!r.admin_closed&&<button onClick={()=>adminCloseAttendance(r)}>退勤扱い</button>}
                  {capabilities.can_correct_attendance&&!r.clock_out_at&&<button onClick={()=>setCorrectionRecord(r)}>勤怠を修正</button>}
                  {canReviewAttendance&&<>
                  {r.overtime_status==="pending"&&<><button onClick={()=>reviewOvertime(r,"approved")}>承認</button><button onClick={()=>reviewOvertime(r,"rejected")}>却下</button></>}
                  {r.attendance_type==="unscheduled"&&r.unscheduled_review_status!=="confirmed"&&<button onClick={()=>confirmUnscheduled(r)}>時間確定</button>}
                  </>}
                </div></td>
              </tr>);
            })}
            {show.length===0&&<tr><td colSpan={8} style={{ textAlign:"center",padding:24,color:"#aaa" }}>記録なし</td></tr>}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
export function PayrollCsvTab({ users }) {
  const today = new Date();
  const defaultEnd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const defaultStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [storeId, setStoreId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState("");

  const availableStaff = users.filter(candidate => {
    const candidateStore = candidate.store || candidate.store_id || candidate.storeId;
    return !storeId || String(candidateStore) === String(storeId);
  });

  useEffect(() => {
    const staffIsAvailable = users.some(candidate => {
      const candidateStore = candidate.store || candidate.store_id || candidate.storeId;
      return String(candidate.id) === String(staffId)
        && (!storeId || String(candidateStore) === String(storeId));
    });
    if (staffId && !staffIsAvailable) setStaffId("");
  }, [storeId, staffId, users]);

  const download = async () => {
    if (!startDate || !endDate) {
      setMessage("開始日と終了日を指定してください。");
      return;
    }
    if (startDate > endDate) {
      setMessage("終了日は開始日以降を指定してください。");
      return;
    }

    setDownloading(true);
    setMessage("");
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (storeId) params.set("store_id", storeId);
      if (staffId) params.set("staff_id", staffId);
      const response = await fetch(`${API_BASE}/payroll_csv.php?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "CSVの出力に失敗しました");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `FIVE_給与勤怠_${startDate}_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setMessage("CSVを出力しました。");
    } catch (error) {
      setMessage(error.message || "CSVの出力に失敗しました");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ maxWidth:760 }}>
      <div style={{ background:"#f8fafc",border:"1px solid #dbe3ea",borderRadius:14,padding:20 }}>
        <div style={{ fontSize:18,fontWeight:800,marginBottom:6 }}>給与計算用 勤怠CSV</div>
        <div style={{ fontSize:12,color:"#667085",lineHeight:1.7,marginBottom:20 }}>
          打刻時に勤怠へ保存した予定・実操作・給与対象snapshotを出力します。シフト表は再参照しません。
        </div>

        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(210px, 1fr))",gap:14 }}>
          <label style={{ fontSize:12,fontWeight:700,color:"#475467" }}>
            開始日
            <input type="date" value={startDate} onChange={event=>setStartDate(event.target.value)} style={{ ...inpS,width:"100%",marginTop:5,boxSizing:"border-box" }}/>
          </label>
          <label style={{ fontSize:12,fontWeight:700,color:"#475467" }}>
            終了日
            <input type="date" value={endDate} onChange={event=>setEndDate(event.target.value)} style={{ ...inpS,width:"100%",marginTop:5,boxSizing:"border-box" }}/>
          </label>
          <label style={{ fontSize:12,fontWeight:700,color:"#475467" }}>
            施設
            <select value={storeId} onChange={event=>setStoreId(event.target.value)} style={{ ...selS,width:"100%",marginTop:5 }}>
              <option value="">全施設</option>
              {STORES.map(candidate=><option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}
            </select>
          </label>
          <label style={{ fontSize:12,fontWeight:700,color:"#475467" }}>
            スタッフ
            <select value={staffId} onChange={event=>setStaffId(event.target.value)} style={{ ...selS,width:"100%",marginTop:5 }}>
              <option value="">全スタッフ</option>
              {availableStaff.map(candidate=><option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}
            </select>
          </label>
        </div>

        <button type="button" onClick={download} disabled={downloading} style={{ ...btnS("#1D6F42",downloading),marginTop:20,padding:"12px 22px",fontSize:14 }}>
          {downloading ? "CSV作成中…" : "CSVをダウンロード"}
        </button>
        {message && <div role="status" style={{ marginTop:12,fontSize:13,color:message.includes("出力しました")?"#267a3e":"#b42318",fontWeight:700 }}>{message}</div>}
      </div>
    </div>
  );
}

export function MonthlyAttendanceTab({ store, user, users = [], correctionStores = [store], capabilities = {} }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [correction, setCorrection] = useState(null);
  const [showCancelled, setShowCancelled] = useState(false);

  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const [ym, setYm] = useState(defaultMonth);
  const [staffId, setStaffId] = useState(user.id);
  const storeStaff = useMemo(() => {
    if (!capabilities.can_view_monthly_attendance) {
      return [{ id:user.id,name:user.name }];
    }
    const byId = new Map();
    for (const candidate of users) {
      byId.set(String(candidate.id),{ id:candidate.id,name:candidate.name });
    }
    for (const record of records) {
      const id = String(record.user_id || "");
      if (id) byId.set(id,{ id,name:record.staff_name || id });
    }
    return [...byId.values()];
  }, [capabilities.can_view_monthly_attendance, records, user.id, user.name, users]);

  useEffect(() => {
    if (storeStaff.length > 0 && !storeStaff.some(s => String(s.id) === String(staffId))) {
      setStaffId(storeStaff[0].id);
    }
  }, [storeStaff, staffId]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const cancelledQuery = capabilities.can_view_monthly_attendance && showCancelled
        ? "&include_cancelled=1"
        : "";
      const d = await api(`/attendance.php?store=${store.id}&ym=${ym}${cancelledQuery}`);
      setRecords(d.records || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [capabilities.can_view_monthly_attendance, showCancelled, store.id, ym]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const cancelAttendance = async record => {
    const payroll=calculatePayrollMinutes(record);
    const reason = window.prompt(`${record.work_date} ${record.staff_name}\n実績 ${record.clock_in_at || record.clock_in || "未記録"}～${record.clock_out_at || record.clock_out || "未退勤"}\n給与対象 ${payroll===null?"未確定":`${payroll}分`}\n\n取消理由を入力してください。取消後も監査履歴は残ります。`);
    if (!reason?.trim()) return;
    if (!window.confirm(`${record.work_date} ${record.staff_name}さんの勤怠を取消します。よろしいですか？`)) return;
    try {
      await api("/attendance_corrections.php", "PUT", {
        action:"cancel", attendance_id:record.id, revision:record.revision,
        reason_code:"other", reason:reason.trim(), note:"", request_id:correctionRequestId(),
      });
      await loadRecords();
    } catch (error) {
      alert(error.payload?.details?.conflict_type === "revision"
        ? "別の操作で更新されました。再読み込みしてやり直してください。"
        : error.message);
    }
  };

  const formatMinutes = (mins) => {
    if (mins === null || mins === undefined) return "-";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h${m}m`;
  };

  const monthRecords = records
    .filter(r => String(r.work_date || "").startsWith(ym))
    .filter(r => String(r.user_id || "") === String(staffId))
    .sort((a, b) => String(a.work_date).localeCompare(String(b.work_date)));
  const activeMonthRecords = monthRecords.filter(r => !r.cancelled_at);

  const totalMinutes = activeMonthRecords.reduce((sum, r) => {
    const mins = calculatePayrollMinutes(r);
    return sum + (mins || 0);
  }, 0);

  const workDays = activeMonthRecords.filter(r => r.clock_in).length;
  const missingOut = activeMonthRecords.filter(r => r.clock_in && !displayClockOut(r)).length;

  return (
    <div>
      {correction&&<AttendanceCorrectionModal store={store} availableStores={correctionStores} users={users.length?users:[user]} defaultUserId={staffId} record={correction.record} canResyncCurrentShift={Boolean(capabilities.can_correct_attendance)} onClose={()=>setCorrection(null)} onSaved={loadRecords}/>}
      <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:20, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontSize:12, color:"#666", marginBottom:4 }}>対象月</div>
          <input
            type="month"
            value={ym}
            onChange={e => setYm(e.target.value)}
            style={{ padding:"10px 12px", border:"1px solid #ddd", borderRadius:8 }}
          />
        </div>

        <div>
          <div style={{ fontSize:12, color:"#666", marginBottom:4 }}>スタッフ</div>
          <select
            value={staffId}
            onChange={e => setStaffId(e.target.value)}
            style={{ padding:"10px 12px", border:"1px solid #ddd", borderRadius:8 }}
          >
            {storeStaff.map(s => (
              <option key={s.id || s.name} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        {capabilities.can_correct_attendance&&<button type="button" onClick={()=>setCorrection({record:null})} style={btnS(store.accent,false)}>過去勤怠を追加</button>}
        {capabilities.can_view_monthly_attendance&&<label style={{display:"flex",gap:6,alignItems:"center",fontSize:12,fontWeight:700}}><input type="checkbox" checked={showCancelled} onChange={event=>setShowCancelled(event.target.checked)}/>取消済みを表示</label>}
      </div>

      <div style={{
        display:"grid",
        gridTemplateColumns:"repeat(3, 1fr)",
        gap:12,
        marginBottom:20
      }}>
        <div style={{ background:"#f0f7f3", border:"1px solid #b7e0c2", borderRadius:10, padding:14 }}>
          <div style={{ fontSize:12, color:"#666" }}>出勤日数</div>
          <div style={{ fontSize:22, fontWeight:700 }}>{workDays}日</div>
        </div>

        <div style={{ background:"#f0f7f3", border:"1px solid #b7e0c2", borderRadius:10, padding:14 }}>
          <div style={{ fontSize:12, color:"#666" }}>勤務時間合計</div>
          <div style={{ fontSize:22, fontWeight:700 }}>{formatMinutes(totalMinutes)}</div>
        </div>

        <div style={{
          background: missingOut ? "#fff4e8" : "#f0f7f3",
          border:"1px solid #e0c2a0",
          borderRadius:10,
          padding:14
        }}>
          <div style={{ fontSize:12, color:"#666" }}>未退勤</div>
          <div style={{
            fontSize:22,
            fontWeight:700,
            color: missingOut ? "#d66b00" : "#267a3e"
          }}>
            {missingOut}件
          </div>
        </div>
      </div>

      {loading ? (
        <p style={{ color:"#888", fontSize:13 }}>読み込み中...</p>
      ) : (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr>
                {["日付", "スタッフ", "実出勤", "実退勤", "給与対象時間", "状態", "管理"].map(h => (
                  <th key={h} style={{
                    background: store.color,
                    color:"#fff",
                    padding:10,
                    textAlign:"center"
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {monthRecords.map((r, i) => {
                const mins = calculatePayrollMinutes(r);
                const shownClockOut = displayClockOut(r);
                const status = r.cancelled_at
                  ? "取消済み"
                  : r.clock_in && shownClockOut
                  ? "完了"
                  : isProvisionalAutoClockout(r)
                    ? "仮自動退勤"
                  : r.clock_in
                    ? "未退勤"
                    : "未出勤";

                return (
                  <tr key={r.id || i} style={{ borderBottom:"1px solid #eee",opacity:r.cancelled_at?0.65:1 }}>
                    <td style={{ padding:9, textAlign:"center" }}>{r.work_date}</td>
                    <td style={{ padding:9, textAlign:"center" }}>{r.staff_name}</td>
                    <td style={{ padding:9, textAlign:"center" }}>{r.clock_in || "-"}</td>
                    <td style={{
                      padding:9,
                      textAlign:"center",
                      color: !shownClockOut && r.clock_in ? "#d66b00" : "#111"
                    }}>
                      {isProvisionalAutoClockout(r) ? `仮 ${r.clock_out}` : (isFinalAutoClockout(r) ? `自動 ${r.clock_out}` : (shownClockOut || "-"))}
                    </td>
                    <td style={{ padding:9, textAlign:"center" }}>{formatMinutes(mins)}</td>
                    <td style={{
                      padding:9,
                      textAlign:"center",
                      color: status === "取消済み" ? "#667085" : status === "未退勤" || status === "仮自動退勤" ? "#d66b00" : "#267a3e",
                      fontWeight:700
                    }}>
                      {status}
                    </td>
                    <td style={{padding:9,textAlign:"center"}}><div style={{display:"flex",gap:5,justifyContent:"center",flexWrap:"wrap"}}>
                      {!r.cancelled_at&&capabilities.can_correct_attendance&&<button type="button" onClick={()=>setCorrection({record:r})}>勤怠を修正</button>}
                      {!r.cancelled_at&&capabilities.can_cancel_attendance&&<button type="button" onClick={()=>cancelAttendance(r)}>勤怠を取消</button>}
                    </div></td>
                  </tr>
                );
              })}

              {monthRecords.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ textAlign:"center", padding:24, color:"#aaa" }}>
                    この月の出退勤データはありません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// HANDOVER TAB（サーバー保存）
// ═══════════════════════════════════════════════════════════
export function ArchiveModeToggle({ mode, onChange, currentLabel, pastLabel, store }) {
  return (
    <div role="group" aria-label="表示期間" style={{ display:"inline-flex",gap:3,padding:3,marginBottom:16,background:"#f1f1f1",borderRadius:10 }}>
      {[["current",currentLabel],["past",pastLabel]].map(([value,label])=>{
        const active=mode===value;
        return <button key={value} type="button" aria-pressed={active} onClick={()=>onChange(value)} style={{ border:"none",borderRadius:8,padding:"7px 12px",background:active?store.color:"transparent",color:active?"#fff":"#777",fontSize:12,fontWeight:active?700:500,cursor:"pointer",fontFamily:"inherit" }}>{label}</button>;
      })}
    </div>
  );
}

function ReceiptStatus({ item, kind, user, users, store, allowConfirm=false, onConfirm }) {
  const [expanded,setExpanded]=useState(false);
  const [confirming,setConfirming]=useState(false);
  const status=getReceiptStatus(item,users,kind,user.id);
  const canViewReadReceipts=item?.can_view_read_receipts===true;

  const confirm = async event => {
    event?.stopPropagation();
    if(!allowConfirm||!status.isTarget||status.isRead||confirming) return;
    setConfirming(true);
    try { await onConfirm(item.id); } finally { setConfirming(false); }
  };

  if(canViewReadReceipts){
    return (
      <div style={{ marginTop:10,padding:"10px 12px",background:"#f8f8f8",borderRadius:8,fontSize:12,color:"#666",lineHeight:1.7,overflowWrap:"anywhere" }} onClick={event=>event.stopPropagation()}>
        <div style={{ fontWeight:700,color:"#555" }}>確認済み {status.readRecipients.length}名 ／ 未確認 {status.unreadRecipients.length}名</div>
        <button type="button" aria-expanded={expanded} onClick={()=>setExpanded(value=>!value)} style={{ marginTop:6,padding:0,border:"none",background:"none",color:store.accent,fontSize:12,fontWeight:700,cursor:"pointer" }}>{expanded?"確認状況を閉じる":"確認状況を見る"}</button>
        {expanded&&<div style={{ marginTop:8,paddingTop:8,borderTop:"1px solid #e5e5e5" }}>
          <div>確認済み {status.readRecipients.length}名：{status.readRecipients.length?status.readRecipients.map(recipient=>recipient.name).join("、"):"なし"}</div>
          <div>未確認 {status.unreadRecipients.length}名：{status.unreadRecipients.length?status.unreadRecipients.map(recipient=>recipient.name).join("、"):"なし"}</div>
        </div>}
        {allowConfirm&&status.isTarget&&<div style={{ marginTop:10 }}>
          {status.isRead
            ?<span style={{ color:"#777",fontWeight:700 }}>✓ 確認済み</span>
            :<button type="button" disabled={confirming} onClick={confirm} style={{ ...btnS(store.accent,confirming),padding:"8px 12px",fontSize:12 }}>{confirming?"確認中...":"内容を確認しました"}</button>}
        </div>}
      </div>
    );
  }

  if(!status.isTarget) return null;
  return (
    <div style={{ marginTop:10,fontSize:12,fontWeight:700,color:status.isRead?"#777":"#e74c3c" }} onClick={event=>event.stopPropagation()}>
      {status.isRead
        ?"✓ 確認済み"
        :(allowConfirm
          ?<button type="button" disabled={confirming} onClick={confirm} style={{ ...btnS(store.accent,confirming),padding:"9px 13px",fontSize:12 }}>{confirming?"確認中...":"内容を確認しました"}</button>
          :"未確認")}
    </div>
  );
}

function ReceiptUnreadToolbar({ count, onlyUnread, onChange, store }) {
  return (
    <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
      {count>0&&<span style={{ background:"#e74c3c",color:"#fff",borderRadius:20,padding:"3px 10px",fontSize:12,fontWeight:700 }}>未確認 {count}件</span>}
      {(count>0||onlyUnread)&&<button type="button" aria-pressed={onlyUnread} onClick={()=>onChange(!onlyUnread)} style={{ border:`1px solid ${store.accent}`,borderRadius:7,padding:"5px 9px",background:onlyUnread?store.accent:"#fff",color:onlyUnread?"#fff":store.accent,fontSize:12,fontWeight:700,cursor:"pointer" }}>{onlyUnread?"すべて表示":"未確認のみ"}</button>}
    </div>
  );
}

const HANDOVER_ATTACHMENT_ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf";
const HANDOVER_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

function validateHandoverFiles(files, currentCount = 0) {
  if (currentCount + files.length > 5) {
    return "添付ファイルは画像とPDFを合わせて5件までです";
  }
  const allowedExtensions = /\.(jpe?g|png|webp|pdf)$/i;
  const allowedMimes = ["image/jpeg","image/png","image/webp","application/pdf"];
  for (const file of files) {
    const extension = file.name.split(".").pop()?.toLowerCase();
    const mimeExtensions = { "image/jpeg":["jpg","jpeg"],"image/png":["png"],"image/webp":["webp"],"application/pdf":["pdf"] };
    if (!allowedExtensions.test(file.name) || (file.type && (!allowedMimes.includes(file.type) || !mimeExtensions[file.type]?.includes(extension)))) {
      return "JPEG、PNG、WEBP、PDFのみ添付できます";
    }
    if (file.size > HANDOVER_ATTACHMENT_MAX_BYTES) {
      return `${file.name} は10MBを超えています`;
    }
  }
  return "";
}

function HandoverAttachmentGallery({ note, user, onChange, store }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const attachments = note.attachments || [];
  const canManage = note.can_manage_attachments === true
    || ["president","manager"].includes(user.role)
    || !!user.perms?.isAdmin
    || (String(note.author_id) === String(user.id)
      && String(note.store_id) === String(user.store_id||user.store));

  const addFiles = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const error = validateHandoverFiles(files, attachments.length);
    if (error) {
      alert(error);
      return;
    }
    setUploading(true);
    try {
      const data = await uploadHandoverAttachments(note.id, files);
      onChange(data.attachments || []);
    } catch (e) {
      alert(e.message || "添付ファイルの追加に失敗しました");
    }
    setUploading(false);
  };

  const remove = async attachment => {
    if (!window.confirm(`${attachment.original_filename} を削除しますか？`)) return;
    try {
      await api(`/handover_attachments.php?id=${attachment.id}`, "DELETE");
      onChange(attachments.filter(a=>a.id!==attachment.id));
    } catch (e) {
      alert(e.message || "添付ファイルの削除に失敗しました");
    }
  };

  if (!attachments.length && !canManage) return null;
  return (
    <div style={{ marginTop:10 }}>
      {attachments.length>0&&<div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8 }}>
        {attachments.map(attachment=>attachment.file_type==="image"?(
          <div key={attachment.id} style={{ position:"relative",minWidth:0 }}>
            <button type="button" onClick={()=>setPreview(attachment)} aria-label={`${attachment.original_filename}を拡大`}
              style={{ display:"block",width:"100%",height:96,padding:0,border:"1px solid #ddd",borderRadius:8,overflow:"hidden",background:"#f3f3f3",cursor:"pointer" }}>
              <img src={attachment.url} alt={attachment.original_filename} loading="lazy" style={{ width:"100%",height:"100%",objectFit:"cover",display:"block" }}/>
            </button>
            <div title={attachment.original_filename} style={{ fontSize:10,color:"#666",marginTop:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{attachment.original_filename}</div>
            {canManage&&<button type="button" onClick={()=>remove(attachment)} aria-label={`${attachment.original_filename}を削除`}
              style={{ position:"absolute",right:4,top:4,width:28,height:28,border:"none",borderRadius:"50%",background:"#000b",color:"#fff",cursor:"pointer",fontSize:15 }}>×</button>}
          </div>
        ):(
          <div key={attachment.id} style={{ position:"relative",minWidth:0,border:"1px solid #ddd",borderRadius:8,padding:"10px 8px",background:"#fff" }}>
            <a href={attachment.url} target="_blank" rel="noreferrer" style={{ display:"flex",alignItems:"center",gap:7,color:store.color,textDecoration:"none",minHeight:52 }}>
              <span style={{ fontSize:30 }}>📄</span>
              <span style={{ fontSize:11,fontWeight:700,overflowWrap:"anywhere" }}>{attachment.original_filename}</span>
            </a>
            {canManage&&<button type="button" onClick={()=>remove(attachment)} aria-label={`${attachment.original_filename}を削除`}
              style={{ position:"absolute",right:4,top:4,width:28,height:28,border:"none",borderRadius:"50%",background:"#000b",color:"#fff",cursor:"pointer",fontSize:15 }}>×</button>}
          </div>
        ))}
      </div>}
      {canManage&&attachments.length<5&&<div style={{ marginTop:8 }}>
        <input ref={inputRef} type="file" multiple accept={HANDOVER_ATTACHMENT_ACCEPT} onChange={addFiles} style={{ display:"none" }}/>
        <button type="button" disabled={uploading} onClick={()=>inputRef.current?.click()}
          style={{ fontSize:12,color:store.accent,background:"#fff",border:`1px solid ${store.accent}`,borderRadius:7,padding:"7px 10px",cursor:uploading?"default":"pointer",minHeight:36 }}>
          {uploading?"追加中...":"＋ 画像・PDFを追加"}（{attachments.length} / 5）
        </button>
      </div>}
      {preview&&<div role="dialog" aria-modal="true" aria-label="画像プレビュー" onClick={()=>setPreview(null)}
        style={{ position:"fixed",inset:0,zIndex:9999,background:"#000d",display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}>
        <button type="button" onClick={()=>setPreview(null)} aria-label="閉じる"
          style={{ position:"fixed",right:16,top:16,width:44,height:44,border:"1px solid #fff8",borderRadius:"50%",background:"#0008",color:"#fff",fontSize:26,cursor:"pointer" }}>×</button>
        <img src={preview.url} alt={preview.original_filename} onClick={e=>e.stopPropagation()}
          style={{ maxWidth:"100%",maxHeight:"90vh",objectFit:"contain",borderRadius:6 }}/>
      </div>}
    </div>
  );
}

const canManagePostRecord = (post,user) => post?.can_manage === true
  || ["president","manager"].includes(user?.role)
  || !!user?.perms?.isAdmin
  || (String(post?.author_id) === String(user?.id)
    && String(post?.store_id) === String(user?.store_id||user?.store));

const postWasEdited = post => post?.is_edited === true || !!post?.updated_at;

function EditedBadge() {
  return <span style={{ fontSize:10,color:"#777",background:"#eee",borderRadius:10,padding:"2px 7px",fontWeight:700 }}>編集済み</span>;
}

function PostManageButtons({ onEdit, onDelete, busy, store }) {
  return <div style={{ display:"flex",gap:7,flexWrap:"wrap",marginTop:12 }}>
    <button type="button" onClick={onEdit} disabled={busy} style={{ fontSize:12,color:store.accent,background:"#fff",border:`1px solid ${store.accent}`,borderRadius:6,padding:"6px 10px",cursor:busy?"default":"pointer" }}>編集</button>
    <button type="button" onClick={onDelete} disabled={busy} style={{ fontSize:12,color:"#b42318",background:"#fff",border:"1px solid #d92d20",borderRadius:6,padding:"6px 10px",cursor:busy?"default":"pointer" }}>削除</button>
  </div>;
}

export function HandoverTab({ store, user, users }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiveMode, setArchiveMode] = useState("current");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [posting, setPosting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState("");
  const [managingId, setManagingId] = useState(null);
  const fileInputRef = useRef(null);
  const loadSequence = useRef(0);

  const loadNotes = useCallback(async mode => {
    const sequence=++loadSequence.current;
    const requestedStoreId=store.id;
    setLoading(true);
    setNotes([]);
    try {
      const d=await api(`/handovers.php?store_id=${encodeURIComponent(requestedStoreId)}&archive=${mode}`);
      const rows=filterHandoversByStore(d.handovers || d.notes || [],requestedStoreId);
      if(sequence===loadSequence.current) setNotes(rows);
    } catch {
      if(sequence===loadSequence.current) setNotes([]);
    }
    if(sequence===loadSequence.current) setLoading(false);
  },[store.id]);

  useEffect(()=>{
    setArchiveMode("current");
    setOnlyUnread(false);
    setViewId(null);
    setEditingId(null);
    loadNotes("current");
  },[loadNotes]);

  const changeArchiveMode = mode => {
    setArchiveMode(mode);
    setOnlyUnread(false);
    setViewId(null);
    loadNotes(mode);
  };

const add = async () => {
  if (!newNote.trim()) return;

  setPosting(true);

  try {
    const d = await api("/handovers.php", "POST", {
      store_id: store.id,
      user_id: user.id,
      author_name: user.name,
      author_role: user.role,
      body: newNote.trim()
    });

    let uploadError = "";
    if (selectedFiles.length) {
      try {
        await uploadHandoverAttachments(d.id, selectedFiles.map(item=>item.file));
      } catch (e) {
        uploadError = e.message || "添付ファイルのアップロードに失敗しました";
      }
    }

    setNewNote("");
    selectedFiles.forEach(item=>item.preview&&URL.revokeObjectURL(item.preview));
    setSelectedFiles([]);
    setArchiveMode("current");
    await loadNotes("current");
    if (uploadError) alert(`申し送りは投稿されましたが、添付に失敗しました。\n${uploadError}`);
  } catch (e) {
    alert(e.message || "投稿に失敗しました");
  }

  setPosting(false);
};

  const selectFiles = event => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const error = validateHandoverFiles(files, selectedFiles.length);
    if (error) {
      alert(error);
      return;
    }
    setSelectedFiles(current=>[
      ...current,
      ...files.map(file=>({ file, preview:file.type.startsWith("image/")?URL.createObjectURL(file):null }))
    ]);
  };

  const removeSelectedFile = index => {
    setSelectedFiles(current=>{
      const target = current[index];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return current.filter((_,i)=>i!==index);
    });
  };

  const markRead = async id => {
    try {
      const data=await api("/handovers.php", "PUT", { id });
      setNotes(p=>p.map(n=>n.id===id?{
        ...n,
        ...data,
        ...(Array.isArray(data.reads)?{reads:data.reads}:{})
      }:n));
      return true;
    } catch(e) {
      alert(e.message||"確認状況を更新できませんでした");
      return false;
    }
  };

  const beginEdit = note => {
    setEditingId(note.id);
    setEditingBody(note.body);
  };

  const saveEdit = async note => {
    const body=editingBody.trim();
    if(!body) return;
    setManagingId(note.id);
    try {
      const data=await api("/handovers.php","PUT",{ action:"edit",id:note.id,body });
      setNotes(current=>current.map(item=>String(item.id)===String(note.id)
        ?{...item,...data.handover,is_edited:!!data.handover?.updated_at}:item));
      setEditingId(null);
      setEditingBody("");
    } catch(e) {
      alert(e.message||"申し送りを編集できませんでした");
    }
    setManagingId(null);
  };

  const deletePost = async note => {
    if(!window.confirm("この投稿を削除しますか？")) return;
    setManagingId(note.id);
    try {
      await api(`/handovers.php?id=${encodeURIComponent(note.id)}`,"DELETE");
      setNotes(current=>current.filter(item=>String(item.id)!==String(note.id)));
      setViewId(null);
      setEditingId(null);
    } catch(e) {
      alert(e.message||"申し送りを削除できませんでした");
    }
    setManagingId(null);
  };

  const viewedNote = notes.find(note=>note.id===viewId);
  if (viewedNote) {
    const receipt=getReceiptStatus(viewedNote,users,"handover",user.id);
    const isUnread=receipt.isTarget&&!receipt.isRead;
    return (
      <div>
        <button type="button" onClick={()=>setViewId(null)} style={{ marginBottom:16,background:"none",border:"none",color:store.accent,cursor:"pointer",fontSize:14,fontWeight:700 }}>← 一覧へ戻る</button>
        <article style={{ background:"#fff",border:`1px solid ${isUnread?store.accent:"#eee"}`,borderLeft:`4px solid ${isUnread?store.accent:"#ddd"}`,borderRadius:12,padding:"clamp(14px,4vw,24px)" }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:14,flexWrap:"wrap" }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}><span style={{ fontSize:18 }}>{ROLE_ICONS[viewedNote.author_role]}</span><span style={{ fontWeight:700,color:store.color,fontSize:15 }}>投稿者：{viewedNote.author_name}</span><span style={{ fontSize:11,background:ROLE_COLORS[viewedNote.author_role]+"20",color:ROLE_COLORS[viewedNote.author_role],borderRadius:10,padding:"2px 8px",fontWeight:700 }}>{ROLE_LABELS[viewedNote.author_role]}</span></div>
            <div style={{ display:"flex",alignItems:"center",gap:7,flexWrap:"wrap" }}><span style={{ fontSize:12,color:"#888" }}>{fmtDT(viewedNote.created_at)}</span>{postWasEdited(viewedNote)&&<EditedBadge/>}</div>
          </div>
          {String(editingId)===String(viewedNote.id)?<div>
            <textarea aria-label="申し送り内容を編集" value={editingBody} onChange={event=>setEditingBody(event.target.value)} rows={5} style={{ ...inpS,resize:"vertical" }}/>
            <div style={{ display:"flex",gap:8,marginTop:8 }}><button type="button" onClick={()=>saveEdit(viewedNote)} disabled={String(managingId)===String(viewedNote.id)||!editingBody.trim()} style={btnS(store.accent,String(managingId)===String(viewedNote.id))}>保存</button><button type="button" onClick={()=>{setEditingId(null);setEditingBody("");}} disabled={String(managingId)===String(viewedNote.id)} style={{ ...btnS("#777",String(managingId)===String(viewedNote.id)),background:"#fff",color:"#555",border:"1px solid #aaa" }}>キャンセル</button></div>
          </div>:<div style={{ fontSize:15,color:"#333",lineHeight:1.8,whiteSpace:"pre-wrap",overflowWrap:"anywhere" }}>{viewedNote.body}</div>}
          <HandoverAttachmentGallery note={viewedNote} user={user} store={store} onChange={attachments=>setNotes(current=>current.map(note=>note.id===viewedNote.id?{...note,attachments}:note))}/>
          <ReceiptStatus item={viewedNote} kind="handover" user={user} users={users} store={store} allowConfirm onConfirm={markRead}/>
          {canManagePostRecord(viewedNote,user)&&String(editingId)!==String(viewedNote.id)&&<PostManageButtons store={store} busy={String(managingId)===String(viewedNote.id)} onEdit={()=>beginEdit(viewedNote)} onDelete={()=>deletePost(viewedNote)}/>}
        </article>
      </div>
    );
  }

  const unreadCount=notes.filter(note=>{
    const receipt=getReceiptStatus(note,users,"handover",user.id);
    return receipt.isTarget&&!receipt.isRead;
  }).length;
  const visibleNotes=onlyUnread?notes.filter(note=>{
    const receipt=getReceiptStatus(note,users,"handover",user.id);
    return receipt.isTarget&&!receipt.isRead;
  }):notes;

  return (
    <div>
      <ArchiveModeToggle mode={archiveMode} onChange={changeArchiveMode} currentLabel="現在の申し送り" pastLabel="過去の申し送り" store={store}/>
      <div style={{ marginBottom:14 }}><ReceiptUnreadToolbar count={unreadCount} onlyUnread={onlyUnread} onChange={setOnlyUnread} store={store}/></div>
      {archiveMode==="current"&&<div style={{ display:"flex",flexDirection:"column",gap:8,marginBottom:16 }}>
        <div style={{ display:"flex",gap:8,alignItems:"center" }}><Avatar u={user} size={34}/><span style={{ fontWeight:700,fontSize:14,flex:1 }}>{user.name}</span><button onClick={add} disabled={posting} style={btnS(store.accent,posting)}>投稿</button></div>
        <textarea value={newNote} onChange={e=>setNewNote(e.target.value)} placeholder="申し送り事項を入力..." rows={3} style={{ ...inpS,resize:"vertical" }}/>
        <input ref={fileInputRef} type="file" multiple accept={HANDOVER_ATTACHMENT_ACCEPT} onChange={selectFiles} style={{ display:"none" }}/>
        <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
          <button type="button" onClick={()=>fileInputRef.current?.click()} disabled={posting||selectedFiles.length>=5}
            style={{ fontSize:13,color:store.accent,background:"#fff",border:`1px solid ${store.accent}`,borderRadius:8,padding:"8px 11px",minHeight:40,cursor:"pointer" }}>
            ＋ 画像・PDFを追加
          </button>
          <span style={{ fontSize:12,fontWeight:700,color:selectedFiles.length>=5?"#e74c3c":"#777" }}>{selectedFiles.length} / 5</span>
        </div>
        {selectedFiles.length>0&&<div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(105px,1fr))",gap:8 }}>
          {selectedFiles.map((item,index)=>(
            <div key={`${item.file.name}-${item.file.lastModified}-${index}`} style={{ position:"relative",border:"1px solid #ddd",borderRadius:8,padding:6,background:"#fff",minWidth:0 }}>
              {item.preview?<img src={item.preview} alt="" style={{ width:"100%",height:76,objectFit:"cover",borderRadius:5,display:"block" }}/>:<div style={{ height:76,display:"flex",alignItems:"center",justifyContent:"center",fontSize:32 }}>📄</div>}
              <div title={item.file.name} style={{ fontSize:10,marginTop:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{item.file.name}</div>
              <button type="button" onClick={()=>removeSelectedFile(index)} aria-label={`${item.file.name}を選択から削除`}
                style={{ position:"absolute",right:3,top:3,width:28,height:28,border:"none",borderRadius:"50%",background:"#000b",color:"#fff",fontSize:16,cursor:"pointer" }}>×</button>
            </div>
          ))}
        </div>}
      </div>}
      {loading?<Loading/>:<div style={{ display:"flex",flexDirection:"column",gap:10 }}>
        {visibleNotes.map(n=>{ const receipt=getReceiptStatus(n,users,"handover",user.id); const isUnread=receipt.isTarget&&!receipt.isRead; return(
          <div key={n.id} style={{ background:isUnread?"#fff":"#f9f9f9",border:`1px solid ${isUnread?store.accent:"#eee"}`,borderLeft:`4px solid ${isUnread?store.accent:"#ddd"}`,borderRadius:10,padding:"12px 16px" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6 }}>
              <div style={{ display:"flex",alignItems:"center",gap:8 }}><span style={{ fontSize:14 }}>{ROLE_ICONS[n.author_role]}</span><span style={{ fontWeight:700,color:store.color,fontSize:14 }}>投稿者：{n.author_name}</span><span style={{ fontSize:11,background:ROLE_COLORS[n.author_role]+"20",color:ROLE_COLORS[n.author_role],borderRadius:10,padding:"1px 7px",fontWeight:700 }}>{ROLE_LABELS[n.author_role]}</span></div>
              <div style={{ display:"flex",alignItems:"center",gap:7,flexWrap:"wrap" }}><span style={{ fontSize:12,color:"#aaa" }}>{fmtDT(n.created_at)}</span>{postWasEdited(n)&&<EditedBadge/>}</div>
            </div>
            <p style={{ margin:0,fontSize:14,color:"#333",lineHeight:1.7 }}>{n.body}</p>
            <HandoverAttachmentGallery note={n} user={user} store={store} onChange={attachments=>setNotes(p=>p.map(note=>note.id===n.id?{...note,attachments}:note))}/>
            <ReceiptStatus item={n} kind="handover" user={user} users={users} store={store}/>
            <div style={{ marginTop:10 }}><button type="button" onClick={()=>setViewId(n.id)} style={{ fontSize:12,color:store.accent,background:"none",border:`1px solid ${store.accent}`,borderRadius:6,padding:"5px 9px",cursor:"pointer" }}>詳細を見る</button></div>
          </div>
        ); })}
        {visibleNotes.length===0&&<div style={{ textAlign:"center",color:"#aaa",padding:40 }}>{onlyUnread?"未確認の申し送りなし":archiveMode==="past"?"過去の申し送りなし":"申し送りなし"}</div>}
      </div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// NOTICE TAB（サーバー保存）
// ═══════════════════════════════════════════════════════════
function NoticeAttachmentGallery({ notice, user, onChange, store }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const attachments = notice.attachments || [];
  const canManage = notice.can_manage_attachments === true
    || ["president","manager"].includes(user.role)
    || !!user.perms?.isAdmin
    || (String(notice.author_id) === String(user.id)
      && String(notice.store_id) === String(user.store_id||user.store));

  const addFiles = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const error = validateHandoverFiles(files, attachments.length);
    if (error) return alert(error);
    setUploading(true);
    try {
      const data = await uploadNoticeAttachments(notice.id, files);
      onChange(data.attachments || []);
    } catch (e) {
      alert(e.message || "添付ファイルの追加に失敗しました");
    }
    setUploading(false);
  };

  const remove = async attachment => {
    if (!window.confirm(`${attachment.original_filename} を削除しますか？`)) return;
    try {
      await api(`/notice_attachments.php?id=${attachment.id}`, "DELETE");
      onChange(attachments.filter(item=>item.id!==attachment.id));
    } catch (e) {
      alert(e.message || "添付ファイルの削除に失敗しました");
    }
  };

  if (!attachments.length && !canManage) return null;
  return (
    <div style={{ marginTop:10 }} onClick={event=>event.stopPropagation()}>
      {attachments.length>0&&<div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8 }}>
        {attachments.map(attachment=>attachment.file_type==="image"?(
          <div key={attachment.id} style={{ position:"relative",minWidth:0 }}>
            <button type="button" onClick={()=>setPreview(attachment)} aria-label={`${attachment.original_filename}を拡大`} style={{ display:"block",width:"100%",height:96,padding:0,border:"1px solid #ddd",borderRadius:8,overflow:"hidden",background:"#f3f3f3",cursor:"pointer" }}><img src={attachment.url} alt={attachment.original_filename} loading="lazy" style={{ width:"100%",height:"100%",objectFit:"cover",display:"block" }}/></button>
            <div title={attachment.original_filename} style={{ fontSize:10,color:"#666",marginTop:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{attachment.original_filename}</div>
            {canManage&&<button type="button" onClick={()=>remove(attachment)} aria-label={`${attachment.original_filename}を削除`} style={{ position:"absolute",right:4,top:4,width:28,height:28,border:"none",borderRadius:"50%",background:"#000b",color:"#fff",cursor:"pointer",fontSize:15 }}>×</button>}
          </div>
        ):(
          <div key={attachment.id} style={{ position:"relative",minWidth:0,border:"1px solid #ddd",borderRadius:8,padding:"10px 8px",background:"#fff" }}>
            <a href={attachment.url} target="_blank" rel="noreferrer" style={{ display:"flex",alignItems:"center",gap:7,color:store.color,textDecoration:"none",minHeight:52 }}><span style={{ fontSize:30 }}>📄</span><span style={{ fontSize:11,fontWeight:700,overflowWrap:"anywhere" }}>{attachment.original_filename}</span></a>
            {canManage&&<button type="button" onClick={()=>remove(attachment)} aria-label={`${attachment.original_filename}を削除`} style={{ position:"absolute",right:4,top:4,width:28,height:28,border:"none",borderRadius:"50%",background:"#000b",color:"#fff",cursor:"pointer",fontSize:15 }}>×</button>}
          </div>
        ))}
      </div>}
      {canManage&&attachments.length<5&&<div style={{ marginTop:8 }}><input ref={inputRef} type="file" multiple accept={HANDOVER_ATTACHMENT_ACCEPT} onChange={addFiles} style={{ display:"none" }}/><button type="button" disabled={uploading} onClick={()=>inputRef.current?.click()} style={{ fontSize:12,color:store.accent,background:"#fff",border:`1px solid ${store.accent}`,borderRadius:7,padding:"7px 10px",cursor:uploading?"default":"pointer",minHeight:36 }}>{uploading?"追加中...":"＋ 画像・PDFを追加"}（{attachments.length} / 5）</button></div>}
      {preview&&<div role="dialog" aria-modal="true" aria-label="連絡画像プレビュー" onClick={()=>setPreview(null)} style={{ position:"fixed",inset:0,zIndex:9999,background:"#000d",display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}><button type="button" onClick={()=>setPreview(null)} aria-label="閉じる" style={{ position:"fixed",right:16,top:16,width:44,height:44,border:"1px solid #fff8",borderRadius:"50%",background:"#0008",color:"#fff",fontSize:26,cursor:"pointer" }}>×</button><img src={preview.url} alt={preview.original_filename} onClick={event=>event.stopPropagation()} style={{ maxWidth:"100%",maxHeight:"90vh",objectFit:"contain",borderRadius:6 }}/></div>}
    </div>
  );
}

const isNoticePinned = notice => notice?.pinned === true || Number(notice?.pinned) === 1;
const sortNotices = notices => [...notices].sort((a,b)=>(isNoticePinned(b)?1:0)-(isNoticePinned(a)?1:0)||(new Date(b.created_at)-new Date(a.created_at)));

export function NoticeTab({ store, user, users }) {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiveMode, setArchiveMode] = useState("current");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ target:"store",category:"業務連絡",title:"",body:"",pinned:false });
  const [viewId, setViewId] = useState(null);
  const [posting, setPosting] = useState(false);
  const [pinningId, setPinningId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editingNoticeId, setEditingNoticeId] = useState(null);
  const [editingNotice, setEditingNotice] = useState({ title:"",body:"" });
  const [managingNoticeId, setManagingNoticeId] = useState(null);
  const fileInputRef = useRef(null);
  const loadSequence = useRef(0);

  const fetchNotices = useCallback(async mode => {
    const d=await api(`/notices.php?store_id=${encodeURIComponent(store.id)}&archive=${mode}`);
    return sortNotices(d.notices||[]);
  },[store.id]);

  const loadNotices = useCallback(async mode => {
    const sequence=++loadSequence.current;
    setLoading(true);
    try {
      const rows=await fetchNotices(mode);
      if(sequence===loadSequence.current) setNotices(rows);
    } catch {}
    if(sequence===loadSequence.current) setLoading(false);
  },[fetchNotices]);

  useEffect(()=>{
    setArchiveMode("current");
    setOnlyUnread(false);
    setViewId(null);
    setEditingNoticeId(null);
    loadNotices("current");
  },[loadNotices]);

  const changeArchiveMode = mode => {
    setArchiveMode(mode);
    setOnlyUnread(false);
    setViewId(null);
    setShowForm(false);
    loadNotices(mode);
  };

  const post = async () => {
    if (!form.title.trim()||!form.body.trim()) return;
    setPosting(true);
    try {
      const d=await api("/notices.php","POST",{ store_id:store.id,author_id:user.id,author_name:user.name,author_role:user.role,...form });
      let uploadError="";
      if(selectedFiles.length){
        try{
          await uploadNoticeAttachments(d.id,selectedFiles.map(item=>item.file));
        }catch(e){ uploadError=e.message||"添付ファイルのアップロードに失敗しました"; }
      }
      setForm({ target:"store",category:"業務連絡",title:"",body:"",pinned:false }); setShowForm(false);
      selectedFiles.forEach(item=>item.preview&&URL.revokeObjectURL(item.preview));
      setSelectedFiles([]);
      setArchiveMode("current");
      await loadNotices("current");
      if(uploadError) alert(`連絡は投稿されましたが、添付に失敗しました。\n${uploadError}`);
    } catch(e){ alert(e.message); }
    setPosting(false);
  };

  const selectFiles = event => {
    const files=Array.from(event.target.files||[]);
    event.target.value="";
    if(!files.length) return;
    const error=validateHandoverFiles(files,selectedFiles.length);
    if(error) return alert(error);
    setSelectedFiles(current=>[...current,...files.map(file=>({file,preview:file.type.startsWith("image/")?URL.createObjectURL(file):null}))]);
  };

  const removeSelectedFile = index => {
    setSelectedFiles(current=>{
      const target=current[index];
      if(target?.preview) URL.revokeObjectURL(target.preview);
      return current.filter((_,itemIndex)=>itemIndex!==index);
    });
  };

  const markRead = async id => {
    try {
      const data=await api("/notices.php","PUT",{ id });
      setNotices(p=>p.map(n=>n.id===id?{
        ...n,
        ...data,
        ...(Array.isArray(data.reads)?{reads:data.reads}:{})
      }:n));
      return true;
    } catch(e) {
      alert(e.message||"確認状況を更新できませんでした");
      return false;
    }
  };

  const beginNoticeEdit = notice => {
    setEditingNoticeId(notice.id);
    setEditingNotice({ title:notice.title,body:notice.body });
  };

  const saveNoticeEdit = async notice => {
    const title=editingNotice.title.trim();
    const body=editingNotice.body.trim();
    if(!title||!body) return;
    setManagingNoticeId(notice.id);
    try {
      const data=await api("/notices.php","PUT",{ action:"edit",id:notice.id,title,body });
      setNotices(current=>current.map(item=>String(item.id)===String(notice.id)
        ?{...item,...data.notice,is_edited:!!data.notice?.updated_at}:item));
      setEditingNoticeId(null);
      setEditingNotice({ title:"",body:"" });
    } catch(e) {
      alert(e.message||"連絡を編集できませんでした");
    }
    setManagingNoticeId(null);
  };

  const deleteNotice = async notice => {
    if(!window.confirm("この投稿を削除しますか？")) return;
    setManagingNoticeId(notice.id);
    try {
      await api(`/notices.php?id=${encodeURIComponent(notice.id)}`,"DELETE");
      setNotices(current=>current.filter(item=>String(item.id)!==String(notice.id)));
      setViewId(null);
      setEditingNoticeId(null);
    } catch(e) {
      alert(e.message||"連絡を削除できませんでした");
    }
    setManagingNoticeId(null);
  };

  const canPinNotice = ["president","manager"].includes(user.role);
  const togglePinned = async notice => {
    const pinned=!isNoticePinned(notice);
    setPinningId(notice.id);
    try {
      await api("/notices.php","PUT",{ action:"set_pinned",id:notice.id,pinned });
      setLoading(true);
      const [currentNotices,pastNotices]=await Promise.all([fetchNotices("current"),fetchNotices("past")]);
      loadSequence.current+=1;
      const targetId=String(notice.id);
      const targetMode=currentNotices.some(item=>String(item.id)===targetId)?"current":pastNotices.some(item=>String(item.id)===targetId)?"past":archiveMode;
      setArchiveMode(targetMode);
      setViewId(null);
      setNotices(targetMode==="past"?pastNotices:currentNotices);
      setLoading(false);
    } catch(e) {
      alert(e.message);
      setLoading(false);
    }
    setPinningId(null);
  };

  const unread=notices.filter(n=>{
    const receipt=getReceiptStatus(n,users,"notice",user.id);
    return receipt.isTarget&&!receipt.isRead;
  }).length;
  const visibleNotices=onlyUnread?notices.filter(n=>{
    const receipt=getReceiptStatus(n,users,"notice",user.id);
    return receipt.isTarget&&!receipt.isRead;
  }):notices;

  if(viewId){
    const n=notices.find(x=>x.id===viewId); if(!n){setViewId(null);return null;}
    return(
      <div>
        <button onClick={()=>setViewId(null)} style={{ marginBottom:16,background:"none",border:"none",color:store.accent,cursor:"pointer",fontSize:14,fontWeight:700 }}>← 一覧へ戻る</button>
        <div style={{ background:"#fff",borderRadius:14,border:"1px solid #eee",overflow:"hidden" }}>
          <div style={{ background:`linear-gradient(135deg,${store.color},${store.accent})`,padding:"24px 28px",color:"#fff" }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:8 }}>
              {isNoticePinned(n)&&<span style={{ background:"rgba(255,255,255,0.2)",borderRadius:20,padding:"3px 10px",fontSize:12 }}>📌</span>}
              <span style={{ background:"rgba(255,255,255,0.15)",borderRadius:20,padding:"3px 10px",fontSize:12 }}>{NOTICE_TGTS[n.target]?.icon} {NOTICE_TGTS[n.target]?.label}</span>
            </div>
            {String(editingNoticeId)===String(n.id)?<input aria-label="連絡タイトルを編集" value={editingNotice.title} onChange={event=>setEditingNotice(current=>({...current,title:event.target.value}))} style={{ ...inpS,fontSize:18,fontWeight:800 }}/>:<div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}><div style={{ fontSize:22,fontWeight:800 }}>{n.title}</div>{postWasEdited(n)&&<EditedBadge/>}</div>}
          </div>
          <div style={{ padding:24 }}>
            <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:20,padding:"12px 16px",background:"#f8f8f8",borderRadius:10 }}><span style={{ fontSize:20 }}>{ROLE_ICONS[n.author_role]}</span><div><div style={{ fontWeight:700,fontSize:14 }}>{n.author_name}</div><div style={{ fontSize:12,color:"#888" }}>{ROLE_LABELS[n.author_role]}　{fmtDT(n.created_at)}</div></div></div>
            {String(editingNoticeId)===String(n.id)?<div style={{ marginBottom:20 }}><textarea aria-label="連絡本文を編集" value={editingNotice.body} onChange={event=>setEditingNotice(current=>({...current,body:event.target.value}))} rows={6} style={{ ...inpS,resize:"vertical" }}/><div style={{ display:"flex",gap:8,marginTop:8 }}><button type="button" onClick={()=>saveNoticeEdit(n)} disabled={String(managingNoticeId)===String(n.id)||!editingNotice.title.trim()||!editingNotice.body.trim()} style={btnS(store.accent,String(managingNoticeId)===String(n.id))}>保存</button><button type="button" onClick={()=>{setEditingNoticeId(null);setEditingNotice({title:"",body:""});}} disabled={String(managingNoticeId)===String(n.id)} style={{ ...btnS("#777",String(managingNoticeId)===String(n.id)),background:"#fff",color:"#555",border:"1px solid #aaa" }}>キャンセル</button></div></div>:<div style={{ fontSize:15,color:"#333",lineHeight:1.8,whiteSpace:"pre-wrap",marginBottom:20 }}>{n.body}</div>}
            <NoticeAttachmentGallery notice={n} user={user} store={store} onChange={attachments=>setNotices(current=>current.map(notice=>notice.id===n.id?{...notice,attachments}:notice))}/>
            <ReceiptStatus item={n} kind="notice" user={user} users={users} store={store} allowConfirm onConfirm={markRead}/>
            {canPinNotice&&<button type="button" onClick={()=>togglePinned(n)} disabled={String(pinningId)===String(n.id)} style={{ ...btnS(store.accent,String(pinningId)===String(n.id)),marginTop:12,padding:"8px 12px",fontSize:12 }}>{isNoticePinned(n)?"📌 ピン留め解除":"📌 ピン留め"}</button>}
            {canManagePostRecord(n,user)&&String(editingNoticeId)!==String(n.id)&&<PostManageButtons store={store} busy={String(managingNoticeId)===String(n.id)} onEdit={()=>beginNoticeEdit(n)} onDelete={()=>deleteNotice(n)}/>}
            <div style={{ padding:"14px 16px",background:"#f8f8f8",borderRadius:10,marginTop:12,marginBottom:12 }}><div style={{ fontSize:12,color:"#888",fontWeight:700,marginBottom:10 }}>📨 この連絡をメール・LINEで転送する</div><ShareButtons title={`【ファイブ連絡】${n.title}`} body={`${n.body}\n\n─\n${n.author_name}（${ROLE_LABELS[n.author_role]}）`}/></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ArchiveModeToggle mode={archiveMode} onChange={changeArchiveMode} currentLabel="現在の連絡" pastLabel="過去の連絡" store={store}/>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8 }}>
        <ReceiptUnreadToolbar count={unread} onlyUnread={onlyUnread} onChange={setOnlyUnread} store={store}/>
        {archiveMode==="current"&&<button onClick={()=>setShowForm(!showForm)} style={btnS(store.accent,false)}>{showForm?"キャンセル":"+ 投稿する"}</button>}
      </div>
      {archiveMode==="current"&&showForm&&(
        <div style={{ background:"#f8f8f8",borderRadius:14,padding:20,marginBottom:20,border:`2px solid ${store.accent}40` }}>
          <div style={{ fontWeight:700,color:store.color,marginBottom:14,fontSize:15 }}>📣 連絡を投稿</div>
          <div style={{ marginBottom:10 }}><span style={lblS}>対象</span><select value={form.target} onChange={e=>setForm({...form,target:e.target.value})} style={{...selS,width:"100%"}}>{Object.entries(NOTICE_TGTS).map(([k,v])=><option key={k} value={k}>{v.icon} {v.label}</option>)}</select></div>
          <div style={{ marginBottom:10 }}><span style={lblS}>タイトル</span><input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="件名を入力" style={inpS}/></div>
          <div style={{ marginBottom:10 }}><span style={lblS}>本文</span><textarea value={form.body} onChange={e=>setForm({...form,body:e.target.value})} rows={4} style={{...inpS,resize:"vertical"}}/></div>
          <input ref={fileInputRef} type="file" multiple accept={HANDOVER_ATTACHMENT_ACCEPT} onChange={selectFiles} style={{ display:"none" }}/>
          <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:10 }}><button type="button" onClick={()=>fileInputRef.current?.click()} disabled={posting||selectedFiles.length>=5} style={{ fontSize:13,color:store.accent,background:"#fff",border:`1px solid ${store.accent}`,borderRadius:8,padding:"8px 11px",minHeight:40,cursor:"pointer" }}>＋ 画像・PDFを追加</button><span style={{ fontSize:12,fontWeight:700,color:selectedFiles.length>=5?"#e74c3c":"#777" }}>{selectedFiles.length} / 5</span></div>
          {selectedFiles.length>0&&<div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(105px,1fr))",gap:8,marginBottom:12 }}>{selectedFiles.map((item,index)=><div key={`${item.file.name}-${item.file.lastModified}-${index}`} style={{ position:"relative",border:"1px solid #ddd",borderRadius:8,padding:6,background:"#fff",minWidth:0 }}>{item.preview?<img src={item.preview} alt="" style={{ width:"100%",height:76,objectFit:"cover",borderRadius:5,display:"block" }}/>:<div style={{ height:76,display:"flex",alignItems:"center",justifyContent:"center",fontSize:32 }}>📄</div>}<div title={item.file.name} style={{ fontSize:10,marginTop:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{item.file.name}</div><button type="button" onClick={()=>removeSelectedFile(index)} aria-label={`${item.file.name}を選択から削除`} style={{ position:"absolute",right:3,top:3,width:28,height:28,border:"none",borderRadius:"50%",background:"#000b",color:"#fff",fontSize:16,cursor:"pointer" }}>×</button></div>)}</div>}
          {["president","manager"].includes(user.role)&&(<div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:14 }}><input type="checkbox" id="pin" checked={form.pinned} onChange={e=>setForm({...form,pinned:e.target.checked})} style={{ width:16,height:16,accentColor:store.accent }}/><label htmlFor="pin" style={{ fontSize:14,color:"#555",cursor:"pointer" }}>📌 ピン留めする</label></div>)}
          <button onClick={post} disabled={posting} style={{ ...btnS(store.color,posting),width:"100%",padding:12 }}>投稿する</button>
        </div>
      )}
      {loading?<Loading/>:<div style={{ display:"flex",flexDirection:"column",gap:10 }}>
        {visibleNotices.map(n=>{
          const receipt=getReceiptStatus(n,users,"notice",user.id);
          const isUnread=receipt.isTarget&&!receipt.isRead;
          return(
            <div key={n.id} style={{ background:isUnread?"#fff":"#f9f9f9",border:`1px solid ${isUnread?store.accent:"#eee"}`,borderLeft:`4px solid ${isUnread?store.accent:"#ddd"}`,borderRadius:10,padding:"14px 16px",boxShadow:isUnread?"0 2px 8px #0000001a":"none" }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8 }}>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:5,flexWrap:"wrap" }}>{isNoticePinned(n)&&<span style={{ fontSize:12 }}>📌</span>}{isUnread&&<span style={{ fontSize:10,background:"#e74c3c",color:"#fff",borderRadius:10,padding:"1px 6px",fontWeight:700 }}>NEW</span>}{postWasEdited(n)&&<EditedBadge/>}</div>
                  <div style={{ fontWeight:700,fontSize:15,color:"#222",marginBottom:4 }}>{n.title}</div>
                  <div style={{ fontSize:13,color:"#888" }}>{n.body.length>60?n.body.slice(0,60)+"…":n.body}</div>
                  <div style={{ display:"flex",alignItems:"center",gap:6,marginTop:6,fontSize:12,color:"#aaa",flexWrap:"wrap" }}><span>{ROLE_ICONS[n.author_role]}</span><span>{n.author_name}</span><span>·</span><span>{fmtDT(n.created_at)}</span></div>
                  <NoticeAttachmentGallery notice={n} user={user} store={store} onChange={attachments=>setNotices(current=>current.map(notice=>notice.id===n.id?{...notice,attachments}:notice))}/>
                  <ReceiptStatus item={n} kind="notice" user={user} users={users} store={store}/>
                </div>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  {canPinNotice&&<button type="button" onClick={event=>{event.stopPropagation();togglePinned(n);}} disabled={String(pinningId)===String(n.id)} style={{ fontSize:12,color:store.accent,background:"#fff",border:`1px solid ${store.accent}`,borderRadius:7,padding:"6px 9px",cursor:String(pinningId)===String(n.id)?"default":"pointer",whiteSpace:"nowrap" }}>{isNoticePinned(n)?"ピン留め解除":"ピン留め"}</button>}
                </div>
              </div>
              <button type="button" onClick={()=>setViewId(n.id)} style={{ marginTop:10,fontSize:12,color:store.accent,background:"none",border:`1px solid ${store.accent}`,borderRadius:6,padding:"5px 9px",cursor:"pointer" }}>詳細を見る</button>
            </div>
          );
        })}
        {visibleNotices.length===0&&<div style={{ textAlign:"center",color:"#aaa",padding:40 }}>{onlyUnread?"未確認の連絡なし":archiveMode==="past"?"過去の連絡なし":"連絡なし"}</div>}
      </div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CHAT TAB（サーバー保存）
// ═══════════════════════════════════════════════════════════
const CHAT_MAX_LENGTH = 300;
const CHAT_ATTACHMENT_MAX_FILES = 5;
const CHAT_ATTACHMENT_MAX_TOTAL_BYTES = 20 * 1024 * 1024;
const CHAT_ATTACHMENT_ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf";

function getChatFileType(file) {
  const extension = file.name.split(".").pop()?.toLowerCase();
  const type = ({ jpg:"JPEG",jpeg:"JPEG",png:"PNG",webp:"WEBP",pdf:"PDF" })[extension] || "";
  const expectedMimes = { JPEG:["","image/jpeg"],PNG:["","image/png"],WEBP:["","image/webp"],PDF:["","application/pdf"] };
  return type&&expectedMimes[type].includes(file.type) ? type : "";
}

function validateChatFiles(files, currentFiles) {
  if (currentFiles.length + files.length > CHAT_ATTACHMENT_MAX_FILES) {
    return "添付できるファイルは最大5件です。";
  }
  for (const file of files) {
    const type = getChatFileType(file);
    if (!type) {
      return "このファイル形式は添付できません。";
    }
  }
  const totalBytes = [...currentFiles,...files].reduce((sum,file)=>sum+file.size,0);
  if (totalBytes > CHAT_ATTACHMENT_MAX_TOTAL_BYTES) {
    return "添付ファイルの合計サイズは20MB以内にしてください。";
  }
  return "";
}

function ChatTab({ store, user, users }) {
  const [messages, setMessages] = useState({});
  const [selUid, setSelUid] = useState(null);
  const [input, setInput] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [unreadMap, setUnreadMap] = useState({});
  const [loading, setLoading] = useState(false);
  const messageListRef = useRef(null);
  const fileInputRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const scrollRequestRef = useRef(null);
  const postingRef = useRef(false);

  // 未読数取得
  useEffect(()=>{
    (async()=>{ try { const d=await api("/chat.php"); setUnreadMap(d.unread||{}); } catch{} })();
  },[]);

  const contacts = getActiveUsers(users).filter(u=>u.id!==user.id).filter(u=>u.name.includes(searchQ)||ROLE_LABELS[u.role].includes(searchQ));
  const getThread = uid => messages[uid]||[];
  const lastMsg   = uid => { const t=getThread(uid); return t.length?t[t.length-1]:null; };
  const unreadCnt = uid => unreadMap[uid]||0;
  const totalUnread = Object.values(unreadMap).reduce((s,v)=>s+v,0);

  // スレッド読み込み
  useEffect(()=>{
    if (!selUid) return;
    let cancelled = false;
    isNearBottomRef.current = true;
    scrollRequestRef.current = "auto";
    const loadThread = async initial => {
      if (initial) setLoading(true);
      try {
        const d = await api(`/chat.php?partnerId=${selUid}`);
        if (cancelled) return;
        const nextMessages = d.messages||[];
        setMessages(p=>{
          const previous = p[selUid]||[];
          if (initial || (nextMessages.length>previous.length && isNearBottomRef.current)) {
            scrollRequestRef.current = initial ? "auto" : "smooth";
          }
          return JSON.stringify(previous)===JSON.stringify(nextMessages)
            ? p
            : {...p,[selUid]:nextMessages};
        });
        setUnreadMap(p=>({...p,[selUid]:0}));
      } catch{}
      if (initial && !cancelled) setLoading(false);
    };
    loadThread(true);
    const timer = window.setInterval(()=>loadThread(false),10000);
    return ()=>{ cancelled=true; window.clearInterval(timer); };
  },[selUid]);

  useEffect(()=>{
    const behavior = scrollRequestRef.current;
    const list = messageListRef.current;
    if (!behavior || !list) return;
    scrollRequestRef.current = null;
    window.requestAnimationFrame(()=>{
      list.scrollTo({ top:list.scrollHeight,behavior });
      isNearBottomRef.current = true;
    });
  },[messages,selUid]);

  const handleMessageScroll = e => {
    const list = e.currentTarget;
    isNearBottomRef.current = list.scrollHeight-list.scrollTop-list.clientHeight<80;
  };

  const selectFiles = e => {
    const files = Array.from(e.target.files||[]);
    e.target.value = "";
    if (!files.length) return;
    const error = validateChatFiles(files,selectedFiles);
    if (error) { setPostError(error); return; }
    setSelectedFiles(current=>[...current,...files]);
    setPostError("");
  };

  const send = async () => {
    if (postingRef.current||!selUid||(!input.trim()&&!selectedFiles.length)) return;
    const text = input.trim();
    postingRef.current = true;
    setPosting(true);
    setPostError("");
    try {
      const d = await postChatMessage(selUid,text,selectedFiles);
      scrollRequestRef.current = "smooth";
      setMessages(p=>({...p,[selUid]:[...(p[selUid]||[]),d.message]}));
      setInput("");
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value="";
    } catch(e){
      setPostError(e.message||"チャットの投稿に失敗しました。");
    } finally {
      postingRef.current = false;
      setPosting(false);
    }
  };

  const selUser = users.find(u=>u.id===selUid);
  if (selUid && selUser) {
    const thread = getThread(selUid);
    return(
      <div style={{ display:"flex",flexDirection:"column",height:520,minWidth:0 }}>
        <div style={{ display:"flex",alignItems:"center",gap:12,paddingBottom:12,borderBottom:"1px solid #eee" }}>
          <button onClick={()=>setSelUid(null)} style={{ background:"none",border:"none",color:store.accent,cursor:"pointer",fontSize:14,fontWeight:700,padding:0 }}>← 戻る</button>
          <Avatar u={selUser} size={40}/>
          <div style={{ flex:1 }}><div style={{ fontWeight:700,fontSize:15 }}>{selUser.name}</div><div style={{ fontSize:12,color:"#aaa" }}>{ROLE_ICONS[selUser.role]} {ROLE_LABELS[selUser.role]}　{STORES.find(s=>s.id===selUser.store)?.name}</div></div>
          <ShareButtons title={null} body={`${selUser.name}さんへの連絡`} compact={true}/>
        </div>
        <div ref={messageListRef} onScroll={handleMessageScroll} style={{ flex:1,minHeight:0,overflowY:"auto",padding:"16px 0",display:"flex",flexDirection:"column",gap:10 }}>
          {loading?<Loading/>:thread.length===0?<div style={{ textAlign:"center",color:"#ccc",fontSize:13,marginTop:40 }}>メッセージを送ってみましょう</div>:null}
          {thread.map(m=>{ const isMine=m.sender_id===user.id; return(
            <div key={m.id} style={{ display:"flex",justifyContent:isMine?"flex-end":"flex-start",alignItems:"flex-end",gap:8 }}>
              {!isMine&&<Avatar u={selUser} size={28}/>}
              <div style={{ maxWidth:"78%",minWidth:0 }}>
                <div style={{ background:isMine?store.color:"#f0f0f0",color:isMine?"#fff":"#333",borderRadius:isMine?"18px 18px 4px 18px":"18px 18px 18px 4px",padding:"10px 14px",fontSize:14,lineHeight:1.6,wordBreak:"break-word",overflow:"hidden" }}>
                  {m.body&&<div style={{ whiteSpace:"pre-wrap" }}>{m.body}</div>}
                  {(m.attachments||[]).map(a=><a key={a.id} href={a.url} target="_blank" rel="noreferrer" style={{ display:"block",marginTop:m.body?8:0,color:isMine?"#fff":store.accent,textDecoration:"none",maxWidth:"100%" }}>
                    {a.file_type==="image"&&<img src={a.url} alt={a.original_filename} style={{ display:"block",maxWidth:"100%",maxHeight:220,borderRadius:8,objectFit:"contain",marginBottom:4 }}/>} 
                    <span style={{ display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:12,textDecoration:"underline" }}>{a.file_type==="pdf"?"📄 ":"🖼️ "}{a.original_filename}</span>
                  </a>)}
                </div>
                <div style={{ fontSize:11,color:"#bbb",marginTop:3,textAlign:isMine?"right":"left" }}>{fmtDT(m.created_at)}{isMine&&<span style={{ marginLeft:4 }}>{m.is_read?"✓✓":"✓"}</span>}</div>
              </div>
              {isMine&&<Avatar u={user} size={28}/>}
            </div>
          ); })}
        </div>
        <div style={{ paddingTop:12,borderTop:"1px solid #eee",minWidth:0 }}>
          <textarea value={input} onChange={e=>{setInput(e.target.value);setPostError("");}} maxLength={CHAT_MAX_LENGTH} rows={5} placeholder="メッセージを入力..." style={{ ...inpS,width:"100%",boxSizing:"border-box",resize:"vertical",lineHeight:1.6,minHeight:"calc(5 * 1.6em + 18px)" }}/>
          <div style={{ textAlign:"right",fontSize:12,color:"#888",marginTop:3 }}>{input.length} / {CHAT_MAX_LENGTH}文字</div>
          {selectedFiles.length>0&&<div style={{ display:"flex",flexDirection:"column",gap:5,marginTop:7 }}>
            {selectedFiles.map((file,index)=><div key={`${file.name}-${file.size}-${index}`} style={{ display:"flex",alignItems:"center",gap:8,background:"#f6f7f8",borderRadius:7,padding:"6px 8px",minWidth:0 }}>
              <span style={{ flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:12 }} title={file.name}>{file.name}</span>
              <span style={{ fontSize:11,color:"#777",flexShrink:0 }}>{getChatFileType(file)}</span>
              <button type="button" onClick={()=>setSelectedFiles(files=>files.filter((_,i)=>i!==index))} disabled={posting} aria-label={`${file.name}を削除`} style={{ border:"none",background:"none",color:"#c0392b",fontSize:12,fontWeight:700,cursor:posting?"default":"pointer",padding:"4px",flexShrink:0 }}>削除</button>
            </div>)}
          </div>}
          {postError&&<div role="alert" style={{ color:"#c0392b",fontSize:12,marginTop:7 }}>{postError}</div>}
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap",marginTop:8 }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,minWidth:0 }}>
              <input ref={fileInputRef} type="file" multiple accept={CHAT_ATTACHMENT_ACCEPT} onChange={selectFiles} style={{ display:"none" }}/>
              <button type="button" onClick={()=>fileInputRef.current?.click()} disabled={posting||selectedFiles.length>=CHAT_ATTACHMENT_MAX_FILES} style={{ fontSize:12,color:store.accent,background:"#fff",border:`1px solid ${store.accent}`,borderRadius:7,padding:"8px 10px",cursor:posting?"default":"pointer",minHeight:40 }}>＋ 画像・PDF</button>
              <span style={{ fontSize:12,color:"#888",whiteSpace:"nowrap" }}>{selectedFiles.length} / {CHAT_ATTACHMENT_MAX_FILES}件</span>
            </div>
            <button type="button" onClick={send} disabled={posting||(!input.trim()&&!selectedFiles.length)} style={{ ...btnS(store.accent,posting||(!input.trim()&&!selectedFiles.length)),padding:"10px 22px",minWidth:96,minHeight:42,flexShrink:0 }}>{posting?"送信中...":"投稿"}</button>
          </div>
        </div>
      </div>
    );
  }

  const sorted=[...contacts].sort((a,b)=>{ const ua=unreadCnt(a.id),ub=unreadCnt(b.id); if(ua!==ub)return ub-ua; return 0; });
  const byRole={};sorted.forEach(u=>{ if(!byRole[u.role])byRole[u.role]=[]; byRole[u.role].push(u); });

  return(
    <div>
      <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:16 }}>
        <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="名前・役職で検索..." style={{ ...inpS,flex:1 }}/>
        {totalUnread>0&&<span style={{ background:"#e74c3c",color:"#fff",borderRadius:20,padding:"4px 12px",fontSize:13,fontWeight:700,flexShrink:0 }}>未読 {totalUnread}</span>}
      </div>
      {Object.entries(ROLE_LABELS).map(([roleKey,roleLabel])=>{ const rc=(byRole[roleKey]||[]); if(!rc.length)return null; return(
        <div key={roleKey} style={{ marginBottom:16 }}>
          <div style={{ fontSize:12,fontWeight:700,color:ROLE_COLORS[roleKey],marginBottom:8,display:"flex",alignItems:"center",gap:6 }}><span>{ROLE_ICONS[roleKey]}</span><span>{roleLabel}</span></div>
          <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
            {rc.map(u=>{ const last=lastMsg(u.id);const uc=unreadCnt(u.id);const ps=STORES.find(s=>s.id===u.store); return(
              <button key={u.id} onClick={()=>setSelUid(u.id)} style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:uc>0?"#fff":"#fafafa",border:`1px solid ${uc>0?"#ddd":"#eee"}`,borderRadius:12,cursor:"pointer",textAlign:"left",fontFamily:"inherit",width:"100%" }}>
                <div style={{ position:"relative",flexShrink:0 }}><Avatar u={u} size={44}/>{uc>0&&<span style={{ position:"absolute",top:-4,right:-4,background:"#e74c3c",color:"#fff",borderRadius:"50%",width:18,height:18,fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900 }}>{uc}</span>}</div>
                <div style={{ flex:1,minWidth:0 }}><div style={{ fontWeight:700,fontSize:14,color:"#222" }}>{u.name}</div><div style={{ fontSize:12,color:"#aaa",marginTop:1 }}>{ps?.name}</div>{last&&<div style={{ fontSize:13,color:uc>0?"#333":"#aaa",marginTop:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:uc>0?600:400 }}>{last.sender_id===user.id?"自分: ":""}{last.body}</div>}</div>
                <div style={{ fontSize:18,color:"#ddd",flexShrink:0 }}>›</div>
              </button>
            ); })}
          </div>
        </div>
      ); })}
      {sorted.length===0&&<div style={{ textAlign:"center",color:"#aaa",padding:40 }}>連絡できる相手がいません</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TASKS TAB（サーバー保存）
// ═══════════════════════════════════════════════════════════
function TasksTab({ store, user, users }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTask, setNewTask] = useState("");
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState("mid");
  const storeStaff = getActiveUsers(users).filter(u=>u.store===store.id).map(u=>u.name);
  const priColor={high:"#e74c3c",mid:"#e67e22",low:"#27ae60"};
  const priLabel={high:"高",mid:"中",low:"低"};

  useEffect(()=>{
    setAssignee(storeStaff[0]||"");
    (async()=>{ setLoading(true); try { const d=await api(buildTasksListPath(store.id)); setTasks(d.tasks||[]); } catch{} setLoading(false); })();
  },[store.id]);

  const add = async () => {
    if (!newTask.trim()) return;
    try {
      const d=await api("/tasks.php","POST",{ store_id:store.id,text:newTask,done:false,priority,assignee,created_by:user.id });
      setTasks(p=>[...p,{ id:d.id,text:newTask,done:0,priority,assignee }]);
      setNewTask("");
    } catch(e){ alert(e.message); }
  };

  const toggle = async (id,done) => {
    try { await api("/tasks.php","PUT",{ id,done:!done }); setTasks(p=>p.map(t=>t.id===id?{...t,done:done?0:1}:t)); } catch{}
  };

  const del = async id => {
    try { await api(`/tasks.php?id=${id}`,"DELETE"); setTasks(p=>p.filter(t=>t.id!==id)); } catch{}
  };

  return(
    <div>
      <div style={{ display:"flex",gap:8,marginBottom:16,flexWrap:"wrap" }}>
        <input value={newTask} onChange={e=>setNewTask(e.target.value)} onKeyDown={e=>e.key==="Enter"&&add()} placeholder="新しいタスク..." style={{ ...inpS,flex:1,minWidth:160 }}/>
        <select value={assignee} onChange={e=>setAssignee(e.target.value)} style={selS}>{storeStaff.map(s=><option key={s}>{s}</option>)}</select>
        <select value={priority} onChange={e=>setPriority(e.target.value)} style={selS}><option value="high">高</option><option value="mid">中</option><option value="low">低</option></select>
        <button onClick={add} style={btnS(store.accent,false)}>追加</button>
      </div>
      {loading?<Loading/>:<div style={{ display:"flex",flexDirection:"column",gap:8 }}>
        {tasks.map(t=>(
          <div key={t.id} style={{ display:"flex",alignItems:"center",gap:10,background:t.done?"#f5f5f5":"#fff",border:`1px solid ${t.done?"#ddd":store.accent+"60"}`,borderRadius:10,padding:"10px 14px" }}>
            <input type="checkbox" checked={!!t.done} onChange={()=>toggle(t.id,t.done)} style={{ width:18,height:18,accentColor:store.accent,cursor:"pointer" }}/>
            <span style={{ flex:1,textDecoration:t.done?"line-through":"none",color:t.done?"#aaa":"#333",fontSize:14 }}>{t.text}</span>
            <span style={{ fontSize:11,background:priColor[t.priority]+"20",color:priColor[t.priority],borderRadius:4,padding:"2px 6px",fontWeight:700 }}>{priLabel[t.priority]}</span>
            <span style={{ fontSize:12,color:"#888" }}>{t.assignee}</span>
            <button onClick={()=>del(t.id)} style={{ background:"none",border:"none",cursor:"pointer",color:"#ccc",fontSize:16 }}>✕</button>
          </div>
        ))}
        {tasks.length===0&&<div style={{ textAlign:"center",color:"#aaa",padding:32 }}>タスクなし</div>}
      </div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MINUTES / EXPENSE / KOKUHO / JIPPI（簡潔版・サーバー保存）
// ※ 長さの都合上、各タブのCRUD基本パターンを踏襲
// ═══════════════════════════════════════════════════════════
export function MinutesTab({ store, user }) {
  const [minutes, setMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title:"",date:getToday(),attendees:"",content:"" });
  const [showForm, setShowForm] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [viewMinute, setViewMinute] = useState(null);
  const [targetFacilities, setTargetFacilities] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title:"",date:"",attendees:"",content:"" });

  useEffect(()=>{ setViewId(null);setViewMinute(null);(async()=>{ setLoading(true); try { const d=await api(`/minutes.php?store=${store.id}`); setMinutes(d.minutes||[]); }catch{} setLoading(false); })(); },[store.id]);
  useEffect(()=>{
    setCanCreate(false);
    (async()=>{ try {
      const d=await api("/minutes.php?action=targets");
      const facilities=d.facilities||[];
      setTargetFacilities(facilities);
      setCanCreate(Boolean(d.can_create));
      setSelectedTargets(facilities.some(f=>f.id===store.id)?[store.id]:(facilities[0]?[facilities[0].id]:[]));
    }catch{ setTargetFacilities([]);setSelectedTargets([]);setCanCreate(false); }
    })();
  },[store.id,user.id]);

  const save = async () => {
    if (!form.title.trim()||!form.content.trim()) return;
    if(selectedTargets.length===0){ alert("投稿先施設を1つ以上選択してください");return; }
    try {
      const d=await api("/minutes.php","POST",{ store_id:store.id,target_store_ids:selectedTargets,...form });
      if(selectedTargets.includes(store.id)) setMinutes(p=>[{ id:d.id,...form,meeting_date:form.date,target_store_ids:selectedTargets,target_stores:targetFacilities.filter(f=>selectedTargets.includes(f.id)),created_at:new Date().toISOString() },...p]);
      setForm({ title:"",date:getToday(),attendees:"",content:"" });
      setSelectedTargets(targetFacilities.some(f=>f.id===store.id)?[store.id]:[]);
      setShowForm(false);
    } catch(e){ alert(e.message); }
  };
  const openMinute = async id => {
    try { const d=await api(`/minutes.php?id=${encodeURIComponent(id)}&store=${encodeURIComponent(store.id)}`);setViewMinute(d.minute);setViewId(id);setEditing(false); } catch(e){ alert(e.message); }
  };
  const toggleTarget=id=>setSelectedTargets(current=>current.includes(id)?current.filter(value=>value!==id):[...current,id]);
  const startEdit=()=>{setEditForm({title:viewMinute.title,date:viewMinute.meeting_date||viewMinute.date,attendees:viewMinute.attendees||"",content:viewMinute.content});setEditing(true);};
  const saveEdit=async()=>{if(!editForm.title.trim()||!editForm.content.trim())return;try{const d=await api("/minutes.php","PUT",{id:viewMinute.id,...editForm});setViewMinute(d.minute);setMinutes(current=>current.map(item=>item.id===d.minute.id?d.minute:item));setEditing(false);}catch(e){alert(e.message);}};
  const deleteMinute=async()=>{if(!window.confirm("この議事録を削除しますか？"))return;try{await api(`/minutes.php?id=${encodeURIComponent(viewMinute.id)}`,"DELETE");setMinutes(current=>current.filter(item=>item.id!==viewMinute.id));setViewId(null);setViewMinute(null);}catch(e){alert(e.message);}};

  if(viewId){
    const m=viewMinute||minutes.find(x=>x.id===viewId);
    if(!m)return null;
    const targetNames=(m.target_stores||[]).map(f=>f.name).join("・")||store.name;
    return(<div>
      <button onClick={()=>{setViewId(null);setViewMinute(null);setEditing(false);}} style={{ marginBottom:16,background:"none",border:"none",color:store.accent,cursor:"pointer",fontSize:14,fontWeight:700 }}>← 戻る</button>
      <div style={{ background:"#fff",borderRadius:12,padding:24,border:"1px solid #eee" }}>
        {editing?<div style={{display:"flex",flexDirection:"column",gap:10}}><div style={{fontSize:13,color:"#666"}}>投稿先施設：{targetNames}（投稿後は変更できません）</div><input value={editForm.title} onChange={e=>setEditForm({...editForm,title:e.target.value})} style={inpS}/><input type="date" value={editForm.date} onChange={e=>setEditForm({...editForm,date:e.target.value})} style={inpS}/><input value={editForm.attendees} onChange={e=>setEditForm({...editForm,attendees:e.target.value})} style={inpS}/><textarea value={editForm.content} onChange={e=>setEditForm({...editForm,content:e.target.value})} rows={8} style={{...inpS,resize:"vertical"}}/><div style={{display:"flex",gap:8}}><button onClick={saveEdit} style={btnS(store.accent,false)}>変更を保存</button><button onClick={()=>setEditing(false)} style={btnS("#888",false)}>キャンセル</button></div></div>:<><h2 style={{ margin:"0 0 8px",color:store.color }}>{m.title}</h2><div style={{ fontSize:13,color:"#888",marginBottom:4 }}>投稿先施設：{targetNames}</div><div style={{ fontSize:13,color:"#888",marginBottom:4 }}>日付：{formatMeetingDate(m.meeting_date||m.date)}</div><div style={{ fontSize:13,color:"#888",marginBottom:16 }}>参加者: {m.attendees||"未記入"}</div><hr style={{ border:"none",borderTop:"1px solid #eee",margin:"16px 0" }}/><pre style={{ whiteSpace:"pre-wrap",fontFamily:"inherit",fontSize:14,color:"#333",lineHeight:1.8 }}>{m.content}</pre>{(m.can_edit||m.can_delete)&&<div style={{display:"flex",gap:8,marginTop:18}}>{m.can_edit&&<button onClick={startEdit} style={btnS(store.accent,false)}>編集</button>}{m.can_delete&&<button onClick={deleteMinute} style={btnS("#c0392b",false)}>削除</button>}</div>}</>}
      </div>
    </div>);
  }
  const allSelected=targetFacilities.length>0&&selectedTargets.length===targetFacilities.length;
  return(<div>
    {canCreate&&<div style={{ display:"flex",justifyContent:"flex-end",marginBottom:16 }}><button onClick={()=>setShowForm(!showForm)} style={btnS(store.accent,false)}>{showForm?"キャンセル":"+ 新しい議事録"}</button></div>}
    {canCreate&&showForm&&(<div style={{ background:"#f8f8f8",borderRadius:12,padding:20,marginBottom:20,border:`1px solid ${store.accent}40` }}><div style={{ display:"flex",flexDirection:"column",gap:10 }}>
      <div style={{fontSize:14,fontWeight:700,color:store.color}}>投稿先施設</div>
      <div style={{display:"flex",gap:14,flexWrap:"wrap",background:"#fff",padding:12,borderRadius:8,border:"1px solid #ddd"}}>
        {targetFacilities.length>1&&<label><input type="checkbox" checked={allSelected} onChange={()=>setSelectedTargets(allSelected?[]:targetFacilities.map(f=>f.id))}/> 全施設</label>}
        {targetFacilities.map(f=><label key={f.id}><input type="checkbox" checked={selectedTargets.includes(f.id)} onChange={()=>toggleTarget(f.id)}/> {f.name}</label>)}
      </div>
      {selectedTargets.length===0&&<div style={{fontSize:12,color:"#c0392b"}}>投稿先施設を1つ以上選択してください</div>}
      <input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="会議タイトル" style={inpS}/><input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} style={inpS}/><input value={form.attendees} onChange={e=>setForm({...form,attendees:e.target.value})} placeholder="参加者" style={inpS}/><textarea value={form.content} onChange={e=>setForm({...form,content:e.target.value})} rows={6} style={{...inpS,resize:"vertical"}}/><button onClick={save} disabled={selectedTargets.length===0} style={btnS(selectedTargets.length===0?"#aaa":store.accent,selectedTargets.length===0)}>保存</button>
    </div></div>)}
    {loading?<Loading/>:<div style={{ display:"flex",flexDirection:"column",gap:10 }}>{minutes.map(m=>(<div key={m.id} onClick={()=>openMinute(m.id)} style={{ background:"#fff",border:"1px solid #eee",borderRadius:10,padding:"14px 18px",cursor:"pointer",boxShadow:"0 1px 4px #0000000a" }}><div><span style={{ fontWeight:700,color:store.color }}>{m.title}</span></div><div style={{ fontSize:12,color:"#888",marginTop:4 }}>{formatMeetingDate(m.meeting_date||m.date)}</div>{m.target_stores?.length>0&&<div style={{fontSize:12,color:"#777",marginTop:4}}>投稿先：{m.target_stores.map(f=>f.name).join("・")}</div>}{m.attendees&&<div style={{ fontSize:12,color:"#888",marginTop:4 }}>参加者: {m.attendees}</div>}<div style={{ fontSize:13,color:"#bbb",marginTop:6 }}>タップして詳細 →</div></div>))}{minutes.length===0&&<div style={{ textAlign:"center",color:"#aaa",padding:40 }}>議事録なし</div>}</div>}
  </div>);
}

function ExpenseTab({ store, user }) {
  const [expenses,setExpenses]=useState([]);const[loading,setLoading]=useState(true);const[showForm,setShowForm]=useState(false);const[form,setForm]=useState({applicant:user.name,date:getToday(),category:EXPENSE_CATS[0],amount:"",description:"",receipt:false});const[approverMode,setApproverMode]=useState(false);const[filterStatus,setFilterStatus]=useState("all");const[viewId,setViewId]=useState(null);const[ac,setAc]=useState("");
  useEffect(()=>{ (async()=>{ setLoading(true);try{const d=await api(`/expenses.php?store=${store.id}`);setExpenses(d.expenses||[]);}catch{}setLoading(false); })(); },[store.id]);
  const submit=async()=>{ if(!form.amount||!form.description.trim())return;try{const d=await api("/expenses.php","POST",{ store_id:store.id,applicant_id:user.id,applicant_name:user.name,...form,amount:Number(form.amount)});setExpenses(p=>[{id:d.id,...form,amount:Number(form.amount),applicant_name:user.name,status:"pending",comment:"",apply_date:form.date},...p]);setForm({applicant:user.name,date:getToday(),category:EXPENSE_CATS[0],amount:"",description:"",receipt:false});setShowForm(false);}catch(e){alert(e.message);} };
  const decide=async(id,status,cmt="")=>{ try{await api("/expenses.php","PUT",{id,status,comment:cmt});setExpenses(p=>p.map(e=>e.id===id?{...e,status,comment:cmt}:e));}catch(e){alert(e.message);} };
  const filtered=filterStatus==="all"?expenses:expenses.filter(e=>e.status===filterStatus);const pc=expenses.filter(e=>e.status==="pending").length;
  if(viewId){const exp=expenses.find(e=>e.id===viewId);if(!exp){setViewId(null);return null;}const sc=EXP_ST[exp.status];return(<div><button onClick={()=>{setViewId(null);setAc("");}} style={{ marginBottom:16,background:"none",border:"none",color:store.accent,cursor:"pointer",fontSize:14,fontWeight:700 }}>← 戻る</button><div style={{ background:"#fff",borderRadius:14,border:"1px solid #eee",overflow:"hidden" }}><div style={{ background:`linear-gradient(135deg,${store.color},${store.accent})`,padding:"24px 28px",color:"#fff" }}><div style={{ fontSize:12,opacity:0.75,marginBottom:6 }}>経費申請 #{exp.id}</div><div style={{ fontSize:16,fontWeight:700 }}>{exp.category}</div><div style={{ fontSize:32,fontWeight:900 }}>¥{Number(exp.amount).toLocaleString()}</div></div><div style={{ padding:24 }}><div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20 }}>{[["申請者",exp.applicant_name||exp.applicant],["申請日",exp.apply_date||exp.date],["カテゴリ",exp.category],["領収書",exp.receipt?"✓":"✗"]].map(([k,v])=>(<div key={k} style={{ background:"#f8f8f8",borderRadius:8,padding:"10px 14px" }}><div style={{ fontSize:11,color:"#aaa",marginBottom:3 }}>{k}</div><div style={{ fontSize:14,fontWeight:600 }}>{v}</div></div>))}</div><div style={{ background:"#f8f8f8",borderRadius:8,padding:"12px 14px",marginBottom:20 }}><div style={{ fontSize:11,color:"#aaa",marginBottom:4 }}>使用目的</div><div style={{ fontSize:14 }}>{exp.description}</div></div><span style={{ fontSize:13,fontWeight:700,background:sc.bg,color:sc.color,borderRadius:20,padding:"5px 16px" }}>{sc.label}</span>{exp.comment&&<div style={{ background:sc.bg,border:`1px solid ${sc.color}30`,borderRadius:8,padding:"12px 14px",marginTop:12 }}><div style={{ fontSize:11,color:sc.color,fontWeight:700,marginBottom:4 }}>コメント</div><div style={{ fontSize:14 }}>{exp.comment}</div></div>}{approverMode&&exp.status==="pending"&&(<div style={{ marginTop:20,borderTop:"1px solid #eee",paddingTop:20 }}><div style={{ fontSize:14,fontWeight:700,marginBottom:10 }}>👔 承認者アクション</div><textarea value={ac} onChange={e=>setAc(e.target.value)} rows={3} style={{ ...inpS,marginBottom:12,resize:"vertical" }}/><div style={{ display:"flex",gap:10 }}><button onClick={()=>{decide(exp.id,"approved",ac);setViewId(null);setAc("");}} style={{ ...btnS("#27ae60",false),flex:1 }}>✓ 承認</button><button onClick={()=>{decide(exp.id,"rejected",ac);setViewId(null);setAc("");}} style={{ ...btnS("#e74c3c",false),flex:1 }}>✕ 差し戻し</button></div></div>)}</div></div></div>); }
  return(<div><div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8 }}><button onClick={()=>setApproverMode(!approverMode)} style={{ background:approverMode?store.color:"#f0f0f0",color:approverMode?"#fff":"#555",border:"none",borderRadius:8,padding:"8px 14px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",position:"relative" }}>👔 {approverMode?"承認者モード ON":"承認者モード"}{!approverMode&&pc>0&&<span style={{ position:"absolute",top:-7,right:-7,background:"#e74c3c",color:"#fff",borderRadius:"50%",width:20,height:20,fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900 }}>{pc}</span>}</button><div style={{ display:"flex",gap:8 }}><select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={selS}><option value="all">すべて</option>{Object.entries(EXP_ST).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}</select><button onClick={()=>setShowForm(!showForm)} style={btnS(store.accent,false)}>{showForm?"キャンセル":"+ 新規申請"}</button></div></div>{showForm&&(<div style={{ background:"#f8f8f8",borderRadius:14,padding:20,marginBottom:20,border:`2px solid ${store.accent}40` }}><div style={{ fontWeight:700,color:store.color,marginBottom:14 }}>💴 経費申請フォーム</div><div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10 }}><div><span style={lblS}>申請者</span><div style={{ padding:"8px 12px",background:"#f0f0f0",borderRadius:8,fontSize:14,fontWeight:700 }}>{user.name}</div></div><div><span style={lblS}>申請日</span><input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} style={inpS}/></div><div><span style={lblS}>カテゴリ</span><select value={form.category} onChange={e=>setForm({...form,category:e.target.value})} style={{...selS,width:"100%"}}>{EXPENSE_CATS.map(c=><option key={c}>{c}</option>)}</select></div><div><span style={lblS}>金額（円）</span><input type="number" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="0" style={inpS}/></div></div><div style={{ marginBottom:10 }}><span style={lblS}>使用目的</span><textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={3} style={{...inpS,resize:"vertical"}}/></div><div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:14 }}><input type="checkbox" id="receipt" checked={form.receipt} onChange={e=>setForm({...form,receipt:e.target.checked})} style={{ width:16,height:16,accentColor:store.accent }}/><label htmlFor="receipt" style={{ fontSize:14,color:"#555",cursor:"pointer" }}>領収書あり</label></div><button onClick={submit} style={{ ...btnS(store.color,false),width:"100%",padding:12 }}>申請する</button></div>)}{loading?<Loading/>:(<><div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20 }}>{Object.entries(EXP_ST).map(([key,cfg])=>{ const cnt=expenses.filter(e=>e.status===key).length;const amt=expenses.filter(e=>e.status===key).reduce((s,e)=>s+Number(e.amount),0);return(<div key={key} onClick={()=>setFilterStatus(filterStatus===key?"all":key)} style={{ background:cfg.bg,border:`2px solid ${filterStatus===key?cfg.color:cfg.color+"30"}`,borderRadius:10,padding:"12px 14px",cursor:"pointer" }}><div style={{ fontSize:11,color:cfg.color,fontWeight:700 }}>{cfg.label}</div><div style={{ fontSize:22,fontWeight:900,color:cfg.color }}>{cnt}件</div><div style={{ fontSize:12,color:cfg.color,opacity:0.8 }}>¥{amt.toLocaleString()}</div></div>); })}</div><div style={{ display:"flex",flexDirection:"column",gap:10 }}>{filtered.map(exp=>{ const sc=EXP_ST[exp.status];return(<div key={exp.id} style={{ background:"#fff",border:`1px solid #eee`,borderLeft:`4px solid ${sc.color}`,borderRadius:10,padding:"14px 16px",cursor:"pointer",boxShadow:"0 1px 4px #0000000a" }} onClick={()=>setViewId(exp.id)}><div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start" }}><div style={{ flex:1 }}><div style={{ fontWeight:700,color:"#333",fontSize:14,marginBottom:4 }}>{exp.description}</div><div style={{ display:"flex",gap:10,fontSize:12,color:"#888" }}><span>{exp.applicant_name||exp.applicant}</span><span>{exp.apply_date||exp.date}</span><span style={{ background:"#f0f0f0",borderRadius:4,padding:"1px 6px" }}>{exp.category}</span></div></div><div style={{ textAlign:"right",flexShrink:0,marginLeft:12 }}><div style={{ fontWeight:900,fontSize:16,color:store.color }}>¥{Number(exp.amount).toLocaleString()}</div><span style={{ fontSize:11,fontWeight:700,background:sc.bg,color:sc.color,borderRadius:20,padding:"2px 10px",display:"inline-block",marginTop:4 }}>{sc.label}</span></div></div>{approverMode&&exp.status==="pending"&&(<div style={{ display:"flex",gap:8,marginTop:10,paddingTop:10,borderTop:"1px solid #f0f0f0" }} onClick={e=>e.stopPropagation()}><button onClick={()=>decide(exp.id,"approved")} style={{...btnS("#27ae60",false),fontSize:12,padding:"6px 0",flex:1}}>✓ 承認</button><button onClick={()=>decide(exp.id,"rejected")} style={{...btnS("#e74c3c",false),fontSize:12,padding:"6px 0",flex:1}}>✕ 差し戻し</button></div>)}</div>); })}{filtered.length===0&&<div style={{ textAlign:"center",color:"#aaa",padding:40 }}>申請なし</div>}</div></>)}</div>);
}

export function KokuhoTab({ store }) {
  const [reports,setReports]=useState([]);const[loading,setLoading]=useState(true);const[showForm,setShowForm]=useState(false);const[form,setForm]=useState({yearMonth:nowYM(),service:(KOKUHO_SVC[store.id]||[])[0]||"",users:"",totalUnits:"",claimAmount:"",paidAmount:"",note:"",submittedDate:getToday()});const[filterYM,setFilterYM]=useState("all");const[viewId,setViewId]=useState(null);const tl=KOKUHO_LABEL[store.id]||"";
  useEffect(()=>{ (async()=>{ setLoading(true);try{const d=await api(`/kokuho.php?storeId=${encodeURIComponent(store.id)}`);setReports(d.reports||[]);}catch{}setLoading(false); })(); },[store.id]);
  const save=async()=>{ if(!form.users||!form.claimAmount)return;try{const d=await api("/kokuho.php","POST",{ storeId:store.id,...form,users_count:Number(form.users),total_units:Number(form.totalUnits),claim_amount:Number(form.claimAmount),paid_amount:form.paidAmount?Number(form.paidAmount):null,status:"draft",submitted_date:form.submittedDate});setReports(p=>[{id:d.id,...form,users_count:Number(form.users),total_units:Number(form.totalUnits),claim_amount:Number(form.claimAmount),paid_amount:form.paidAmount?Number(form.paidAmount):null,status:"draft",submitted_date:form.submittedDate},...p]);setForm({yearMonth:nowYM(),service:(KOKUHO_SVC[store.id]||[])[0]||"",users:"",totalUnits:"",claimAmount:"",paidAmount:"",note:"",submittedDate:getToday()});setShowForm(false);}catch(e){alert(e.message);} };
  const allYMs=[...new Set(reports.map(r=>r.year_month||r.yearMonth).filter(Boolean))].sort().reverse();const filtered=filterYM==="all"?reports:reports.filter(r=>(r.year_month||r.yearMonth)===filterYM);const tC=filtered.reduce((s,r)=>s+Number(r.claim_amount||r.claimAmount||0),0);const pT=filtered.filter(r=>r.status==="paid").reduce((s,r)=>s+Number(r.paid_amount||r.paidAmount||0),0);
  if(viewId){const rep=reports.find(r=>r.id===viewId);if(!rep){setViewId(null);return null;}const sc=CLAIM_ST[rep.status];const ym=(rep.year_month||rep.yearMonth||"").replace("-","年")+"月";return(<div><button onClick={()=>setViewId(null)} style={{ marginBottom:16,background:"none",border:"none",color:store.accent,cursor:"pointer",fontSize:14,fontWeight:700 }}>← 戻る</button><div style={{ background:"#fff",borderRadius:14,border:"1px solid #eee",overflow:"hidden" }}><div style={{ background:`linear-gradient(135deg,${store.color},${store.accent})`,padding:"24px 28px",color:"#fff" }}><div style={{ fontSize:12,opacity:0.75,marginBottom:4 }}>国保連請求　{tl}</div><div style={{ fontSize:16,fontWeight:700,marginBottom:4 }}>{ym}　{rep.service}</div><div style={{ fontSize:32,fontWeight:900 }}>¥{Number(rep.claim_amount||rep.claimAmount||0).toLocaleString()}</div></div><div style={{ padding:24 }}><div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:20 }}>{[["対象年月",ym],["利用者数",(rep.users_count||rep.users||0)+"名"],["総単位数",Number(rep.total_units||rep.totalUnits||0).toLocaleString()+"単位"],["請求額","¥"+Number(rep.claim_amount||rep.claimAmount||0).toLocaleString()],["支払決定額",(rep.paid_amount||rep.paidAmount)!=null?"¥"+Number(rep.paid_amount||rep.paidAmount||0).toLocaleString():"―"],["請求日",rep.submitted_date||rep.submittedDate||""]].map(([k,v])=>(<div key={k} style={{ background:"#f8f8f8",borderRadius:8,padding:"10px 14px" }}><div style={{ fontSize:11,color:"#aaa",marginBottom:3 }}>{k}</div><div style={{ fontSize:14,fontWeight:600 }}>{v}</div></div>))}</div>{rep.note&&<div style={{ background:"#fff8e1",border:"1px solid #ffe082",borderRadius:8,padding:"12px 14px",marginBottom:16 }}><div style={{ fontSize:11,color:"#f9a825",fontWeight:700,marginBottom:4 }}>備考</div><div style={{ fontSize:14 }}>{rep.note}</div></div>}<span style={{ fontSize:13,fontWeight:700,background:sc.bg,color:sc.color,borderRadius:20,padding:"5px 16px" }}>{sc.label}</span><div style={{ display:"flex",gap:8,flexWrap:"wrap",marginTop:16 }}>{Object.entries(CLAIM_ST).map(([key,cfg])=>(<button key={key} onClick={async()=>{ try{await api("/kokuho.php","PUT",{id:rep.id,status:key});setReports(p=>p.map(r=>r.id===rep.id?{...r,status:key}:r));setViewId(null);}catch(e){alert(e.message);} }} disabled={rep.status===key} style={{...btnS(rep.status===key?"#ccc":cfg.color,rep.status===key),fontSize:12,padding:"7px 14px"}}>{cfg.label}に変更</button>))}</div></div></div></div>); }
  return(<div><div style={{ display:"inline-flex",alignItems:"center",gap:6,background:store.color+"15",border:`1px solid ${store.accent}`,borderRadius:20,padding:"4px 14px",marginBottom:16 }}><span>🏥</span><span style={{ fontSize:13,fontWeight:700,color:store.color }}>{tl}</span></div><div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8 }}><select value={filterYM} onChange={e=>setFilterYM(e.target.value)} style={selS}><option value="all">全期間</option>{allYMs.map(ym=><option key={ym} value={ym}>{ym.replace("-","年")}月</option>)}</select><button onClick={()=>setShowForm(!showForm)} style={btnS(store.accent,false)}>{showForm?"キャンセル":"+ 新規登録"}</button></div>{showForm&&(<div style={{ background:"#f8f8f8",borderRadius:14,padding:20,marginBottom:20,border:`2px solid ${store.accent}40` }}><div style={{ fontWeight:700,color:store.color,marginBottom:14 }}>🏥 国保連請求　新規登録</div><div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10 }}><div><span style={lblS}>対象年月</span><input type="month" value={form.yearMonth} onChange={e=>setForm({...form,yearMonth:e.target.value})} style={inpS}/></div><div><span style={lblS}>サービス種別</span><select value={form.service} onChange={e=>setForm({...form,service:e.target.value})} style={{...selS,width:"100%"}}>{(KOKUHO_SVC[store.id]||[]).map(s=><option key={s}>{s}</option>)}</select></div><div><span style={lblS}>利用者数</span><input type="number" value={form.users} onChange={e=>setForm({...form,users:e.target.value})} placeholder="0" style={inpS}/></div><div><span style={lblS}>総単位数</span><input type="number" value={form.totalUnits} onChange={e=>setForm({...form,totalUnits:e.target.value})} placeholder="0" style={inpS}/></div><div><span style={lblS}>請求額（円）</span><input type="number" value={form.claimAmount} onChange={e=>setForm({...form,claimAmount:e.target.value})} placeholder="0" style={inpS}/></div><div><span style={lblS}>支払決定額（円）</span><input type="number" value={form.paidAmount} onChange={e=>setForm({...form,paidAmount:e.target.value})} placeholder="0" style={inpS}/></div><div><span style={lblS}>請求日</span><input type="date" value={form.submittedDate} onChange={e=>setForm({...form,submittedDate:e.target.value})} style={inpS}/></div></div><div style={{ marginBottom:14 }}><span style={lblS}>備考</span><textarea value={form.note} onChange={e=>setForm({...form,note:e.target.value})} rows={2} style={{...inpS,resize:"vertical"}}/></div><button onClick={save} style={{ ...btnS(store.color,false),width:"100%",padding:12 }}>登録する</button></div>)}{loading?<Loading/>:(<><div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16 }}>{Object.entries(CLAIM_ST).map(([key,cfg])=>{ const cnt=filtered.filter(r=>r.status===key).length;const amt=filtered.filter(r=>r.status===key).reduce((s,r)=>s+Number(r.claim_amount||r.claimAmount||0),0);return(<div key={key} style={{ background:cfg.bg,border:`1px solid ${cfg.color}30`,borderRadius:10,padding:"10px 12px" }}><div style={{ fontSize:10,color:cfg.color,fontWeight:700 }}>{cfg.label}</div><div style={{ fontSize:18,fontWeight:900,color:cfg.color }}>{cnt}件</div><div style={{ fontSize:11,color:cfg.color,opacity:0.8 }}>¥{amt.toLocaleString()}</div></div>); })}</div><div style={{ display:"flex",gap:12,marginBottom:16,background:store.color+"10",borderRadius:10,padding:"12px 16px" }}><div><span style={{ fontSize:12,color:"#666" }}>請求合計　</span><span style={{ fontSize:18,fontWeight:900,color:store.color }}>¥{tC.toLocaleString()}</span></div><div style={{ borderLeft:"1px solid #ccc",paddingLeft:12 }}><span style={{ fontSize:12,color:"#666" }}>入金済み　</span><span style={{ fontSize:18,fontWeight:900,color:"#27ae60" }}>¥{pT.toLocaleString()}</span></div></div><div style={{ overflowX:"auto" }}><table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}><thead><tr>{["年月","サービス","利用者","単位数","請求額","支払決定額","請求日","状態",""].map(h=><th key={h} style={thS(store.color)}>{h}</th>)}</tr></thead><tbody>{filtered.map((r,i)=>{ const sc=CLAIM_ST[r.status];const ym2=(r.year_month||r.yearMonth||"").replace("-","年")+"月";return(<tr key={r.id||i} style={{ background:i%2===0?"#fff":"#f9f9f9",cursor:"pointer" }} onClick={()=>setViewId(r.id)}><td style={tdS}>{ym2}</td><td style={tdS}>{r.service}</td><td style={tdS}>{r.users_count||r.users}名</td><td style={tdS}>{Number(r.total_units||r.totalUnits||0).toLocaleString()}</td><td style={{...tdS,fontWeight:700}}>¥{Number(r.claim_amount||r.claimAmount||0).toLocaleString()}</td><td style={{...tdS,fontWeight:700,color:"#27ae60"}}>{(r.paid_amount||r.paidAmount)!=null?"¥"+Number(r.paid_amount||r.paidAmount||0).toLocaleString():"―"}</td><td style={tdS}>{r.submitted_date||r.submittedDate||""}</td><td style={tdS}><span style={{ fontSize:11,fontWeight:700,background:sc.bg,color:sc.color,borderRadius:20,padding:"2px 10px" }}>{sc.label}</span></td><td style={tdS}><span style={{ color:store.accent,fontSize:12 }}>詳細→</span></td></tr>); })} {filtered.length===0&&<tr><td colSpan={9} style={{ textAlign:"center",padding:32,color:"#aaa" }}>データなし</td></tr>}</tbody></table></div></>)}</div>);
}

// ═══════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════
export default function FiveApp() {
  const [user, setUser]     = useState(null);
  const [users, setUsers]   = useState([]);
  const [activeStore, setActiveStore] = useState("kasama");
  const [activeTab, setActiveTab]     = useState("shift");
  const [locationSearch, setLocationSearch] = useState(window.location.search);
  const [checkingSession, setCheckingSession] = useState(true);
  const [loginNotice, setLoginNotice] = useState("");
  const [returnTabAfterLogin, setReturnTabAfterLogin] = useState(null);
  const [canViewServiceUsers, setCanViewServiceUsers] = useState(false);
  const [jippiAccess, setJippiAccess] = useState(null);
  const [attendanceAccess, setAttendanceAccess] = useState(null);

  useEffect(() => {
    const handleAuthRequired = event => {
      const qrAttendance = isQrAttendanceSearch(window.location.search);
      const destination = qrAttendance ? "qr" : (activeTab === "attendance" ? "attendance" : "screen");
      setReturnTabAfterLogin(qrAttendance ? "attendance" : activeTab);
      setLoginNotice(authRecoveryMessage(event.detail?.message, destination));
      setAttendanceAccess(null);
      setJippiAccess(null);
      setUser(null);
    };
    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
  }, [activeTab]);

  useEffect(() => {
    const syncLocation = () => setLocationSearch(window.location.search);
    window.addEventListener("popstate", syncLocation);
    return () => window.removeEventListener("popstate", syncLocation);
  }, []);

  // 起動時にセッション確認
  useEffect(()=>{
    (async()=>{
      try {
        const d = await api("/login.php");
        setUser(d.user);
        setActiveStore(d.user.role==="president" ? "kasama" : (d.user.store || d.user.store_id || d.user.storeId || "kasama"));
      } catch (e) {
        if (e.status === 401 && isQrAttendanceSearch(window.location.search)) {
          setReturnTabAfterLogin("attendance");
          setLoginNotice(authRecoveryMessage(e.message, "qr"));
        }
      }
      setCheckingSession(false);
    })();
  },[]);

  // QRコードURLから開いた場合、ログイン後に専用の出退勤画面を表示
  useEffect(() => {
    if (!user) return;

    const params = new URLSearchParams(locationSearch);
    const attendanceMode = params.get("attendance");
    const qrStore = params.get("store");

    if (["clock-in", "clock-out"].includes(attendanceMode)) {
      setActiveTab("attendance");

      if (qrStore && STORES.some(s => s.id === qrStore)) {
        // 主所属外でも、APIが公開済みシフトまたは既存未退勤を検証する。
        setActiveStore(qrStore);
      }
    }
  }, [user, locationSearch]);

  // ユーザー一覧取得（ログイン後）
  useEffect(()=>{
    if (!user) return;
(async()=>{
  try {
    const d = await api("/users.php");
    setUsers(
      (d.users || []).map(u => ({
        ...u,
        loginId: u.login_id,
        store: u.store_id
      }))
    );
  } catch {}
})();
  },[user?.id]);

  const jippiAccessUserId = user?.id;
  const jippiPermissionRevision = user?.permission_revision;
  // 実費請求タブはjippi.viewで許可された施設がある場合だけ表示する。
  useEffect(()=>{
    if (!jippiAccessUserId) { setJippiAccess(null); return; }
    let active = true;
    setJippiAccess(null);
    api("/billing_month_user_checks.php?action=targets")
      .then(data=>{ if (active) setJippiAccess({ facilities:Array.isArray(data.facilities) ? data.facilities : [] }); })
      .catch(()=>{ if (active) setJippiAccess({ facilities:[] }); });
    return ()=>{ active = false; };
  },[jippiAccessUserId, jippiPermissionRevision]);

  const attendanceAccessUserId = user?.id;
  const attendanceAccessRevision = user?.permission_revision;
  useEffect(()=>{
    if (!attendanceAccessUserId) { setAttendanceAccess(null); return; }
    let active = true;
    api("/attendance.php?action=capabilities")
      .then(data=>{ if (active) setAttendanceAccess(data); })
      .catch(()=>{ if (active) setAttendanceAccess({ facilities:[] }); });
    return ()=>{ active = false; };
  },[attendanceAccessUserId, attendanceAccessRevision]);

  // 利用者管理タブはrole固定ではなく、APIが返す施設scope付き権限へ追従する。
  useEffect(()=>{
    if (!user) { setCanViewServiceUsers(false); return; }
    let active = true;
    api("/service_users.php?action=targets")
      .then(data=>{ if (active) setCanViewServiceUsers(Boolean(data.can_view)); })
      .catch(()=>{ if (active) setCanViewServiceUsers(false); });
    return ()=>{ active = false; };
  },[user?.id]);

  const handleLogin = u => {
    setUser(u);
    setActiveStore(u.role==="president" ? "kasama" : (u.store || u.store_id || u.storeId || "kasama"));
    setActiveTab(returnTabAfterLogin || "shift");
    setReturnTabAfterLogin(null);
    setLoginNotice("");
(async()=>{
  try {
    const d = await api("/users.php");
    setUsers(
      (d.users || []).map(u => ({
        ...u,
        loginId: u.login_id,
        store: u.store_id
      }))
    );
  } catch {}
})();
  };

  const handleLogout = async () => {
    try { await api("/login.php","DELETE"); } catch {}
    setAttendanceAccess(null);
    setJippiAccess(null);
    setUser(null);
  };

  const handleTabChange = tabId => {
    if (tabId === "attendance") {
      const url = new URL(window.location.href);
      const attendanceMode = url.searchParams.get("attendance");
      if (["clock-in", "clock-out"].includes(attendanceMode)) {
        url.searchParams.delete("attendance");
        url.searchParams.delete("store");
        window.history.pushState(window.history.state, "", url);
        setLocationSearch(url.search);
      }
    }
    setActiveTab(tabId);
  };

  if (checkingSession) return <div style={{ minHeight:"100vh",background:"#0f172a",display:"flex",alignItems:"center",justifyContent:"center" }}><Loading text="起動中…"/></div>;
  if (!user) return <LoginScreen onLogin={handleLogin} notice={loginNotice}/>;
  if (attendanceAccess === null) return <div style={{ minHeight:"100vh",background:"#0f172a",display:"flex",alignItems:"center",justifyContent:"center" }}><Loading text="権限確認中…"/></div>;
  const userStoreId = user.store || user.store_id || user.storeId || "kasama";

const visibleTabs = ALL_TABS.filter(t =>
  !HIDDEN_MAIN_TAB_IDS.has(t.id)
  && (t.id !== "payrollCsv" || canExportPayrollCsv(user))
  && (t.id !== "serviceUsers" || canViewServiceUsers)
  && (t.id !== "jippi" || Boolean(jippiAccess?.facilities?.length))
  && (t.id !== "jippiDaily" || Boolean(jippiAccess?.facilities?.some(facility=>facility.capabilities?.can_create)))
  && (t.id !== "attendance" || canOpenAttendanceTab(attendanceAccess))
  && (t.id !== "monthlyAttendance" || canOpenMonthlyAttendanceTab(attendanceAccess))
  && (["serviceUsers","attendance","monthlyAttendance","jippi","jippiDaily"].includes(t.id)
    || canViewAppTab(user, t.id, DEFAULT_PERMS[user.role]?.[t.id]))
);
  if (user.perms.isAdmin || user.role==="manager") visibleTabs.push({ id:"admin", label:"権限管理", icon:"⚙️" });
  const safeTab = resolveSafeAppTab(visibleTabs, activeTab);
  const attendanceStores = attendanceStoresForTab(attendanceAccess, STORES, safeTab, userStoreId);
  const visibleStores = attendanceStores || getVisibleStores(user, STORES);
  const qrAttendanceActive = safeTab === "attendance" && isQrAttendanceSearch(locationSearch);
  const store = (qrAttendanceActive ? STORES : visibleStores).find(s => s.id === activeStore)
    || visibleStores.find(s => s.id === userStoreId)
    || visibleStores[0]
    || STORES[0];
  const attendanceCapabilities = attendanceCapabilityForStore(attendanceAccess, store.id);
  const correctionStores = STORES.filter(candidate =>
    attendanceCapabilityForStore(attendanceAccess, candidate.id).can_correct_attendance
  );

  const renderTab = () => {
    switch(safeTab) {
      case "shift":      return <ShiftTab      store={store} user={user} users={users}/>;
      case "attendance": return <AttendanceTab store={store} user={user} users={users} correctionStores={correctionStores} capabilities={attendanceCapabilities} locationSearch={locationSearch}/>;
      case "monthlyAttendance": return <MonthlyAttendanceTab store={store} user={user} users={users} correctionStores={correctionStores} capabilities={attendanceCapabilities}/>;
      case "payrollCsv": return <PayrollCsvTab users={users}/>;
      case "handover": return <HandoverTab store={store} user={user} users={users}/>;
      case "notice":     return <NoticeTab     store={store} user={user} users={users}/>;
      case "chat":       return <ChatTab       store={store} user={user} users={users}/>;
      case "tasks":      return <TasksTab      store={store} user={user} users={users}/>;
      case "minutes":    return <MinutesTab    store={store} user={user}/>;
      case "expense":    return <ExpenseTab    store={store} user={user}/>;
      case "kokuho":     return <KokuhoTab     store={store} user={user}/>;
      case "jippiDaily": return <JippiDailyEntry store={store} request={api} access={jippiAccess}/>;
      case "jippi":      return <JippiLinkedTab store={store} request={api} access={jippiAccess}/>;
      case "serviceUsers": return <ServiceUsersTab store={store}/>;
      case "admin":
  return (
    <AdminTab
      currentUser={user}
      users={users}
      setUsers={setUsers}
      store={store}
    />
  );
      default: return null;
    }
  };

  return (
    <div style={{ minHeight:"100vh",background:"#f0f2f5",fontFamily:"'Hiragino Sans','Noto Sans JP',sans-serif" }}>
      <style>{`
        .five-mobile-store-switch { display: none; }
        @media (max-width: 480px) {
          .five-header-main {
            flex-wrap: wrap;
            gap: 6px 8px;
            padding-top: 8px !important;
          }
          .five-header-store-area {
            order: 2;
            width: 100%;
            min-width: 0;
          }
          .five-header-store-tabs { display: none !important; }
          .five-mobile-store-switch {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            min-width: 0;
          }
          .five-mobile-store-switch-label {
            flex: 0 0 auto;
            color: rgba(255,255,255,0.78);
            font-size: 11px;
            font-weight: 700;
            white-space: nowrap;
          }
          .five-mobile-store-switch select {
            flex: 1 1 auto;
            min-width: 0;
            height: 32px;
            border: 1px solid rgba(255,255,255,0.55);
            border-radius: 8px;
            padding: 0 28px 0 10px;
            background: rgba(255,255,255,0.96);
            font-family: inherit;
            font-size: 12px;
            font-weight: 700;
          }
          .five-header-user-actions {
            order: 1;
            width: 100%;
            min-width: 0;
            justify-content: space-between;
          }
          .five-header-user-pill { min-width: 0; }
          .five-header-user-copy { min-width: 0; }
          .five-header-user-name {
            max-width: 150px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .five-header-logout {
            flex: 0 0 auto;
            white-space: nowrap;
          }
          .five-header-brand { display: none; }
        }
      `}</style>
      <div style={{ background:store.color,padding:"0 16px",boxShadow:"0 2px 12px #00000030",position:"sticky",top:0,zIndex:100 }}>
        <div style={{ maxWidth:960,margin:"0 auto" }}>
          <div className="five-header-main" style={{ display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:10,paddingBottom:6 }}>
            <div className="five-header-store-area">
              <div className="five-header-store-tabs" style={{ display:"flex",gap:2 }}>
              {visibleStores.map(s=><button key={s.id} onClick={()=>setActiveStore(s.id)} style={{ background:store.id===s.id?"rgba(255,255,255,0.95)":"transparent",color:store.id===s.id?s.color:"rgba(255,255,255,0.75)",border:"none",borderRadius:"8px 8px 0 0",padding:"7px 14px",fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit" }}>{s.name}</button>)}
              </div>
              <label className="five-mobile-store-switch">
                <span className="five-mobile-store-switch-label">施設切替</span>
                <select aria-label="施設切替" value={store.id} onChange={event=>setActiveStore(event.target.value)} style={{ color:store.color }}>
                  {visibleStores.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
            </div>
            <div className="five-header-user-actions" style={{ display:"flex",alignItems:"center",gap:8 }}>
              <div className="five-header-user-pill" style={{ display:"flex",alignItems:"center",gap:8,background:"rgba(255,255,255,0.15)",borderRadius:20,padding:"5px 12px 5px 6px" }}>
                <Avatar u={user} size={28}/>
                <div className="five-header-user-copy"><div className="five-header-user-name" style={{ fontSize:12,color:"#fff",fontWeight:700,lineHeight:1 }}>{user.name}</div><div style={{ fontSize:10,color:"rgba(255,255,255,0.65)",marginTop:1 }}>{ROLE_ICONS[user.role]} {ROLE_LABELS[user.role]}</div></div>
              </div>
              <button className="five-header-logout" onClick={handleLogout} style={{ background:"rgba(255,255,255,0.2)",border:"none",borderRadius:8,padding:"5px 10px",color:"#fff",fontSize:12,cursor:"pointer",fontFamily:"inherit" }}>ログアウト</button>
            </div>
          </div>
          <div className="five-header-brand" style={{ color:"rgba(255,255,255,0.4)",fontSize:10,letterSpacing:3,paddingBottom:5 }}>FIVE MANAGEMENT SYSTEM</div>
        </div>
      </div>
      <div style={{ height:3,background:`linear-gradient(90deg,${store.color},${store.accent})` }}/>
      <div style={{ maxWidth:960,margin:"0 auto",padding:"14px 14px" }}>
        <div style={{ display:"flex",gap:3,marginBottom:14,background:"#fff",borderRadius:14,padding:5,boxShadow:"0 1px 8px #0000001a",flexWrap:"wrap" }}>
          {visibleTabs.map(t=>{ const active=safeTab===t.id; return(
            <button key={t.id} onClick={()=>handleTabChange(t.id)} style={{ flex:1,minWidth:58,background:active?store.color:"transparent",color:active?"#fff":"#999",border:"none",borderRadius:10,padding:"7px 3px",fontSize:10,fontWeight:active?700:400,cursor:"pointer",fontFamily:"inherit" }}>
              <div style={{ fontSize:14,marginBottom:1 }}>{t.icon}</div>
              <div style={{ lineHeight:1.2,whiteSpace:"nowrap" }}>{t.label}</div>
            </button>
          ); })}
        </div>
        <div style={{ background:"#fff",borderRadius:16,padding:20,boxShadow:"0 2px 12px #0000000e",minHeight:420 }}>
          <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:18,paddingBottom:12,borderBottom:`2px solid ${store.accent}` }}>
            <span style={{ fontSize:20 }}>{visibleTabs.find(t=>t.id===safeTab)?.icon}</span>
            <div>
              <div style={{ fontSize:16,fontWeight:800,color:store.color }}>{visibleTabs.find(t=>t.id===safeTab)?.label}</div>
              <div style={{ fontSize:11,color:"#aaa" }}>{store.name}　{ROLE_ICONS[user.role]} {ROLE_LABELS[user.role]}：{user.name}</div>
            </div>
          </div>
          {renderTab()}
        </div>
      </div>
    </div>
  );
}
